import React, { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/Layout/Layout';
import { useUser } from '../contexts/UserContext';
import api from '../utils/api';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FiFileText, FiCheckCircle, FiPlusCircle, FiClock, FiDownload, FiCalendar, FiUsers, FiXCircle, FiChevronLeft, FiChevronRight, FiPlus, FiSearch, FiEdit2, FiTrash2, FiEye, FiArrowUp, FiArrowDown, FiMinus, FiChevronDown, FiRotateCcw, FiChevronsLeft, FiChevronsRight, FiShare2, FiLock } from 'react-icons/fi';
import TicketDetails from '../components/TicketDetails';


const DurationPicker = React.forwardRef(({ value, onChange, disabled, className }, ref) => {
  const [h, setH] = useState('');
  const [m, setM] = useState('');

  // Sync internal state with external value
  useEffect(() => {
    if (value === undefined || value === null || value === '') {
      setH('');
      setM('');
      return;
    }

    // Parse the incoming value (could be decimal from DB or string "2h 30m")
    let hours = 0;
    let minutes = 0;
    const valStr = String(value).trim().toLowerCase();

    // Regex for "Xh Ym"
    const hmMatch = valStr.match(/^(\d+(?:\.\d+)?)\s*h(?:\s*(\d+)\s*m?)?$/);
    if (hmMatch) {
      const parsedH = parseFloat(hmMatch[1]);
      const parsedM = hmMatch[2] ? parseInt(hmMatch[2]) : 0;
      // Convert to pure H and M
      // If parsedH is decimal 2.5, that is 2h 30m
      const totalDec = parsedH + parsedM / 60;
      hours = Math.floor(totalDec);
      minutes = Math.round((totalDec - hours) * 60);
    } else if (!isNaN(parseFloat(valStr))) {
      // Decimal format "2.5"
      const total = parseFloat(valStr);
      hours = Math.floor(total);
      minutes = Math.round((total - hours) * 60);
    } else {
      // Colon format "2:30"
      const colonMatch = valStr.match(/^(\d+):(\d+)$/);
      if (colonMatch) {
        hours = parseInt(colonMatch[1]);
        minutes = parseInt(colonMatch[2]);
      }
    }

    setH(hours.toString());
    setM(minutes.toString());
  }, [value]);

  const handleInputChange = (type, valStr) => {
    // Sanitize: allow only digits, max 2 chars
    const clean = valStr.replace(/\D/g, '').slice(0, 2);

    let val = parseInt(clean) || 0;

    let newH = parseInt(h) || 0;
    let newM = parseInt(m) || 0;

    if (type === 'h') {
      if (val > 24) val = 24;
      newH = val;
    } else {
      if (val > 59) val = 59;
      newM = val;
    }

    // Set state with cleaned string (allows empty '' for user typing)
    const displayVal = (clean === '' && valStr === '') ? '' : (type === 'h' && val > 24 ? '24' : (type === 'm' && val > 59 ? '59' : clean));

    let stateVal = clean;
    if (type === 'h' && parseInt(clean) > 24) stateVal = '24';
    if (type === 'm' && parseInt(clean) > 59) stateVal = '59';

    if (type === 'h') setH(stateVal);
    else setM(stateVal);

    // Emit change
    const emitH = type === 'h' ? (parseInt(stateVal) || 0) : newH;
    const emitM = type === 'm' ? (parseInt(stateVal) || 0) : newM;

    onChange(`${emitH}h ${emitM}m`);
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <div className="flex flex-col items-center">
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={h}
          onChange={(e) => handleInputChange('h', e.target.value)}
          disabled={disabled}
          placeholder="HH"
          className="w-12 p-1.5 border rounded text-center text-xs leading-tight bg-white focus:ring-1 focus:ring-blue-500 border-gray-300"
        />
        <span className="text-[9px] text-gray-500">Hrs</span>
      </div>
      <span className="text-gray-400 font-bold">:</span>
      <div className="flex flex-col items-center">
        <input
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={m}
          onChange={(e) => handleInputChange('m', e.target.value)}
          disabled={disabled}
          placeholder="MM"
          className="w-12 p-1.5 border rounded text-center text-xs leading-tight bg-white focus:ring-1 focus:ring-blue-500 border-gray-300"
        />
        <span className="text-[9px] text-gray-500">Mins</span>
      </div>
    </div>
  );
});

const CheckboxOption = (props) => {
  const { isSelected, label, data, selectProps } = props;
  const isAllOption = data.value === 'all';
  const selectedValues = selectProps.value || [];
  const allOtherOptionsCount = selectProps.options.length - 1;
  const currentChecked = isAllOption 
    ? (selectedValues.length === allOtherOptionsCount && allOtherOptionsCount > 0)
    : isSelected;

  return (
    <div>
      <components.Option {...props}>
        <input
          type="checkbox"
          checked={currentChecked}
          onChange={() => null}
          style={{ marginRight: '8px' }}
        />
        <label>{label}</label>
      </components.Option>
    </div>
  );
};

const MultiValueSummary = ({ children, ...props }) => {
  const { getValue, selectProps } = props;
  const numSelected = getValue().length;
  const totalOptions = (selectProps.options || []).length - 1;

  if (numSelected === 0) return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  
  let label = "";
  if (numSelected === totalOptions && totalOptions > 0) {
    label = "All Roles Selected";
  } else if (numSelected > 1) {
    label = `${numSelected} Roles Selected`;
  } else {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  return (
    <components.ValueContainer {...props}>
      <div className="text-[11px] font-medium text-gray-700 px-1">{label}</div>
      {Array.isArray(children) ? children[1] : children}
    </components.ValueContainer>
  );
};

export default function UpdateStatus() {
  const { user } = useUser();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // Determine if user is admin
  const roleIdRaw = user?.user_role_id ?? user?.role_id;
  const roleId = roleIdRaw ? Number(roleIdRaw) : undefined;
  const userId = user?.user_id ? Number(user.user_id) : (user?.id ? Number(user.id) : undefined);

  // Role 1 is Admin, Role 2 is Customer, Role 9 is HR (Admin-like)
  const isAdminIncludingCustomer = ![3, 4, 5, 6, 9, 10, 12, 13, 14].includes(roleId || 0);
  const isSuperAdmin = roleId === 1 || roleId === 11;
  const isAnyAdmin = [1, 11].includes(roleId);
  const isCustomer = roleId === 2;
  // Treat as "Admin View" if Role 1 or 2
  const isAdmin = isAdminIncludingCustomer;

  const getTodayStr = () => {
    const d = new Date();
    // Shift the "business today" back by 6 hours
    if (d.getHours() < 6) {
      d.setDate(d.getDate() - 1);
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Format date to YYYY-MM-DD using local time (avoid UTC shift)
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Format date to DD/MM/YYYY for display without timezone issues
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const parts = String(dateStr).split('-');
    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    return dateStr;
  };

  // Backend-driven flag: whether this user can enter past dates (set by fetchAccess)
  // NOTE: This is UX-only. ALL real enforcement happens on the backend.
  const [canLogPastDate, setCanLogPastDate] = useState(false);

  /**
   * Centralized UX logic for date editability.
   * Admin/SuperAdmin always allowed. Others allowed only if backend granted past-date access
   * OR if within the 6 AM grace period for yesterday.
   * IMPORTANT: This is a UX hint only — backend enforces the real rule.
   */
  const isEditableDate = (dateToCheck) => {
    // Admins are always unrestricted
    if (isSuperAdmin || isAdmin) return true;
    // Backend-whitelisted users have unrestricted past-date access
    if (canLogPastDate) return true;

    const today = new Date();
    const todayStr = formatDate(today);
    const dateToCheckStr = formatDate(new Date(dateToCheck));

    if (dateToCheckStr === todayStr) return true;

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = formatDate(yesterday);

    // 6 AM grace period for yesterday
    if (dateToCheckStr === yesterdayStr) {
      return today.getHours() < 6;
    }

    return false;
  };

  // URL params for date ignored to prevent tab leakage
  const date = getTodayStr();
  const fromDateParam = getTodayStr();
  const toDateParam = getTodayStr();
  const customerId = params.get('customer');

  // reportDate and reportCurrentDate removed to restore previous workflow link with fromDate/toDate

  // Independent Date State for My Tasks Tab - Shifted to Yesterday if before 6 AM
  const [myTasksDate, setMyTasksDate] = useState(() => {
    const d = new Date();
    if (d.getHours() < 6) {
      d.setDate(d.getDate() - 1);
    }
    return d;
  });

  // Developer view states
  const [formData, setFormData] = useState({});
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const INTERNAL_CUSTOMER = { value: 'INT', label: 'INT - Internal (Default)', short_code: 'INT', name: 'Internal' };
  const [searchTerm, setSearchTerm] = useState('');
  const [newTask, setNewTask] = useState({ task_type: '', ticket_id: null, task_ref: '', work_done: '', hours: '', description: '', completion_percentage: 0 });
  const [newTaskCustomer, setNewTaskCustomer] = useState(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(true);
  const [taskRefPrefix, setTaskRefPrefix] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedTaskIds, setSavedTaskIds] = useState(new Set());
  const [devSortField, setDevSortField] = useState(null);
  const [devSortDir, setDevSortDir] = useState('asc');
  const [devLoading, setDevLoading] = useState(false);
  const [devCurrentPage, setDevCurrentPage] = useState(1);
  const [devItemsPerPage, setDevItemsPerPage] = useState(5);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState([]);
  const [emailSubject, setEmailSubject] = useState('Timesheet Report');
  const [emailBody, setEmailBody] = useState('');
  const [emailCc, setEmailCc] = useState([]);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Admin edit/delete states
  const [adminEditingTaskId, setAdminEditingTaskId] = useState(null);
  const [adminTaskToDelete, setAdminTaskToDelete] = useState(null);
  const [showAdminDeleteConfirm, setShowAdminDeleteConfirm] = useState(false);

  // Pagination states for Employee Report (Admin view)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedReportTaskIds, setSelectedReportTaskIds] = useState(new Set());
  const [showBulkRevertConfirm, setShowBulkRevertConfirm] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [taskToRevert, setTaskToRevert] = useState(null);

  // Option States
  const [customerOptionsFilter, setCustomerOptionsFilter] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [taskTypeOptions, setTaskTypeOptions] = useState([]);

  // Filter States
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedDesignations, setSelectedDesignations] = useState([]);

  const handleDesignationChange = (selected, event) => {
    if (event.option && event.option.value === 'all') {
      const isCurrentlyAll = selectedDesignations.length === designationOptions.length - 1 && designationOptions.length > 1;
      if (isCurrentlyAll) {
        setSelectedDesignations([]);
      } else {
        setSelectedDesignations(designationOptions.filter(o => o.value !== 'all'));
      }
      return;
    }
    setSelectedDesignations(selected ? selected.filter(o => o.value !== 'all') : []);
  };
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedTaskType, setSelectedTaskType] = useState(null);

  // Mail Sent State (UI Only)
  const [sentTaskIds, setSentTaskIds] = useState(new Set());
  const [isSentTasksLoaded, setIsSentTasksLoaded] = useState(false);

  // Load from LS
  useEffect(() => {
    const userId = user?.user_id || user?.id;
    if (userId && !isSentTasksLoaded) {
      try {
        const key = `timesheet_sent_tasks_${userId}`;
        const saved = localStorage.getItem(key);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            setSentTaskIds(new Set(parsed));
          }
        }
      } catch (e) {
        console.error('Failed to load sent tasks', e);
      } finally {
        setIsSentTasksLoaded(true);
      }
    }
  }, [user, isSentTasksLoaded]);

  // Sync to localStorage
  useEffect(() => {
    const userId = user?.user_id || user?.id;
    if (userId && isSentTasksLoaded) {
      const key = `timesheet_sent_tasks_${userId}`;
      localStorage.setItem(key, JSON.stringify(Array.from(sentTaskIds)));
    }
  }, [sentTaskIds, user, isSentTasksLoaded]);

  // New States for Split Buttons
  const [showReportDropdown, setShowReportDropdown] = useState(false);
  const [showCustomerReportDropdown, setShowCustomerReportDropdown] = useState(false);

  const tableContainerRef = useRef(null);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      tableContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Approval Constants
  const STATUS_WAITING_START = 10;
  const STATUS_APPROVED = 14;
  const STATUS_REJECTED = 99;

  // Role-based access control
  const [isApprover, setIsApprover] = useState(false);
  const [isExternal, setIsExternal] = useState(false);

  // Tab state for approvers
  const [activeTab, setActiveTab] = useState('waiting');

  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [showTimesheetAccess, setShowTimesheetAccess] = useState(true);

  const fetchAccess = async () => {
    try {
      const r = await api.get('/timesheet/user-config');
      if (r.data?.status) {
        const hasAccess = !!r.data.requires_approval;
        setShowTimesheetAccess(hasAccess);
        setIsApprover(!!r.data.is_approver);
        setIsExternal(!!r.data.is_external);
        // Consume backend-authoritative past-date permission (UX hint only)
        setCanLogPastDate(!!r.data.can_log_past_date);
        if (!!r.data.is_approver) {
          setActiveTab('waiting');
        }
        if (!hasAccess) {
          navigate('/tickets'); // Redirect to dashboard
        }
      }
    } catch (e) {
      console.error('Failed to fetch access config');
    }
  };

  useEffect(() => {
    if (user) {
      fetchAccess();
      // Default to 'my_tasks' for Employees (Non-Admin, Non-Customer)
      if (!isAdmin && !isCustomer) {
        setActiveTab('my_tasks');
      }
    }
  }, [user, isAdmin, isCustomer, navigate]);

  // Load pending approvals
  const loadPendingApprovals = async () => {
    // if (!isApprover) return; // Always check for everyone

    setLoadingPending(true);
    try {
      const r = await api.get('/timesheet/pending-approvals');
      if (r.data?.status) {
        setPendingApprovals(r.data.data || []);
      }
    } catch (e) {
      console.error('Failed to load pending approvals', e);
    }
    setLoadingPending(false);
  };

  useEffect(() => {
    if (isApprover && activeTab === 'waiting') {
      loadPendingApprovals();
    }
  }, [isApprover, activeTab]);

  const startEditingAdmin = (record) => {
    setAdminEditingTaskId(record.id);
  };

  // Confirmation and Editing states
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [taskToApprove, setTaskToApprove] = useState(null);
  const [approvalType, setApprovalType] = useState('normal'); // normal | override
  const [editingApprovalId, setEditingApprovalId] = useState(null);
  const [editingHours, setEditingHours] = useState('');
  const [editingWorkDone, setEditingWorkDone] = useState('');
  const [editingType, setEditingType] = useState('');
  const [editingApprovalRef, setEditingApprovalRef] = useState('');
  const [editingCompletionPercentage, setEditingCompletionPercentage] = useState('');
  const [editingDate, setEditingDate] = useState('');
  const [showBulkDropdown, setShowBulkDropdown] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [taskToReject, setTaskToReject] = useState(null);


  const [editingApprovalCustomerId, setEditingApprovalCustomerId] = useState(null);
  const [showBulkApproveConfirm, setShowBulkApproveConfirm] = useState(false);
  const [showApprovalCustomerDropdown, setShowApprovalCustomerDropdown] = useState(false);

  // Bulk Approval State
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [pendingBulkActionSet, setPendingBulkActionSet] = useState(null);

  // Customer View Editing States
  const [customerEditingTaskId, setCustomerEditingTaskId] = useState(null);
  const [cvRowData, setCvRowData] = useState({});

  // Toggle state for customer data rows (track which rows are expanded)
  const [expandedCustomerRows, setExpandedCustomerRows] = useState(new Set());
  const [showAssignInline, setShowAssignInline] = useState(false); // For inline assignment form

  // Assigned Tasks State
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [assignmentsSent, setAssignmentsSent] = useState([]);
  const [loadingAssignmentsSent, setLoadingAssignmentsSent] = useState(false);
  const [showAssignFormRow, setShowAssignFormRow] = useState(false);
  const [assignForm, setAssignForm] = useState({
    employee_id: null,
    task_type: '',
    task_ref: '',
    description: '',
    ticket_id: null,
    customer_id: null
  });
  const [isAssigning, setIsAssigning] = useState(false);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [expandedAssignmentId, setExpandedAssignmentId] = useState(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [showAssignmentDeleteConfirm, setShowAssignmentDeleteConfirm] = useState(false);

  // Load assigned tasks for the current user (Waiting on Me)
  const loadAssignedTasks = async () => {
    let prepared = [];
    try {
      setLoadingAssigned(true);
      const res = await api.get('/timesheet/assigned-tasks');
      if (res.data && res.data.status) {
        // Prepare tasks for editing in "Waiting on me" table
        prepared = res.data.data.map(t => ({
          ...t,
          hours: '0h 0m',
          work_done: '',
          completion_percentage: 0
        }));
        setAssignedTasks(prepared);
      }
      setLoadingAssigned(false);
    } catch (e) {
      console.error('Failed to load assigned tasks', e);
      setLoadingAssigned(false);
    }
    return prepared;
  };

  const loadAssignmentsSent = async () => {
    try {
      setLoadingAssignmentsSent(true);
      const res = await api.get('/timesheet/assignments-sent');
      if (res.data && res.data.status) {
        setAssignmentsSent(res.data.data);
      }
      setLoadingAssignmentsSent(false);
    } catch (e) {
      console.error('Failed to load assignments sent', e);
      setLoadingAssignmentsSent(false);
    }
  };

  // Handle Assign Task Submit
  const handleAssignTaskSubmit = async () => {
    if (!assignForm.employee_id || !assignForm.task_type || !assignForm.description) {
      toast.error('Please fill in Employee, Task Type, and Description');
      return;
    }

    try {
      setIsAssigning(true);
      await api.post('/timesheet/assign-task', {
        assigned_to: assignForm.employee_id.value,
        task_type: assignForm.task_type,
        task_ref: assignForm.task_ref,
        description: assignForm.description,
        ticket_id: assignForm.ticket_id
      });
      toast.success('Task Assigned Successfully');
      setAssignForm({ employee_id: null, task_type: '', task_ref: '', description: '', ticket_id: null, customer_id: null });
      setShowAssignFormRow(false);
      setIsAssigning(false);
      loadAssignmentsSent(); // Refresh assignments list
    } catch (e) {
      toast.error(e.response?.data?.messages?.error || 'Failed to assign task');
      setIsAssigning(false);
    }
  };

  const onAssignTaskLog = async (task) => {
    try {
      if (!task.work_done || !task.hours || task.hours === '0h 0m') {
        toast.error('Please enter work done and valid hours');
        return;
      }
      setIsSubmitting(true);
      const res = await saveTaskToServer(task);
      if (res.status) {
        toast.success('Assigned task logged successfully');
        const updatedAssigned = await loadAssignedTasks();
        await loadMyRows(formatDate(currentDate));
        if ((isAdmin || isSuperAdmin)) loadAssignmentsSent();

        // If no more assigned tasks and we are on the waiting tab (and not an approver), switch to my_tasks
        if (activeTab === 'waiting' && updatedAssigned.length === 0 && !isApprover) {
          setActiveTab('my_tasks');
        }
      }
      setIsSubmitting(false);
    } catch (e) {
      toast.error(e.message || 'Failed to log assigned task');
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'waiting' || activeTab === 'my_tasks') {
      loadAssignedTasks();
    }
    if (activeTab === 'assign') {
      loadAssignmentsSent();
    }
  }, [activeTab]);

  // ============ TIME CONVERSION HELPERS ============
  /**
   * Convert decimal hours to "Xh Ym" format
   * @param {number} decimalHours - Hours in decimal format (e.g., 2.5)
   * @returns {string} - Formatted string (e.g., "2h 30m")
   */
  const decimalToHoursMinutes = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '0h 0m';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  /**
   * Parse various time input formats and convert to decimal hours
   * Supports: "2h 30m", "2:30", "2h30m", "2h 30", "150m", "2.5", "2"
   * @param {string} input - Time input string
   * @returns {number|null} - Decimal hours or null if invalid
   */
  const parseTimeInput = (input) => {
    if (!input || input.trim() === '') return null;

    const trimmed = input.trim().toLowerCase();

    // Format: "2h 30m", "2h30m", "2h 30", "2h"
    const hoursMinutesMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*h(?:\s*(\d+)\s*m?)?$/);
    if (hoursMinutesMatch) {
      const hours = parseFloat(hoursMinutesMatch[1]);
      const minutes = hoursMinutesMatch[2] ? parseInt(hoursMinutesMatch[2]) : 0;
      return hours + (minutes / 60);
    }

    // Format: "150m", "30m"
    const minutesOnlyMatch = trimmed.match(/^(\d+)\s*m$/);
    if (minutesOnlyMatch) {
      return parseInt(minutesOnlyMatch[1]) / 60;
    }

    // Format: "2:30", "2:15"
    const colonMatch = trimmed.match(/^(\d+):(\d+)$/);
    if (colonMatch) {
      const hours = parseInt(colonMatch[1]);
      const minutes = parseInt(colonMatch[2]);
      if (minutes >= 60) return null; // Invalid minutes
      return hours + (minutes / 60);
    }

    // Format: "2.5", "2", decimal hours
    const decimalMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/);
    if (decimalMatch) {
      return parseFloat(decimalMatch[1]);
    }

    return null;
  };

  /**
   * Validate time input and return error message if invalid
   * @param {string} input - Time input string
   * @param {number} maxHours - Maximum allowed hours
   * @returns {string|null} - Error message or null if valid
   */
  const validateTimeInput = (input, maxHours = 24) => {
    const decimal = parseTimeInput(input);
    if (decimal === null) {
      return 'Invalid time format. Use: 2h 30m, 2:30, 150m, or 2.5';
    }
    if (decimal > maxHours) {
      return `Time cannot exceed ${maxHours} hours`;
    }
    if (decimal < 0) {
      return 'Time cannot be negative';
    }
    return null;
  };
  // ============ END TIME CONVERSION HELPERS ============

  const handleUpdateCustomerView = async (task, updatedCvData) => {
    try {
      // Parse and validate hours if provided
      let hoursToSend = updatedCvData.hours;
      if (updatedCvData.hours) {
        const hoursDecimal = parseTimeInput(updatedCvData.hours);
        if (!hoursDecimal) {
          toast.error('Invalid time format. Use: 2h 30m, 2:30, 150m, or 2.5');
          return;
        }
        hoursToSend = String(hoursDecimal);
      }

      const r = await api.post('/timesheet/save-customer-view', {
        task_id: task.id,
        ...updatedCvData,
        hours: hoursToSend,
        approval_status: 'PENDING' // Edits require re-approval (triggered when task is approved)
      });
      if (r.data?.status) {
        toast.success(r.data.message);
        setCustomerEditingTaskId(null);
        // Refresh data
        if (activeTab === 'waiting') loadPendingApprovals();
        if (activeTab === 'all_tasks') fetchReport();
      } else {
        toast.error(r.data?.message || 'Failed to update customer view');
      }
    } catch (e) {
      toast.error('Failed to update customer view');
    }
  };

  // Sorting for Waiting For Me Tab
  const [waitingSortField, setWaitingSortField] = useState(null);
  const [waitingSortDir, setWaitingSortDir] = useState('asc');
  const [waitingSearchTerm, setWaitingSearchTerm] = useState('');

  const sortedPendingApprovals = useMemo(() => {
    let list = pendingApprovals;

    // Filter
    if (waitingSearchTerm) {
      const term = waitingSearchTerm.toLowerCase();
      list = list.filter(t =>
        String(t.id).toLowerCase().includes(term) ||
        (t.employee_name || '').toLowerCase().includes(term) ||
        (t.task_ref || '').toLowerCase().includes(term) ||
        (t.work_done || t.description || '').toLowerCase().includes(term) ||
        (t.type || t.task_type_name || '').toLowerCase().includes(term)
      );
    }

    if (waitingSortField) {
      const dir = waitingSortDir === 'desc' ? -1 : 1;
      const val = (row) => {
        const key = waitingSortField;
        let v = row[key];

        // Handle specific fields
        if (key === 'hours') return parseFloat(v || 0);
        if (key === 'completion_percentage') return parseFloat(v || 0);
        if (key === 'date') return new Date(v).getTime();

        return String(v || '').toLowerCase();
      };

      list = [...list].sort((a, b) => {
        const va = val(a), vb = val(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }
    return list;
  }, [pendingApprovals, waitingSortField, waitingSortDir, waitingSearchTerm]);

  const toggleWaitingSort = (field) => {
    if (waitingSortField === field) {
      setWaitingSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setWaitingSortField(field);
      setWaitingSortDir('asc');
    }
  };

  // Helper to toggle bulk selection
  // Internal Types Constant
  const INTERNAL_TYPES = ['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'];

  // Helper to check if task is internal
  // Helper to check if task is internal (Case Insensitive)
  const isInternalTask = (taskType) => {
    if (!taskType) return false;
    return INTERNAL_TYPES.map(t => t.toUpperCase()).includes(taskType.toUpperCase());
  };

  const toggleTaskSelection = (taskId) => {
    const task = pendingApprovals.find(t => t.id === taskId);
    if (task && isInternalTask(task.type)) return; // Prevent selection of internal tasks

    const newSet = new Set(selectedTaskIds);
    if (newSet.has(taskId)) {
      newSet.delete(taskId);
    } else {
      newSet.add(taskId);
    }
    setSelectedTaskIds(newSet);
  };

  const toggleAllTasks = () => {
    // Select all pending approvals EXCEPT internal types
    const selectable = pendingApprovals.filter(t => !isInternalTask(t.type));

    // Check if all selectable are currently selected
    const allSelected = selectable.length > 0 && selectable.every(t => selectedTaskIds.has(t.id));

    if (allSelected) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(selectable.map(r => r.id)));
    }
  };

  const confirmBulkApprove = async () => {
    // call existing handleBulkApprove with approvalType
    await handleBulkApprove(approvalType);
    setShowBulkApproveConfirm(false);
  };

  // Modified to handle both Report and Waiting tabs, accepting specific IDs
  const handleBulkApproveClick = (specificIds = null) => {
    // Check if specificIds is an event object (from direct onClick binding) or null/undefined
    const isEvent = specificIds && specificIds.nativeEvent;
    const actualSpecificIds = (specificIds && !isEvent) ? specificIds : null;

    const isReportTab = activeTab === 'all_tasks';
    // If specific IDs provided, use them. Else use current selection.
    let targetSet = actualSpecificIds ? new Set(actualSpecificIds) : (isReportTab ? selectedReportTaskIds : selectedTaskIds);

    // Filter out Internal Tasks from Bulk Actions
    const dataList = isReportTab ? reportData : pendingApprovals;
    if (dataList && !actualSpecificIds) { // Only filter if using bulk selection, not specific ID click
      const filteredSet = new Set();
      targetSet.forEach(id => {
        const t = dataList.find(r => r.id === id);
        const type = t ? (t.type || t.task_type_name) : null;
        if (t && !isInternalTask(type)) {
          filteredSet.add(id);
        }
      });
      targetSet = filteredSet;
    }

    if (targetSet.size === 0) {
      toast.error('Please select valid tasks to approve (Internal tasks cannot be bulk approved).');
      return;
    }

    setPendingBulkActionSet(targetSet);
    setApprovalType((roleId === 1 || roleId === 9) ? 'override' : 'normal');
    setShowBulkApproveConfirm(true);
  };

  // Bulk Approve handler
  const handleBulkApprove = async (type = 'normal') => {
    // Close dropdown
    setShowBulkDropdown(false);

    // Use pending set if available, fallback to selection logic (though click should have set it)
    const isReportTab = activeTab === 'all_tasks';
    const currentSelectedIds = pendingBulkActionSet || (isReportTab ? selectedReportTaskIds : selectedTaskIds);

    if (!currentSelectedIds || currentSelectedIds.size === 0) return;

    try {
      const r = await api.post('/timesheet/bulk-approve', {
        taskIds: Array.from(currentSelectedIds),
        type: type // Support 'normal' or 'override' in backend bulk approve
      });
      if (r.data?.status) {
        toast.success(r.data.message);

        // Clear selections
        if (isReportTab) {
          setSelectedReportTaskIds(new Set());
        } else {
          setSelectedTaskIds(new Set());
        }
        setPendingBulkActionSet(null);

        // Refresh Logic
        if (isReportTab) {
          fetchReport();
        } else {
          await loadPendingApprovals();
        }

        // Cross-refresh if likely needed (e.g. Approver in Report view might affect My Tasks or Waiting)
        if (activeTab === 'all_tasks' && isApprover) loadPendingApprovals();
        if (isAdmin && activeTab !== 'all_tasks') fetchReport();

      } else {
        toast.error(r.data?.message || 'Bulk approval failed (Check IP restriction)');
      }
    } catch (e) {
      toast.error('Bulk approval failed (Check IP restriction)');
    }
  };

  const handleRevertTask = (taskId) => {
    setTaskToRevert(taskId);
    setShowRevertConfirm(true);
  };

  const confirmRevert = async () => {
    if (!taskToRevert) return;
    try {
      const r = await api.post('/timesheet/revert-task', { taskId: taskToRevert });
      if (r.data?.status) {
        toast.success('Task reverted');
        if (isAdmin || (isApprover && activeTab === 'all_tasks')) fetchReport();
        await loadPendingApprovals(); // It might appear in pending
      } else {
        // toast.error(r.data?.message || 'Revert failed');
        toast.error(r.data?.message || 'Revert failed (Check IP restriction)');
      }
    } catch (e) {
      // toast.error('Failed to revert task');
      toast.error(e.response?.data?.messages?.error || 'Failed to revert task (Check IP restriction)');
    }
    setShowRevertConfirm(false);
    setTaskToRevert(null);
  }

  const handleBulkRevertClick = (specificIds = null) => {
    const isReport = activeTab === 'all_tasks';
    let targetSet = specificIds ? new Set(specificIds) : (isReport ? selectedReportTaskIds : selectedTaskIds);

    // Filter out Internal Tasks
    const dataList = isReport ? reportData : pendingApprovals;
    if (dataList && !specificIds) {
      const filteredSet = new Set();
      targetSet.forEach(id => {
        const t = dataList.find(r => r.id === id);
        const type = t ? (t.type || t.task_type_name) : null;
        if (t && !isInternalTask(type)) {
          filteredSet.add(id);
        }
      });
      targetSet = filteredSet;
    }

    if (targetSet.size === 0) {
      toast.error('Please select valid tasks to revert (Internal tasks cannot be bulk reverted).');
      return;
    }

    // Update selection if specific
    if (specificIds) {
      if (isReport) setSelectedReportTaskIds(targetSet);
      else setSelectedTaskIds(targetSet);
    }

    // Check if tasks are revertible (Status != 1 & != 4)
    // We can do a quick check here if we have data access
    // const dataList = isReport ? reportData : pendingApprovals; // Already defined above
    if (dataList) {
      const revertible = dataList.filter(t => targetSet.has(t.id) && String(t.status_id) !== '1' && String(t.status_id) !== '4');
      if (revertible.length === 0) {
        toast.info('All selected tasks are already in a reverted or pending state.');
        return;
      }
      // If some are not revertible, filter them out from the targetSet for the actual bulk revert
      const nonRevertibleCount = targetSet.size - revertible.length;
      if (nonRevertibleCount > 0) {
        toast.info(`${nonRevertibleCount} selected task(s) cannot be reverted (already pending or reverted) and will be skipped.`);
        targetSet = new Set(revertible.map(t => t.id));
        if (targetSet.size === 0) { // After filtering, if nothing left to revert
          return;
        }
      }
    }

    setShowBulkRevertConfirm(true);
  };

  const handleBulkRevert = async () => {
    try {
      const isReport = activeTab === 'all_tasks';
      const taskIds = Array.from(isReport ? selectedReportTaskIds : selectedTaskIds);

      const r = await api.post('/timesheet/bulk-revert', {
        taskIds
      });
      if (r.data?.status) {
        toast.success(r.data.message);
        if (isReport) {
          setSelectedReportTaskIds(new Set());
          fetchReport();
        } else {
          setSelectedTaskIds(new Set());
          await loadPendingApprovals();
        }
        // Also refresh developer view if active
        if (!isAdmin) loadMyRows(fromDate);
      } else {
        // toast.error(r.data?.message || 'Bulk revert failed');
        toast.error(r.data?.message || 'Bulk revert failed (Possible IP restriction)');
      }
    } catch (e) {
      // toast.error('Bulk revert failed');
      toast.error(e.response?.data?.messages?.error || 'Bulk revert failed (Check IP restriction)');
    }
    setShowBulkRevertConfirm(false);
  };

  const toggleReportTaskSelection = (taskId) => {
    const task = reportData.find(t => t.id === taskId);
    if (task && isInternalTask(task.type || task.task_type_name)) return;

    const newSet = new Set(selectedReportTaskIds);
    if (newSet.has(taskId)) {
      newSet.delete(taskId);
    } else {
      newSet.add(taskId);
    }
    setSelectedReportTaskIds(newSet);
  };

  const toggleAllReportTasks = () => {
    const pageItems = filteredReportData
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // Filter out internal tasks
    const selectable = pageItems.filter(r => !isInternalTask(r.type || r.task_type_name));
    const selectableIds = selectable.map(r => r.id);

    // If all selectable are selected, unselect them. Else select them.
    if (selectableIds.length > 0 && selectableIds.every(id => selectedReportTaskIds.has(id))) {
      const newSet = new Set(selectedReportTaskIds);
      selectableIds.forEach(id => newSet.delete(id));
      setSelectedReportTaskIds(newSet);
    } else {
      const newSet = new Set(selectedReportTaskIds);
      selectableIds.forEach(id => newSet.add(id));
      setSelectedReportTaskIds(newSet);
    }
  };


  // Auto-clear filter for All Tasks tab
  useEffect(() => {
    if (activeTab === 'all_tasks') {
      // Clear filters if we switch to All Tasks so it doesn't default to self
      setSelectedEmployees([]);
      setSelectedDesignations([]);
    }
  }, [activeTab]);

  // Client View: Force active tab to all_tasks
  useEffect(() => {
    if (isCustomer) {
      setActiveTab('all_tasks');
    }
  }, [isCustomer]);

  const handleUpdateApprovalTask = async (taskId) => {
    // Parse and validate hours
    const hoursDecimal = parseTimeInput(editingHours);
    if (!hoursDecimal) {
      toast.error('Invalid time format. Use: 2h 30m, 2:30, 150m, or 2.5');
      return;
    }
    if (hoursDecimal > 24) {
      toast.error('Hours cannot exceed 24');
      return;
    }

    if (editingCompletionPercentage === '' || isNaN(parseInt(editingCompletionPercentage, 10)) || parseInt(editingCompletionPercentage, 10) < 0 || parseInt(editingCompletionPercentage, 10) > 100) {
      toast.error('Invalid completion percentage');
      return;
    }
    if (!editingType) {
      toast.error('Type is required');
      return;
    }
    if (!editingWorkDone) {
      toast.error('Work Done is required');
      return;
    }

    try {
      const payload = {
        task_id: taskId,
        hours: String(hoursDecimal), // Send as decimal
        work_done: editingWorkDone,
        task_type: editingType,
        task_ref: editingApprovalRef,
        completion_percentage: editingCompletionPercentage,
        date: editingDate,
        customerId: editingApprovalCustomerId
      };
      // Use the generic edit endpoint which supports updating these fields
      const r = await api.post(`/timesheet/edit/${taskId}`, payload);
      if (r.data?.status) {
        toast.success('Task details updated');
        setEditingApprovalId(null);
        loadPendingApprovals();
        if (isAdmin || (isApprover && activeTab === 'all_tasks')) fetchReport();
      } else {
        toast.error(r.data?.message || 'Update failed');
      }
    } catch (e) {
      toast.error('Failed to update task');
    }
  };

  const startEditingApproval = (task) => {
    setEditingApprovalId(task.id);
    // Convert decimal hours to formatted time for editing
    const hoursDecimal = parseFloat(task.hours) || 0;
    setEditingHours(decimalToHoursMinutes(hoursDecimal));
    setEditingWorkDone(task.work_done);
    setEditingType(task.type);
    setEditingType(task.type);
    // setEditingCode(task.task_type_code); // Removed as state doesn't exist and not critically used here
    // Strip prefix for internal tasks when editing
    let ref = task.task_ref || '';
    const isInternal = isInternalTask(task.type);
    if (isInternal && ref.startsWith('INT: ')) {
      ref = ref.substring(5);
    }
    setEditingApprovalRef(ref);

    setEditingCompletionPercentage(task.completion_percentage || 0);
    setEditingDate(task.date);
    setEditingApprovalCustomerId(task.customer_id);

    if (isInternal) {
      setShowApprovalCustomerDropdown(false);
    } else {
      setShowApprovalCustomerDropdown(!(task.task_ref && task.task_ref.trim() !== ''));
    }

    // Auto-expand customer row when editing (for dual-row display)
    if ([1, 9, 11].includes(roleId)) {
      setExpandedCustomerRows(prev => {
        const newSet = new Set(prev);
        newSet.add(task.id);
        return newSet;
      });
    }
  };

  const handleApproveClick = (task) => {
    setTaskToApprove(task);
    setApprovalType('normal'); // Default to normal for hierarchical workflow
    setShowApproveConfirm(true);
  };

  const confirmApprove = async () => {
    if (!taskToApprove) return;
    try {
      const r = await api.post('/timesheet/approve-task', { taskId: taskToApprove.id, type: approvalType });
      if (r.data?.status) {
        toast.success(r.data.message);
        // Force wait for reload to reflect status change
        await loadPendingApprovals();
        if (isAdmin || (isApprover && activeTab === 'all_tasks')) fetchReport();
      } else {
        toast.error(r.data?.message || 'Approval failed');
      }
    } catch (e) {
      toast.error('Failed to approve task');
    }
    setShowApproveConfirm(false);
    setTaskToApprove(null);
  };

  const cancelApprove = () => {
    setShowApproveConfirm(false);
    setTaskToApprove(null);
  };

  const handleReject = (task) => {
    setTaskToReject(task);
    setShowRejectConfirm(true);
  };

  const confirmReject = async () => {
    if (!taskToReject) return;
    try {
      const r = await api.post('/timesheet/reject-task', { taskId: taskToReject.id, type: 'normal' });
      if (r.data?.status) {
        toast.success(r.data.message);
        await loadPendingApprovals();
        if (isAdmin || (isApprover && activeTab === 'all_tasks')) fetchReport();
      } else {
        // toast.error(r.data?.message || 'Rejection failed');
        toast.error(r.data?.message || 'Rejection failed (Check IP restriction)');
      }
    } catch (e) {
      toast.error(e.response?.data?.messages?.error || 'Rejection failed (Check IP restriction)');
    }
    setShowRejectConfirm(false);
    setTaskToReject(null);
  };

  const getStatusText = (statusId) => {
    const s = Number(statusId);
    if (s === STATUS_APPROVED) return 'Approved';
    if (s === STATUS_REJECTED) return 'Rejected';
    if (s >= STATUS_WAITING_START && s < STATUS_APPROVED) return 'Waiting for Approval';
    return '';
  };

  const canApproveTask = (task) => {
    // If the task is in our pendingApprovals list, we can approve it.
    if (!task || !pendingApprovals) return false;
    return pendingApprovals.some(p => p.id === task.id);
  };

  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    task_type: '',
    work_done: '',
    hours: '',
    task_ref: ''
  });

  // Load draft from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('timesheet_draft_task');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.task) setNewTask(parsed.task);
        if (parsed.customer) setNewTaskCustomer(parsed.customer);
        if (parsed.prefix) setTaskRefPrefix(parsed.prefix);
        if (parsed.showDropdown !== undefined) setShowCustomerDropdown(parsed.showDropdown);
      } catch (e) {
        console.error('Failed to parse draft task', e);
      }
    }
  }, []);

  // Save draft to localStorage on change
  useEffect(() => {
    const draft = {
      task: newTask,
      customer: newTaskCustomer,
      prefix: taskRefPrefix,
      showDropdown: showCustomerDropdown
    };
    // Only save if there is some meaningful data
    if (newTask.task_type || newTask.work_done || newTask.hours || newTask.task_ref) {
      localStorage.setItem('timesheet_draft_task', JSON.stringify(draft));
    }
  }, [newTask, newTaskCustomer, taskRefPrefix, showCustomerDropdown]);

  // Refs for auto-focus navigation
  const taskTypeRef = useRef(null);
  const taskRefRef = useRef(null);
  const workDoneRef = useRef(null);
  const statusRef = useRef(null);
  const hoursRef = useRef(null);

  // Helper: generate a temporary uid for client-only rows
  const genUid = () => `tmp_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

  // Derived filtered task list for client-side search
  const filteredTasks = useMemo(() => {
    const q = (searchTerm || '').toLowerCase();
    let list = tasks;

    // Apply Task Type Filter
    if (selectedTaskType) {
      list = list.filter(r => r.task_type === selectedTaskType.label);
    }

    if (q) {
      list = list.filter(r => {
        return [r.task_type, r.task_ref, r.work_done, String(r.hours), r.ticket_id, r.description]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(q));
      });
    }
    if (devSortField) {
      const dir = devSortDir === 'desc' ? -1 : 1;
      const val = (row) => {
        const v = row[devSortField];
        if (devSortField === 'hours') return parseFloat(v || 0);
        return String(v || '').toLowerCase();
      };
      list = [...list].sort((a, b) => {
        const va = val(a), vb = val(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }
    return list;
  }, [tasks, searchTerm, devSortField, devSortDir, selectedTaskType]);

  // Clean localStorage on successful save
  const clearDraft = () => {
    localStorage.removeItem('timesheet_draft_task');
  };

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredTasks.length / devItemsPerPage));
    if (devCurrentPage > totalPages) {
      setDevCurrentPage(totalPages);
    }
    if (devCurrentPage < 1 && totalPages >= 1) {
      setDevCurrentPage(1);
    }
  }, [filteredTasks.length, devItemsPerPage, devCurrentPage]);

  const devPaginatedTasks = useMemo(() => {
    const start = (devCurrentPage - 1) * devItemsPerPage;
    return filteredTasks.slice(start, start + devItemsPerPage);
  }, [filteredTasks, devCurrentPage, devItemsPerPage]);

  useEffect(() => {
    setDevCurrentPage(1);
  }, [selectedTaskType, devSortField, devSortDir]);

  useEffect(() => {
    if (searchTerm) setSearchTerm('');
  }, [devCurrentPage]);

  const toggleDevSort = (field) => {
    if (devSortField === field) {
      setDevSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setDevSortField(field);
      setDevSortDir('asc');
    }
  };

  const usedTicketIds = useMemo(() => {
    return new Set(
      tasks
        .filter(t => t.task_type === 'Ticket' && t.ticket_id)
        .map(t => Number(t.ticket_id))
    );
  }, [tasks]);

  const availableTicketOptions = useMemo(() => {
    return ticketOptions.filter(t => !usedTicketIds.has(Number(t.id)));
  }, [ticketOptions, usedTicketIds]);

  // Validate form fields
  const validateForm = () => {
    const errors = {
      task_type: '',
      work_done: '',
      hours: '',
      task_ref: ''
    };
    let isValid = true;

    if (!newTask.task_type) {
      errors.task_type = 'Task type is required';
      isValid = false;
    }

    if (!newTask.work_done || newTask.work_done.trim().length < 5) {
      errors.work_done = 'Work done must be at least 5 characters';
      isValid = false;
    }

    if (!newTask.hours) {
      errors.hours = 'Hours are required';
      isValid = false;
    } else {
      // Use the new time validation function
      const error = validateTimeInput(newTask.hours, 24);
      if (error) {
        errors.hours = error;
        isValid = false;
      }
    }

    if (newTask.task_type !== 'Ticket' && (!newTask.task_ref || newTask.task_ref.trim().length < 3)) {
      errors.task_ref = 'Task reference must be at least 3 characters';
      isValid = false;
    } else if (newTask.task_ref && newTask.task_ref.length > 100) {
      errors.task_ref = 'Task reference must be less than 100 characters';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const onNewTaskChange = (field, value) => {
    setNewTask(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Auto-focus navigation: after Task Type is selected, move to next field
    if (field === 'task_type' && value) {
      // Logic for Internal Types (HR, Others, Admin)
      const internalTypes = ['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'];

      // Helper to clean existing prefixes
      const cleanRef = (ref) => {
        if (!ref) return '';
        // Known prefixes
        const p1 = 'INT: ';
        if (ref.startsWith(p1)) return ref.substring(p1.length);
        // Prefix from selected customer (if any)
        if (taskRefPrefix && ref.startsWith(taskRefPrefix)) return ref.substring(taskRefPrefix.length);
        return ref;
      };

      if (internalTypes.includes(value)) {
        // Auto-load internal short code
        const prefix = `${INTERNAL_CUSTOMER.short_code}: `;
        setNewTaskCustomer(INTERNAL_CUSTOMER);

        const content = cleanRef(newTask.task_ref);
        setTaskRefPrefix(prefix);

        // Update all relevant state
        setNewTask(prev => ({ ...prev, [field]: value, ticket_id: null, task_ref: prefix + content }));
        setShowCustomerDropdown(false);
      }
      else if (value === 'Ticket') {
        loadTicketOptions();
        setShowCustomerDropdown(true);
        setTaskRefPrefix('');
        // Clear ref for ticket as it uses dropdown ID
        setNewTask(prev => ({ ...prev, [field]: value, task_ref: '', ticket_id: null }));

        setTimeout(() => {
          const ticketSelect = document.querySelector('select[value="' + (newTask.ticket_id || '') + '"]');
          if (ticketSelect) ticketSelect.focus();
        }, 100);
      }
      else {
        // Standard Types (Design, Dev, etc.)
        setShowCustomerDropdown(true);
        const content = cleanRef(newTask.task_ref);
        setTaskRefPrefix('');

        setNewTask(prev => ({ ...prev, [field]: value, ticket_id: null, task_ref: content }));
        setNewTaskCustomer(null);

        setTimeout(() => {
          const elm = taskRefRef.current;
          if (elm) {
            elm.focus();
            // Move cursor to end of text
            const len = elm.value.length;
            elm.setSelectionRange(len, len);
          }
        }, 100);
      }
    }
  };

  const onRowChange = (uid, field, value) => {
    setTasks(prev => prev.map(r => ((r._uid || r.id) === uid ? { ...r, [field]: value } : r)));
    if (field === 'task_type' && value === 'Ticket') {
      loadTicketOptions();
    }
  };

  const onAddRow = () => {
    const todayStr = formatDate(new Date());
    const selStr = formatDate(myTasksDate);
    if (selStr > todayStr) {
      toast.error('Cannot create tasks for future dates');
      return;
    }

    if (!isEditableDate(myTasksDate)) {
      toast.error('You do not have permission to log tasks for previous dates.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    const row = { ...newTask, _uid: genUid() };
    if (row.task_type === 'Ticket' && row.ticket_id) {
      const t = ticketOptions.find(x => Number(x.id) === Number(row.ticket_id));
      if (t) row.task_ref = `${t.short_code || 'N/A'}:${t.id} - ${t.title}`;
    }

    // Save the task directly
    saveTaskToServer(row).then(async (resp) => {
      // Add new task to the top of the list with the returned ID
      const newRow = { ...row, id: resp.task_id };
      setTasks(prev => [newRow, ...prev]);
      setNewTask({ task_type: '', ticket_id: null, task_ref: '', work_done: '', hours: '', description: '', completion_percentage: 0 });
      setNewTaskCustomer(null);
      setShowCustomerDropdown(true);
      setTaskRefPrefix('');
      setShowForm(false);
      setIsSubmitting(false);
      clearDraft();
      await loadMyRows(formatDate(myTasksDate));

      // If it was a ticket, reload tickets to remove closed ones
      if (row.task_type === 'Ticket') {
        setTicketOptions(prev => prev.filter(t => Number(t.id) !== Number(row.ticket_id)));
        await loadTickets();
      }
    }).catch(error => {
      setIsSubmitting(false);
      toast.error(error.message || 'Failed to save task');
    });
  };

  // Save task to server
  const saveTaskToServer = async (task) => {
    if (!task.task_type || !task.work_done || !task.hours) {
      throw new Error('Please fill Task Type, Work Done and Hours');
    }

    // Parse and convert hours to decimal format
    const hoursDecimal = parseTimeInput(task.hours);
    if (hoursDecimal === null) {
      throw new Error('Invalid time format. Use: 2h 30m, 2:30, 150m, or 2.5');
    }

    const payload = {
      task_type: task.task_type,
      work_done: task.work_done,
      hours: String(hoursDecimal), // Send as decimal hours to backend
      description: task.description || '',
      date: formatDate(myTasksDate),
      completion_percentage: task.completion_percentage || 0,
      customerId: (newTaskCustomer?.value && !isNaN(Number(newTaskCustomer.value)))
        ? Number(newTaskCustomer.value)
        : (newTaskCustomer?.value === 'INT' ? null : (selectedCustomer?.value ? Number(selectedCustomer.value) : (customerId ? Number(customerId) : null))),
    };

    if (task.task_type === 'Ticket' && task.ticket_id) payload.ticket_id = Number(task.ticket_id);
    else payload.task_ref = task.task_ref || '';

    // If this is an assigned task being completed, include the ID to update
    const isUpdate = (task.id && !String(task.id).startsWith('tmp_')) || !!task.assigned_task_id || !!task.main_task_id;
    const saveId = task.main_task_id || task.assigned_task_id || task.id;

    if (isUpdate) {
      payload.task_id = Number(saveId);
      payload.id = Number(saveId);
    }

    // For legacy tracking and marking assignment as COMPLETED
    if (task.main_task_id || task.assigned_task_id) {
      payload.assigned_task_id = Number(task.id || task.assigned_task_id);
    }

    try {
      const r = await api.post('/timesheet/saveTask', payload);
      if (!r.data?.status) {
        throw new Error(r.data?.message || 'Save failed');
      }
      return r.data;
    } catch (e) {
      console.error(e);
      let errMsg = 'Save failed';
      if (e.response?.data?.messages) {
        if (typeof e.response.data.messages === 'object') {
          // If messages is an object (validation errors), join them or pick the first one
          const msgs = Object.values(e.response.data.messages);
          if (msgs.length > 0) errMsg = msgs[0]; // Just show the first error (e.g. "Total hours...")
        } else if (typeof e.response.data.messages === 'string') {
          errMsg = e.response.data.messages;
        }
      } else if (e.response?.data?.message) {
        errMsg = e.response.data.message;
      } else if (e.message) {
        errMsg = e.message;
      }
      throw new Error(errMsg);
    }
  };

  // Add an empty editable row (used by header + button)
  const addEmptyRow = () => {
    if (isExternal) {
      toast.error('Restricted Access: Create/Edit blocked from external network.');
      return;
    }

    const todayStr = formatDate(new Date());
    const selStr = formatDate(myTasksDate);
    if (selStr > todayStr) {
      toast.error('Cannot create tasks for future dates');
      return;
    }

    if (!isEditableDate(myTasksDate)) {
      toast.error('You do not have permission to log tasks for previous dates.');
      return;
    }
    setShowForm(true);
  };

  // Load developer's own rows/tasks for a given date
  const abortControllerRef = useRef(null);

  const loadMyRows = async (d) => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const theDate = d || date;
    setDevLoading(true);
    try {
      const tParams = { date: theDate };
      const isMyTasks = activeTab === 'my_tasks';
      // For My Tasks, strictly use the single date (ignore global range filters)
      if (isMyTasks) {
        tParams.from = theDate;
        tParams.to = theDate;
      } else {
        tParams.from = fromDate;
        tParams.to = toDate;
      }
      if (customerId) tParams.customerId = Number(customerId);
      const r = await api.get('/timesheet/my', {
        params: tParams,
        signal: abortControllerRef.current.signal
      });
      const data = r.data?.data || [];
      setTasks(data);

      // Track saved task IDs to differentiate from unsaved tasks
      const savedIds = new Set(data.filter(task => task.id).map(task => task.id));
      setSavedTaskIds(savedIds);
    } catch (e) {
      if (e.name === 'Canceled' || e.code === 'ERR_CANCELED') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Failed to load my rows', e);
      setTasks([]);
    }
    setDevLoading(false);
  };

  const onSaveRow = async (row) => {
    const todayStr = formatDate(new Date());
    const selStr = formatDate(currentDate);
    if (selStr > todayStr) {
      toast.error('Tomorrow cannot create task');
      return;
    }

    if (!isEditableDate(currentDate)) {
      toast.error('You do not have permission to log tasks for previous dates.');
      return;
    }
    if (!row.task_type || !row.work_done || !row.hours) {
      toast.error('Please fill Task Type, Work Done and Hours');
      return;
    }

    // Validation: Check total hours for the day (excluding current task being edited)
    const currentTotalExcludingThis = tasks
      .filter(t => t.id !== row.id) // Exclude the task being edited
      .reduce((sum, t) => sum + (parseFloat(t.hours) || 0), 0);

    const resultingTotal = currentTotalExcludingThis + parseFloat(row.hours);

    if (resultingTotal > 24) {
      toast.error(`Total hours cannot exceed 24 per day. Current: ${currentTotalExcludingThis.toFixed(1)}, New Total: ${resultingTotal.toFixed(1)}`);
      return;
    }

    try {
      const payload = {
        task_type: row.task_type,
        work_done: row.work_done,
        hours: String(row.hours),
        description: row.description || '',
        date: formatDate(currentDate),
        customerId: selectedCustomer?.value ? Number(selectedCustomer.value) : (customerId ? Number(customerId) : null),
      };

      if (row.task_type === 'Ticket' && row.ticket_id) payload.ticket_id = Number(row.ticket_id);
      else payload.task_ref = row.task_ref || '';

      const r = await api.post('/timesheet/saveTask', payload);
      if (r.data?.status) {
        toast.success('Saved');
        // Add the ID to saved tasks set
        if (r.data.task_id) {
          setSavedTaskIds(prev => new Set(prev).add(r.data.task_id));
        }
        // reload the same date we saved to
        await loadMyRows(payload.date || date);

        // If it was a ticket, reload tickets to remove closed ones
        if (row.task_type === 'Ticket') {
          setTicketOptions(prev => prev.filter(t => Number(t.id) !== Number(row.ticket_id)));
          await loadTickets();
        }
      } else {
        toast.error(r.data?.message || 'Save failed');
      }
    } catch (e) {
      console.error(e);
      let errMsg = 'Save failed';
      if (e.response?.data?.messages) {
        if (typeof e.response.data.messages === 'object') {
          const msgs = Object.values(e.response.data.messages);
          if (msgs.length > 0) errMsg = msgs[0];
        } else if (typeof e.response.data.messages === 'string') {
          errMsg = e.response.data.messages;
        }
      } else if (e.response?.data?.message) {
        errMsg = e.response.data.message;
      }
      toast.error(errMsg);
    }
  };

  const loadTicketOptions = async () => {
    try {
      // Load ALL tickets for dropdown search
      const r = await api.get('/timesheet/getTickets');
      const data = r.data?.data || [];
      setTicketOptions(data.map(t => ({ id: t.id, title: t.title, short_code: t.short_code })));
    } catch (e) {
      console.error('Failed to load tickets', e);
      setTicketOptions([]);
    }
  };

  useEffect(() => {
    loadTicketOptions();
  }, []);

  // Load tickets for the user
  const loadTickets = async () => {
    try {
      const r = await api.get('/ticket', { params: { userStatus: 'mytickets', clientId: customerId || undefined } });
      const mapped = (r.data?.data || []).map(t => ({
        id: t.id,
        title: t.title,
        status: t.status,
        priority: t.priority,
        updated_date: t.updated_date,
        assigned_to: t.assigned_to,
        college_name: t.college_name
      }));
      setTickets(mapped);
    } catch {
      setTickets([]);
    }
  };

  // Load customer options for Task.ref dropdown
  const loadCustomerOptions = async () => {
    try {
      const r = await api.get('/timesheet/getCustomers');
      const data = r.data?.data || [];
      const opts = data.map(c => ({
        value: c.cust_key,
        label: `${c.short_code} - ${c.customer_name}`,
        short_code: c.short_code,
        name: c.customer_name
      }));
      const seen = new Set();
      const uniqueOpts = [];
      for (const c of opts) {
        const key = (c.name || '').trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        uniqueOpts.push(c);
      }
      setCustomerOptions(uniqueOpts);
      if (!isAdmin) {
        const base = uniqueOpts.map(c => ({ value: c.value, label: c.name }));
        const seen2 = new Set();
        const uniqueFilter = [];
        for (const o of base) {
          const key = (o.label || '').trim().toLowerCase();
          if (!key || seen2.has(key)) continue;
          seen2.add(key);
          uniqueFilter.push(o);
        }
        setCustomerOptionsFilter(uniqueFilter);
      }
    } catch (e) {
      console.error('Failed to load customers', e);
      setCustomerOptions([]);
    }
  };

  const loadTaskTypes = async () => {
    // Hardcoded for now as per PHP logic or fetch if API exists. 
    // For filter purposes we can use static list matching the PHP map.
    const types = [
      { value: 'Ticket', label: 'Ticket' },
      { value: 'DN', label: 'Design' },
      { value: 'DT', label: 'Development' },
      { value: 'QA', label: 'Testing' },
      { value: 'UT', label: 'Unit Testing' },
      { value: 'SS', label: 'Security Scan' },
      { value: 'IM', label: 'Implementation' },
      { value: 'TR', label: 'Training' },
      { value: 'HR', label: 'HR' },
      { value: 'OT', label: 'Others' },
      { value: 'AD', label: 'Admin' },
      { value: 'ST', label: 'System Testing' },
      { value: 'BG', label: 'Bug Fixes in Staging' },
      { value: 'RD', label: 'Requirement Discussion' },
      { value: 'PT', label: 'Production Testing' },
      { value: 'CT', label: 'Customer Testing' },
      { value: 'PI', label: 'Post Implementation' },
      { value: 'IA', label: 'Infrastructure Administration' },
      { value: 'DP', label: 'DEMO PREP' }
    ];
    setTaskTypeOptions(types);
  };

  // Handle ticket row click - navigate to ticket page
  const handleTicketRowClick = (ticketId) => {
    if (ticketId) {
      setOpenedTicketId(ticketId);
    }
  };

  // Handle customer selection for Task.ref
  const handleCustomerSelect = (selectedOption) => {
    if (selectedOption) {
      const prefix = `${selectedOption.short_code}: `;
      setTaskRefPrefix(prefix);
      setNewTask(prev => ({ ...prev, task_ref: prefix }));
      setNewTaskCustomer(selectedOption);
      setShowCustomerDropdown(false);

      // Auto-focus on task_ref input after customer selection
      setTimeout(() => {
        taskRefRef.current?.focus();
      }, 100);
    } else {
      setTaskRefPrefix('');
      setNewTask(prev => ({ ...prev, task_ref: '' }));
      setNewTaskCustomer(null);
      setShowCustomerDropdown(true);
    }
  };

  const onTaskRefInputChange = (value) => {
    const prefix = taskRefPrefix || '';

    // Valid input starting with prefix
    if (value.startsWith(prefix)) {
      setNewTask(prev => ({ ...prev, task_ref: value }));
      return;
    }

    // User deleted characters of the prefix from the right (e.g. "INT: " -> "INT:")
    // Restore the prefix content.
    if (prefix.startsWith(value)) {
      setNewTask(prev => ({ ...prev, task_ref: prefix }));
      return;
    }

    // Fallback: If user prepended or messed up prefix, try to enforce it.
    // But since the main bug is the "double" caused by blindly adding prefix, 
    // we only add prefix if it's completely missing or not matching logic above.
    // However, simplest fix for the "double" issue described is preventing the blind concatenation.

    // If value doesn't start with prefix, it likely means they modified the prefix part.
    // We enforce the prefix + whatever "content" they might have typed *after* the prefix?
    // Hard to guess. 
    // For now, allow text update but try our best.
    // But given the "INT: INT:" issue, returning here prevents the update which resets it to previous state?
    // No, controlled input needs set state.

    // If we just do nothing, input freezes.
    // If we just set `value`, they lose prefix.
    // We want to force prefix.

    setNewTask(prev => ({ ...prev, task_ref: prefix + value }));
  };

  const [completedTasksCount, setCompletedTasksCount] = useState('');
  const [completedTasksHours, setCompletedTasksHours] = useState('');
  const [yetReason, setYetReason] = useState('');
  const [overallHours, setOverallHours] = useState('');
  const [completedTicketsCount, setCompletedTicketsCount] = useState('');
  const [completedTicketsHours, setCompletedTicketsHours] = useState('');
  const [ticketsYetReason, setTicketsYetReason] = useState('');

  // Admin view states
  const [currentDate, setCurrentDate] = useState(() => new Date(date + 'T00:00:00'));
  const [fromDate, setFromDate] = useState(fromDateParam);
  const [toDate, setToDate] = useState(toDateParam);

  const [reportData, setReportData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [openedTicketId, setOpenedTicketId] = useState(null);
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  const [adminSortField, setAdminSortField] = useState(null);
  const [adminSortDir, setAdminSortDir] = useState('asc');

  // Assign Task Tab States
  const [assignSearchTerm, setAssignSearchTerm] = useState('');
  const [assignSortField, setAssignSortField] = useState('created_at');
  const [assignSortDir, setAssignSortDir] = useState('desc');
  const [assignItemsPerPage, setAssignItemsPerPage] = useState(50);
  const [assignCurrentPage, setAssignCurrentPage] = useState(1);

  const [reportType, setReportType] = useState(null);
  const [downloadingButton, setDownloadingButton] = useState(null); // 'internal' | 'customer' | null
  const dateInputRef = useRef(null);
  const toDateInputRef = useRef(null);
  const myTasksDateInputRef = useRef(null);


  const totalTasks = tasks.length;
  const totalTickets = tickets.length;
  const completedTasks = tasks.filter(t => t.status === 'Completed' || t.status === 'Fully Approved').length;
  const completedTickets = tickets.filter(t => (t.status || '').toLowerCase() === 'closed').length;
  const totalTaskHours = tasks.reduce((sum, t) => sum + (parseFloat(t.hours) || 0), 0);
  const totalReportHours = reportData.reduce((sum, r) => sum + (parseFloat(r.hours) || 0), 0);



  // Get current date display
  const getCurrentDateDisplay = () => {
    return currentDate.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Navigate to previous day
  // Navigate to previous day
  const goToPrevDay = () => {
    // Close form if open when navigating dates
    if (showForm) {
      setShowForm(false);
      setFormErrors({ task_type: '', work_done: '', hours: '', task_ref: '' });
      setNewTask({ task_type: '', ticket_id: null, task_ref: '', work_done: '', hours: '', description: '', completion_percentage: 0 });
    }

    if (activeTab === 'my_tasks') {
      const prevDay = new Date(myTasksDate);
      prevDay.setDate(prevDay.getDate() - 1);

      if (!isEditableDate(prevDay)) {
        toast.error('Previous date limit reached');
        return;
      }
      setDevLoading(true);
      setMyTasksDate(prevDay);
      // Navigation handled by useEffect on myTasksDate
    } else {
      const prevDay = new Date(currentDate);
      prevDay.setDate(prevDay.getDate() - 1);
      setDevLoading(true);
      setCurrentDate(prevDay);
      setFromDate(formatDate(prevDay));
      setToDate(formatDate(prevDay));
    }
  };

  // Navigate to next day (with future date blocking)
  const goToNextDay = () => {
    const today = new Date();

    if (activeTab === 'my_tasks') {
      const nextDay = new Date(myTasksDate);
      nextDay.setDate(nextDay.getDate() + 1);
      if (formatDate(nextDay) > formatDate(today)) return;

      closeForm(); // helper
      setDevLoading(true);
      setMyTasksDate(nextDay);
    } else {
      const workingDate = currentDate;
      const nextDay = new Date(workingDate);
      nextDay.setDate(nextDay.getDate() + 1);
      if (formatDate(nextDay) > formatDate(today)) return;

      closeForm(); // helper
      setDevLoading(true);
      setCurrentDate(nextDay);
      setFromDate(formatDate(nextDay));
      setToDate(formatDate(nextDay));
    }
  };

  const closeForm = () => {
    if (showForm) {
      setShowForm(false);
      setFormErrors({ task_type: '', work_done: '', hours: '', task_ref: '' });
      setNewTask({ task_type: '', ticket_id: null, task_ref: '', work_done: '', hours: '', description: '', completion_percentage: 0 });
    }
  }

  // Set to today (Business Today: includes yesterday before 6 AM)
  const goToToday = () => {
    const today = new Date();
    if (today.getHours() < 6) {
      today.setDate(today.getDate() - 1);
    }
    const todayStr = formatDate(today);

    if (activeTab === 'my_tasks') {
      if (formatDate(myTasksDate) === todayStr) {
        loadMyRows(todayStr); // Force reload
        return;
      }
      setDevLoading(true);
      setMyTasksDate(today);
      // useEffect handles load
    } else {
      const currentStr = formatDate(currentDate);
      // If already on today, force a reload to ensure loading state clears
      // and to refresh data, instead of relying on useEffect navigation which may not fire
      if (currentStr === todayStr) {
        setFromDate(todayStr);
        setToDate(todayStr);
        setAdminSearchTerm(''); // Optional: clear search on reset
        loadMyRows(todayStr);
        return;
      }

      setDevLoading(true);
      setCurrentDate(today);
      setFromDate(todayStr);
      setToDate(todayStr);
    }
  };

  const filteredReportData = useMemo(() => {
    const term = adminSearchTerm.toLowerCase();
    let list = reportData;

    if (term) {
      list = reportData.filter(r => {
        const employeeName = (r.employee_name || '').toLowerCase();
        const taskId = String(r.id || '');
        const reference = (r.reference || '').toLowerCase();
        const ticketId = String(r.ticket_id || '');
        const workDone = (r.work_done || '').toLowerCase();

        const typeRaw = (r.task_type_name || r.type || 'Task');
        const map = { BG: 'Bug', DN: 'Design', QA: 'Testing', DT: 'Development', SS: 'Security Scan', IM: 'Implementation', TR: 'Training', HR: 'HR', OT: 'Others', DP: 'DEMO PREP' };
        const typeName = (map[typeRaw] || typeRaw).toLowerCase();

        return employeeName.includes(term) ||
          taskId.includes(term) ||
          reference.includes(term) ||
          ticketId.includes(term) ||
          workDone.includes(term) ||
          typeName.includes(term);
      });
    }

    if (adminSortField) {
      const dir = adminSortDir === 'desc' ? -1 : 1;
      const val = (row) => {
        const key = adminSortField;
        let v = row[key];
        if (key === 'type') {
          const raw = (row.task_type_name || row.type || 'Task');
          const map = { BG: 'Bug', DN: 'Design', QA: 'Testing', DT: 'Development', SS: 'Security Scan', IM: 'Implementation', TR: 'Training', HR: 'HR', OT: 'Others', DP: 'DEMO PREP' };
          v = map[raw] || raw;
        }
        if (key === 'hours') return parseFloat(v || 0);
        return String(v || '').toLowerCase();
      };
      list = [...list].sort((a, b) => {
        const va = val(a), vb = val(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }
    return list;
  }, [reportData, adminSearchTerm, adminSortField, adminSortDir]);

  // Filtered Assignments for Assign Task Tab
  const filteredAssignments = useMemo(() => {
    let list = assignmentsSent;

    // 1. Date Filter (Client-side match against fromDate/toDate)
    if (fromDate && toDate) {
      const start = new Date(fromDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);

      list = list.filter(t => {
        if (!t.created_at) return false;
        const d = new Date(t.created_at);
        return d >= start && d <= end;
      });
    }

    // 2. Search Filter
    const term = assignSearchTerm.toLowerCase();
    if (term) {
      list = list.filter(t => {
        const assignedTo = (t.assigned_to_name || '').toLowerCase();
        const type = (t.task_type || '').toLowerCase();
        const ref = (t.task_ref || '').toLowerCase();
        const desc = (t.description || '').toLowerCase();
        const status = (t.status || '').toLowerCase();

        return assignedTo.includes(term) ||
          type.includes(term) ||
          ref.includes(term) ||
          desc.includes(term) ||
          status.includes(term);
      });
    }

    // 3. Sorting
    if (assignSortField) {
      const dir = assignSortDir === 'desc' ? -1 : 1;
      const val = (row) => {
        const key = assignSortField;
        if (key === 'created_at') return new Date(row[key] || 0).getTime();
        return String(row[key] || '').toLowerCase();
      };

      list = [...list].sort((a, b) => {
        const va = val(a), vb = val(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }

    return list;
  }, [assignmentsSent, assignSearchTerm, assignSortField, assignSortDir, fromDate, toDate]);

  const toggleAssignSort = (field) => {
    if (assignSortField === field) {
      setAssignSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setAssignSortField(field);
      setAssignSortDir('asc');
    }
  };

  const toggleAdminSort = (field) => {
    if (adminSortField === field) {
      setAdminSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setAdminSortField(field);
      setAdminSortDir('asc');
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCustomer, selectedEmployees, selectedTaskType]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredReportData.length / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
    if (currentPage < 1) setCurrentPage(1);
  }, [filteredReportData.length, itemsPerPage, currentPage]);

  useEffect(() => {
    if (adminSearchTerm) setAdminSearchTerm('');
  }, [currentPage]);

  // Load form data for admin or approver in All Tasks
  useEffect(() => {
    if (isAdmin || (isApprover && activeTab === 'all_tasks')) {
      const loadFormData = async () => {
        try {
          const r = await api.get('/timesheet/form-data');
          const empOpts = (r.data?.employees || []).map(e => ({ value: e.user_id, label: e.name }));
          const custOpts = (r.data?.customers || []).map(c => ({ value: c.cust_key, label: c.customer_name }));
          const desigOpts = [
            { value: 'all', label: 'All' },
            ...(r.data?.designations || []).map(d => ({ value: d.id, label: d.designation_name }))
          ];
          const seen = new Set();
          const uniqueCust = [];
          for (const o of custOpts) {
            const k = (o.label || '').trim().toLowerCase();
            if (!k || seen.has(k)) continue;
            seen.add(k);
            uniqueCust.push(o);
          }
          setEmployeeOptions(empOpts);
          setDesignationOptions(desigOpts);
          setCustomerOptionsFilter(uniqueCust);

          loadTaskTypes();

          // Load customer options for edit functionality
          await loadCustomerOptions();
        } catch (e) {
          console.error('Failed to load form data', e);
        }
      };
      loadFormData();
    }
  }, [isAdmin, isApprover, activeTab]);

  // Load developer data
  useEffect(() => {
    if (!isAdmin || (isApprover && activeTab === 'my_tasks')) {
      const load = async () => {
        // load user's timesheet rows
        // Use myTasksDate if we are strictly in My Tasks tab context (and not Admin doing other things)
        // But wait, standard user IS in My Tasks context by default.
        // Simplest check: use myTasksDate formatted.
        const dateToLoad = (activeTab === 'my_tasks') ? formatDate(myTasksDate) : date;
        await loadMyRows(dateToLoad);

        // load customer options for Task.ref dropdown
        await loadCustomerOptions();
        loadTaskTypes();

        // load user's tickets
        await loadTickets();
      };
      load();
    }
  }, [date, myTasksDate, customerId, isAdmin, isApprover, activeTab]);

  // Ensure Date is reset for Admin/TaskAdmin on mount if params empty
  useEffect(() => {
    if (isAdmin) {
      // If we don't have URL params forcing a date, ensure we start with Today.
      // This fixes issues where stale state (e.g. from hot reload or weird nav) keeps "Yesterday".
      if (!params.get('date')) {
        const todayStr = getTodayStr();
        setFromDate(todayStr);
        setToDate(todayStr);
        setCurrentDate(new Date(todayStr + 'T00:00:00'));
      }
    }
  }, [isAdmin]);

  /* URL Sync logic removed as per user request to prevent tab leakage
  useEffect(() => {
    if (!isAdmin || (isApprover && activeTab === 'my_tasks')) {
      const desiredDate = formatDate(currentDate);
      const desiredCustomer = selectedCustomer?.value ? String(selectedCustomer.value) : '';
      const existingDate = params.get('date') || formatDate(new Date());
      const existingCustomer = params.get('customer') || '';
      if (desiredDate !== existingDate || desiredCustomer !== existingCustomer) {
        const q = new URLSearchParams(params);
        q.set('date', desiredDate);
        if (desiredCustomer) q.set('customer', desiredCustomer); else q.delete('customer');
        navigate({ search: `?${q.toString()}` }, { replace: true });
      }
    }
  }, [selectedCustomer, currentDate, isAdmin, isApprover, activeTab]);
  */

  useEffect(() => {
    if ((!isAdmin || (isApprover && activeTab === 'my_tasks')) && customerOptionsFilter.length && customerId) {
      const opt = customerOptionsFilter.find(o => String(o.value) === String(customerId));
      if (opt) setSelectedCustomer(opt);
    }
  }, [customerOptionsFilter, customerId, isAdmin, isApprover, activeTab]);

  // Auto-select current user for developer
  useEffect(() => {
    if ((!isAdmin || (isApprover && activeTab === 'my_tasks')) && user) {
      const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;

      // Ensure we don't overwrite if manually changed, but initial load needs it
      // Simple check: if empty, set it
      if (selectedEmployees.length === 0) {
        setSelectedEmployees([{ value: user.user_id, label: name }]);
      }
    }
    // If Admin/All Task view, we might want to default to nothing or current user? usually nothing for admin report
  }, [isAdmin, user, isApprover, activeTab]);

  // Fetch report data
  const fetchReport = async () => {
    setLoadingReport(true);
    try {
      const params = { from: fromDate, to: toDate };
      if (selectedEmployees.length > 0) {
        params.employeeIds = selectedEmployees.map(e => e.value).join(',');
      }
      if (selectedDesignations.length > 0) {
        params.designationIds = selectedDesignations.map(d => d.value).join(',');
      }
      if (selectedCustomer) params.customerId = selectedCustomer.value;
      if (selectedTaskType) params.taskType = selectedTaskType.value;

      const r = await api.get('/timesheet/admin-detailed-report', { params });
      setReportData(r.data?.data || []);
      setLoadingReport(false);
    } catch (e) {
      setLoadingReport(false);
    }
  };

  // Auto-fetch report when filters or fromDate/toDate changes
  useEffect(() => {
    if (!fromDate || !toDate) return;
    // Run if Admin OR All Tasks tab (allow Employees)
    if (!(isAdmin || activeTab === 'all_tasks')) return;

    const timer = setTimeout(fetchReport, 500);
    return () => clearTimeout(timer);
  }, [selectedEmployees, selectedDesignations, fromDate, toDate, selectedCustomer, selectedTaskType, activeTab]);

  // Reset pagination when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedEmployees, selectedDesignations, fromDate, toDate, selectedCustomer, selectedTaskType, adminSearchTerm]);

  const exportAssignmentPDF = async () => {
    const params = { from: fromDate, to: toDate, searchTerm: assignSearchTerm };
    setDownloadingButton('assignment');

    try {
      const res = await api.get('/timesheet/export-assignment-report-pdf', { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const getTimestamp = () => {
        const now = new Date();
        const ymd = now.getFullYear() + String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0');
        let h = now.getHours();
        const m = String(now.getMinutes()).padStart(2, '0');
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        const hStr = String(h).padStart(2, '0');
        return `${ymd}_${hStr}_${m}_${ampm}`;
      };

      const filename = `eNova_Assignment_Report_${getTimestamp()}.pdf`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to export Assignment PDF');
    } finally {
      setDownloadingButton(null);
    }
  };

  const exportPDF = async (overrideRange) => {
    const effFrom = overrideRange?.from ?? (activeTab === 'my_tasks' ? formatDate(myTasksDate) : fromDate);
    const effTo = overrideRange?.to ?? (activeTab === 'my_tasks' ? formatDate(myTasksDate) : toDate);
    const params = { from: effFrom, to: effTo };
    const effEmployeeIds = overrideRange?.employeeIds ?? (selectedEmployees && selectedEmployees.length > 0 ? selectedEmployees.map(e => e.value).join(',') : null);
    if (effEmployeeIds) {
      params.employeeIds = effEmployeeIds;
    }
    if (selectedDesignations && selectedDesignations.length > 0) {
      params.designationIds = selectedDesignations.map(d => d.value).join(',');
    }
    if (selectedCustomer) params.customerId = selectedCustomer.value;
    if (selectedTaskType) params.taskType = selectedTaskType.value;
    if (selectedTaskType) params.taskType = selectedTaskType.value;
    // if (selectedDesignations && selectedDesignations.length > 0) {
    //   params.designationIds = selectedDesignations.map(d => d.value).join(',');
    // }

    if (overrideRange?.type || reportType) params.reportType = overrideRange?.type || reportType;
    if (overrideRange?.isCustomerData) params.useCustomerData = 1;
    if (params.reportType === 'assignment') params.searchTerm = assignSearchTerm;

    // Determine loading button
    let btnType = overrideRange?.isCustomerData ? 'customer' : 'internal';
    if (overrideRange?.type === 'assignment') btnType = 'assignment';
    setDownloadingButton(btnType);

    try {
      // setIsDownloading(true); // Removed boolean state
      const res = await api.get('/timesheet/export-status-report-pdf', { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const getTimestamp = () => {
        const now = new Date();
        const ymd = now.getFullYear() + String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0');
        let h = now.getHours();
        const m = String(now.getMinutes()).padStart(2, '0');
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        const hStr = String(h).padStart(2, '0');
        return `${ymd}_${hStr}_${m}_${ampm}`;
      };

      const type = params.reportType || 'staff';
      let typeLabel = 'Report';
      if (type === 'staff') typeLabel = 'Employee_Wise_Report';
      if (type === 'customer') typeLabel = 'Customer_Wise_Report';
      if (type === 'task') typeLabel = 'Task_Wise_Report';
      if (type === 'designation') typeLabel = 'Designation_Wise_Report';
      if (type === 'assignment') typeLabel = 'Assignment_Report';

      const prefix = params.useCustomerData ? 'eNova_CustomerData_' : 'eNova_Internal_';
      const filename = `${prefix}${typeLabel}_${getTimestamp()}.pdf`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to export PDF');
    } finally {
      setDownloadingButton(null);
    }
  };

  const handleDownloadClientReport = async () => {
    // Only customer data reports should be available on the client side.
    // Specifying the 'By designation' report with customer data mode enabled.
    await exportPDF({ type: 'designation', isCustomerData: true });
  };

  const exportExcel = () => {
    const effFrom = activeTab === 'my_tasks' ? formatDate(myTasksDate) : fromDate;
    const effTo = activeTab === 'my_tasks' ? formatDate(myTasksDate) : toDate;
    let url = `${api.defaults.baseURL}/timesheet/export-status-report-excel?from=${effFrom}&to=${effTo}`;
    if (selectedEmployees && selectedEmployees.length > 0) {
      url += `&employeeIds=${selectedEmployees.map(e => e.value).join(',')}`;
    }
    if (selectedCustomer) {
      url += `&customerId=${selectedCustomer.value}`;
    }
    if (selectedTaskType) {
      url += `&taskType=${selectedTaskType.value}`;
    }
    // Pass context
    url += `&context=${activeTab}`;

    // Note: Excel export uses server-side filename generation
    // The backend should handle dynamic naming based on filters
    window.open(url, '_blank');
  };

  const handleDateWiseFilter = () => {
    toast.success('Date-wise filter applied');
  };

  const handleWeeklyReport = () => {
    const today = new Date();
    const firstDay = new Date(today.setDate(today.getDate() - today.getDay() + 1)); // Monday
    const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 7)); // Sunday
    setFromDate(formatDate(firstDay));
    setToDate(formatDate(lastDay));
    toast.success('Weekly report filter applied');
  };

  // Get minimum date allowed for UI selection (UX hint only — backend is the real enforcer)
  const getMinDate = () => {
    // Admins and backend-whitelisted users: no min restriction
    if (isSuperAdmin || isAdmin || canLogPastDate) return undefined;
    const today = new Date();
    // 6 AM grace period: include yesterday
    if (today.getHours() < 6) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return formatDate(yesterday);
    }
    return formatDate(today);
  };

  // Handle date input click to show date picker
  const handleDateInputClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  return (
    <Layout defaultSidebarOpen={false}>
      <div className="min-h-screen bg-gray-50 p-2 sm:p-4 md:p-6">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Timesheet</h1>
            <p className="text-gray-600">{(isAdmin || (isApprover && activeTab === 'all_tasks')) ? 'Employee Timesheet Report' : 'Your Daily Tasks Status!'}</p>
          </div>

          {/* Main Container with Filters and Table */}
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
            {/* Filter Section - Responsive Grid */}
            <div className="mb-6">
              {/* Tabs List - Hidden for Client View */}
              {!isCustomer && (
                <div className="flex border-b mb-6">
                  {(isApprover || assignedTasks.length > 0) && (
                    <button
                      onClick={() => setActiveTab('waiting')}
                      className={`flex items-center gap-2 px-6 py-3 rounded-t-lg font-bold text-sm transition-all duration-200 ${activeTab === 'waiting'
                        ? 'bg-red-600 text-white shadow-md border-b-2 border-red-700'
                        : 'bg-white text-gray-600 hover:text-red-600 hover:bg-red-50'
                        }`}
                    >
                      <div className="relative">
                        <FiCheckCircle className="w-4 h-4" />
                        {pendingApprovals.length > 0 && (
                          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full animate-pulse">
                            {pendingApprovals.length}
                          </span>
                        )}
                      </div>
                      Waiting On Me
                    </button>
                  )}
                  {((!isAdmin || isSuperAdmin || roleId === 9) && roleId !== 1) && (
                    <button
                      onClick={() => {
                        setActiveTab('my_tasks');
                        // Navigation for My Tasks
                        if (roleId !== 11 && !([182, 107].includes(userId))) {
                          // No automatic reset of dates when switching - respect separation
                          // navigate({ search: `?date=${fromDate}` }, { replace: true });
                        }
                      }}
                      className={`flex items-center gap-2 px-6 py-3 rounded-t-lg font-bold text-sm transition-all duration-200 ${activeTab === 'my_tasks'
                        ? 'bg-red-600 text-white shadow-md border-b-2 border-red-700'
                        : 'bg-white text-gray-600 hover:text-red-600 hover:bg-red-50'
                        }`}
                    >
                      <FiUsers className="w-4 h-4" />
                      My Tasks
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab('all_tasks')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-t-lg font-bold text-sm transition-all duration-200 ${activeTab === 'all_tasks'
                      ? 'bg-red-600 text-white shadow-md border-b-2 border-red-700'
                      : 'bg-white text-gray-600 hover:text-red-600 hover:bg-red-50'
                      }`}
                  >
                    <FiFileText className="w-4 h-4" />
                    {isCustomer ? 'My Company Tasks' : 'All Tasks'}
                  </button>
                  {(roleId === 0) && !isCustomer && (
                    <button
                      onClick={() => setActiveTab('assign')}
                      className={`flex items-center gap-2 px-6 py-3 rounded-t-lg font-bold text-sm transition-all duration-200 ${activeTab === 'assign'
                        ? 'bg-red-600 text-white shadow-md border-b-2 border-red-700'
                        : 'bg-white text-gray-600 hover:text-red-600 hover:bg-red-50'
                        }`}
                    >
                      <FiPlusCircle className="w-4 h-4" />
                      Assign Task
                    </button>
                  )}
                  {/* <button
                    onClick={() => navigate('/org')}
                    className="ml-auto flex items-center gap-2 px-4 py-3 text-gray-600 hover:text-blue-600 transition-colors"
                    title="Organization Chart"
                  >
                    <FiShare2 className="w-6 h-6" />
                  </button> */}
                </div>
              )}



              {(activeTab === 'all_tasks' || activeTab === 'waiting' || activeTab === 'assign' || activeTab === 'my_tasks') && (
                <>
                  {/* Row 1: Filters (All Tasks Only) */}
                  {(isAdmin || isApprover) && !isCustomer && activeTab === 'all_tasks' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {/* 1. Task Type Filter */}
                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest mb-2">Task Type</label>
                        <Select
                          options={taskTypeOptions}
                          value={selectedTaskType}
                          onChange={setSelectedTaskType}
                          placeholder="All Types"
                          isClearable
                          className="text-sm"
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              borderColor: '#e5e7eb',
                              borderRadius: '0.5rem',
                              minHeight: '40px',
                              boxShadow: 'none',
                              '&:hover': { borderColor: '#ef4444' }
                            }),
                            menu: (provided) => ({ ...provided, zIndex: 50 })
                          }}
                        />
                      </div>

                      {/* 2. Customer Dropdown */}
                      {!isCustomer && (
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest mb-2">Customer</label>
                          <Select
                            options={customerOptionsFilter}
                            value={selectedCustomer}
                            onChange={setSelectedCustomer}
                            placeholder="All Customers"
                            isClearable
                            className="text-sm"
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                borderColor: '#e5e7eb',
                                borderRadius: '0.5rem',
                                minHeight: '40px',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#ef4444' }
                              }),
                              menu: (provided) => ({ ...provided, zIndex: 50 })
                            }}
                          />
                        </div>
                      )}

                      {/* 3. Employee Dropdown */}
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest mb-2">Employees</label>
                        <Select
                          isMulti
                          options={employeeOptions}
                          className="text-xs"
                          value={selectedEmployees}
                          onChange={setSelectedEmployees}
                          placeholder="Select employees..."
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              borderColor: '#e5e7eb',
                              borderRadius: '0.5rem',
                              minHeight: '40px',
                              boxShadow: 'none',
                              '&:hover': { borderColor: '#ef4444' }
                            }),
                            menu: (provided) => ({ ...provided, zIndex: 50 })
                          }}
                        />
                      </div>

                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest mb-2">Roles</label>
                         <Select
                          isMulti
                          options={designationOptions}
                          className="text-xs"
                          value={selectedDesignations}
                          onChange={handleDesignationChange}
                          placeholder="Select roles..."
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          components={{
                            Option: CheckboxOption,
                            ValueContainer: MultiValueSummary
                          }}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              borderColor: '#e5e7eb',
                              borderRadius: '0.5rem',
                              minHeight: '40px',
                              boxShadow: 'none',
                              '&:hover': { borderColor: '#ef4444' }
                            }),
                            menu: (provided) => ({ ...provided, zIndex: 50 }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: 'transparent',
                              color: 'inherit',
                              '&:active': {
                                backgroundColor: 'transparent'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Date Controls and Action Buttons Row */}
                  {/* STANDARD TOOLBAR: All Tasks, Assign, and now My Tasks (Centered) */}
                  {activeTab !== 'waiting' && (
                    <div className="flex flex-wrap items-center justify-center gap-2 w-full mt-4">
                      <button
                        onClick={goToPrevDay}
                        className="p-2 border rounded-lg hover:bg-gray-50 flex-shrink-0"
                        title="Previous Day"
                      >
                        <FiChevronLeft className="w-4 h-4 text-gray-600" />
                      </button>

                      <div className="flex items-center gap-2">
                        {activeTab === 'my_tasks' ? (
                          /* Centered Single Date for My Tasks */
                          <div
                            className="relative w-48 flex items-center justify-center p-2 border rounded-lg bg-gray-50 cursor-pointer hover:border-blue-400 transition-colors shadow-sm"
                            onClick={() => myTasksDateInputRef.current?.showPicker()}
                          >
                            <FiCalendar className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                            <span className="text-sm font-semibold text-gray-700">{formatDateDisplay(formatDate(myTasksDate))}</span>
                            <input
                              type="date"
                              ref={myTasksDateInputRef}
                              value={formatDate(myTasksDate)}
                              max={formatDate(new Date())}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (new Date(val) > new Date()) {
                                  toast.error('Cannot select future date');
                                  return;
                                }
                                if (!isEditableDate(new Date(val + 'T00:00:00'))) {
                                  toast.error('You do not have permission to select previous dates.');
                                  return;
                                }
                                setDevLoading(true);
                                setMyTasksDate(new Date(val + 'T00:00:00'));
                              }}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                          </div>
                        ) : (
                          /* Range Date for All Tasks / Assign */
                          <>
                            <div
                              className="relative w-40 flex items-center justify-center p-2 border rounded-lg bg-gray-50 cursor-pointer"
                              onClick={() => dateInputRef.current?.showPicker()}
                            >
                              <FiCalendar className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-700">{formatDateDisplay(fromDate)}</span>
                              <input
                                type="date"
                                ref={dateInputRef}
                                value={fromDate}
                                max={formatDate(new Date())}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (new Date(val) > new Date()) {
                                    toast.error('Cannot select future date');
                                    return;
                                  }
                                  setDevLoading(true);
                                  setFromDate(val);
                                  if (val > toDate) setToDate(val);
                                  setCurrentDate(new Date(val + 'T00:00:00'));
                                  setAdminSearchTerm('');
                                }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>

                            <span className="text-sm text-gray-600">to</span>

                            <div
                              className="relative w-40 flex items-center justify-center p-2 border rounded-lg bg-gray-50 cursor-pointer"
                              onClick={() => toDateInputRef.current?.showPicker()}
                            >
                              <FiCalendar className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-700">{formatDateDisplay(toDate)}</span>
                              <input
                                type="date"
                                ref={toDateInputRef}
                                value={toDate}
                                min={fromDate}
                                max={formatDate(new Date())}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (new Date(val) > new Date()) {
                                    toast.error('Cannot select future date');
                                    return;
                                  }
                                  if (val < fromDate) {
                                    toast.error('End date cannot be before start date');
                                    return;
                                  }
                                  setDevLoading(true);
                                  setToDate(val);
                                }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </>
                        )}
                      </div>

                      <button
                        onClick={goToNextDay}
                        disabled={(activeTab === 'my_tasks' ? formatDate(myTasksDate) : toDate) >= formatDate(new Date())}
                        className={`p-2 border rounded-lg flex-shrink-0 ${(activeTab === 'my_tasks' ? formatDate(myTasksDate) : toDate) >= formatDate(new Date())
                          ? 'opacity-50 cursor-not-allowed bg-gray-100'
                          : 'hover:bg-gray-50'
                          }`}
                        title="Next Day"
                      >
                        <FiChevronRight className="w-4 h-4 text-gray-600" />
                      </button>

                      <button
                        onClick={goToToday}
                        className="p-2 border text-green-600 border-green-200 bg-green-50 rounded-lg hover:bg-green-100 flex-shrink-0"
                        title="Reset to Today"
                      >
                        <FiRotateCcw className="w-4 h-4" />
                      </button>

                      <div className="flex items-center gap-2 ml-2 relative">
                        {/* Hide Report Button for Customers completely */}
                        {!isCustomer && (
                          (isAdmin || isApprover) ? (
                            <>
                              {/* 1. Internal/General Report Button - Blue/Purple */}
                              {(activeTab === 'all_tasks' || activeTab === 'my_tasks' || activeTab === 'assign') && (
                                <div className="relative">
                                  <button
                                    onClick={() => {
                                      if ((isAdmin || isApprover) && activeTab === 'my_tasks') {
                                        exportPDF({ employeeIds: userId, type: 'task', isCustomerData: false });
                                      } else if (activeTab === 'assign') {
                                        exportAssignmentPDF();
                                      } else {
                                        setShowReportDropdown(!showReportDropdown);
                                        setShowCustomerReportDropdown(false);
                                      }
                                    }}
                                    disabled={downloadingButton !== null}
                                    className={`flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium ${downloadingButton === 'internal' || downloadingButton === 'assignment' ? 'opacity-75 cursor-wait' : ''}`}
                                  >
                                    {downloadingButton === 'internal' || downloadingButton === 'assignment' ? (
                                      <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Downloading...
                                      </>
                                    ) : (
                                      <>
                                        <FiDownload className="w-4 h-4" />
                                        {activeTab === 'my_tasks' || activeTab === 'assign' ? 'Download Report' : 'Internal Report'}
                                        {(activeTab !== 'assign' && !((isAdmin || isApprover) && activeTab === 'my_tasks')) && <FiChevronDown className="w-4 h-4" />}
                                      </>
                                    )}
                                  </button>
                                  {showReportDropdown && (
                                    <div className="absolute top-12 right-0 bg-white border rounded-lg shadow-xl z-50 w-48 py-1">
                                      <button
                                        onClick={() => {
                                          exportPDF({ type: 'task', isCustomerData: false });
                                          setShowReportDropdown(false);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        By Task
                                      </button>
                                      <button
                                        onClick={() => {
                                          exportPDF({ type: 'customer', isCustomerData: false });
                                          setShowReportDropdown(false);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        By Customer
                                      </button>
                                      <button
                                        onClick={() => {
                                          exportPDF({ type: 'staff', isCustomerData: false });
                                          setShowReportDropdown(false);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        By Employee
                                      </button>
                                      <button
                                        onClick={() => {
                                          exportPDF({ type: 'designation', isCustomerData: false });
                                          setShowReportDropdown(false);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        By Designation
                                      </button>
                                      {isApprover && !isAdmin && (
                                        <button
                                          onClick={() => {
                                            exportPDF({ employeeIds: userId, type: 'task', isCustomerData: false });
                                            setShowReportDropdown(false);
                                          }}
                                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                          My Tasks
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* 2. Customer Report Button - Purple (Only for All Tasks) */}
                              {activeTab === 'all_tasks' && (
                                <div className="relative ml-2">
                                  <button
                                    onClick={() => {
                                      setShowCustomerReportDropdown(!showCustomerReportDropdown);
                                      setShowReportDropdown(false);
                                    }}
                                    disabled={downloadingButton !== null}
                                    className={`flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium ${downloadingButton === 'customer' ? 'opacity-75 cursor-wait' : ''}`}
                                  >
                                    {downloadingButton === 'customer' ? (
                                      <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Downloading...
                                      </>
                                    ) : (
                                      <>
                                        <FiDownload className="w-4 h-4" />
                                        Customer Report
                                        <FiChevronDown className="w-4 h-4" />
                                      </>
                                    )}
                                  </button>
                                  {showCustomerReportDropdown && (
                                    <div className="absolute top-12 right-0 bg-white border rounded-lg shadow-xl z-50 w-48 py-1">
                                      <button
                                        onClick={() => {
                                          exportPDF({ type: 'task', isCustomerData: true });
                                          setShowCustomerReportDropdown(false);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        By Task
                                      </button>
                                      <button
                                        onClick={() => {
                                          exportPDF({ type: 'designation', isCustomerData: true });
                                          setShowCustomerReportDropdown(false);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        By Designation
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <button
                              onClick={() => exportPDF()}
                              disabled={downloadingButton !== null}
                              className={`flex items-center gap-1 px-3 py-1.5 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 text-sm font-medium ${downloadingButton !== null ? 'opacity-75 cursor-wait' : ''}`}
                            >
                              {downloadingButton !== null ? (
                                <>
                                  <div className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <FiDownload className="w-4 h-4" />
                                  Download Report
                                </>
                              )}
                            </button>
                          )
                        )}

                        {/* 3. Client-side Download Report (Specifically for Customers) */}
                        {isCustomer && (
                          <div className="ml-2">
                            <button
                              onClick={handleDownloadClientReport}
                              disabled={downloadingButton !== null}
                              className={`flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium shadow-sm transition-all ${downloadingButton === 'customer' ? 'opacity-75 cursor-wait' : ''}`}
                              title="Download Report (By Designation)"
                            >
                              {downloadingButton === 'customer' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <FiDownload className="w-4 h-4" />
                                  Download Report
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

            </div >

            {/* Table Section */}
            < div className="border-t pt-6" >
              {/** V1: Waiting On me (Approvals & Assigned Tasks) **/}
              {
                ((isApprover || assignedTasks.length > 0) && activeTab === 'waiting') && (
                  <div className="mb-0">
                    {/* Approvals Section */}
                    {isApprover && (
                      <div className="mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                          <div className="flex flex-wrap items-center gap-4">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                              <FiCheckCircle className="w-5 h-5 text-yellow-600" />
                              Tasks Waiting for Your Approval: {sortedPendingApprovals.length}
                            </h2>

                            {selectedTaskIds.size > 0 && (
                              <div className="flex items-center bg-green-600 rounded overflow-hidden shadow-sm h-fit">
                                {/* Admin (Role 1/9) Actions: Just Approve (Override) button */}
                                {(roleId === 1) && (
                                  <button
                                    onClick={() => handleBulkApprove('override')}
                                    className="px-3 py-1.5 text-white hover:bg-green-700 text-xs font-medium flex items-center gap-2"
                                  >
                                    <FiCheckCircle className="w-3.5 h-3.5" /> Approve Selected ({selectedTaskIds.size})
                                  </button>
                                )}

                                {/* Task Admin (Role 11) Actions: Single Button + Modal */}
                                {(roleId === 11) && (
                                  <button
                                    onClick={() => handleBulkApproveClick(null)}
                                    className="px-3 py-1.5 text-white hover:bg-green-700 text-xs font-medium flex items-center gap-2"
                                  >
                                    <FiCheckCircle className="w-3.5 h-3.5" /> Approve Selected ({selectedTaskIds.size})
                                  </button>
                                )}

                                {/* Other Approvers: Standard Approve Button */}
                                {(roleId !== 1 && roleId !== 11 && roleId !== 9) && (
                                  <button
                                    onClick={() => handleBulkApprove('normal')}
                                    className="px-3 py-1.5 text-white hover:bg-green-700 text-xs font-medium flex items-center gap-2"
                                  >
                                    <FiCheckCircle className="w-3.5 h-3.5" /> Approve Selected ({selectedTaskIds.size})
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="relative w-full sm:w-64">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              placeholder="Search pending tasks..."
                              className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm"
                              value={waitingSearchTerm}
                              onChange={e => setWaitingSearchTerm(e.target.value)}
                            />
                          </div>
                        </div>
                        {(!loadingPending && pendingApprovals.length === 0) ? (
                          <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                            <p className="text-gray-500">No pending approvals found.</p>
                          </div>
                        ) : (
                          <>
                            {/* Color Legend/Agenda */}
                            {/* {([1, 9, 11].includes(roleId)) && (
                        <div className="mb-3 flex items-center gap-4 px-2">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-[#FFE9E9] border-2 border-rose-400 rounded"></div>
                            <span className="text-xs font-medium text-gray-700">Employee Data</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-[#EBF5FF] border-2 border-blue-400 rounded"></div>
                            <span className="text-xs font-medium text-gray-700">Customer Data</span>
                          </div>
                        </div>
                      )} */}

                            <div className="overflow-x-auto bg-white border border-gray-200 rounded-md shadow-sm">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-700">
                                  <tr>
                                    <th className="px-4 py-2 text-center min-w-[50px]">
                                      <input
                                        type="checkbox"
                                        onChange={toggleAllTasks}
                                        checked={pendingApprovals.length > 0 && selectedTaskIds.size === pendingApprovals.length}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                      />
                                    </th>
                                    {([1, 9, 11].includes(roleId) || isApprover) && (
                                      <th className="px-2 py-2 text-center min-w-[40px]">
                                        <span className="text-xs font-medium text-gray-700"></span>
                                      </th>
                                    )}
                                    <th onClick={() => toggleWaitingSort('date')} className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100">
                                      <div className="flex items-center gap-1">
                                        Date
                                        {waitingSortField === 'date' ? (
                                          waitingSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                        ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                      </div>
                                    </th>
                                    <th onClick={() => toggleWaitingSort('id')} className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100">
                                      <div className="flex items-center gap-1">
                                        Task ID
                                        {waitingSortField === 'id' ? (
                                          waitingSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                        ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                      </div>
                                    </th>
                                    <th onClick={() => toggleWaitingSort('employee_name')} className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100">
                                      <div className="flex items-center gap-1">
                                        Resource
                                        {waitingSortField === 'employee_name' ? (
                                          waitingSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                        ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                      </div>
                                    </th>
                                    <th onClick={() => toggleWaitingSort('type')} className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100">
                                      <div className="flex items-center gap-1">
                                        Task Type
                                        {waitingSortField === 'type' ? (
                                          waitingSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                        ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                      </div>
                                    </th>
                                    <th onClick={() => toggleWaitingSort('task_ref')} className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100 min-w-[200px]">
                                      <div className="flex items-center gap-1">
                                        Task Ref
                                        {waitingSortField === 'task_ref' ? (
                                          waitingSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                        ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                      </div>
                                    </th>
                                    <th onClick={() => toggleWaitingSort('work_done')} className="px-4 py-2 text-left cursor-pointer hover:bg-gray-100">
                                      <div className="flex items-center gap-1">
                                        Work Done
                                        {waitingSortField === 'work_done' ? (
                                          waitingSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                        ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                      </div>
                                    </th>
                                    <th onClick={() => toggleWaitingSort('completion_percentage')} className="px-4 py-2 text-center cursor-pointer hover:bg-gray-100">
                                      <div className="flex items-center justify-center gap-1">
                                        Status %
                                        {waitingSortField === 'completion_percentage' ? (
                                          waitingSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                        ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                      </div>
                                    </th>
                                    <th onClick={() => toggleWaitingSort('hours')} className="px-4 py-2 text-center cursor-pointer hover:bg-gray-100">
                                      <div className="flex items-center justify-center gap-1">
                                        Hours
                                        {waitingSortField === 'hours' ? (
                                          waitingSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                        ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                      </div>
                                    </th>
                                    <th className="px-4 py-2 text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {sortedPendingApprovals.map((task) => (
                                    <React.Fragment key={task.id}>
                                      {/* Row 1: Employee/Original Data */}
                                      <tr
                                        className={`hover:bg-gray-50 transition-colors ${([1, 9, 11].includes(roleId) || isApprover) ? 'bg-white' : 'bg-white'} border-l-4 border-l-white`}
                                        onClick={() => toggleTaskSelection(task.id)}
                                      >
                                        <td className="px-4 py-2 text-center" onClick={e => e.stopPropagation()}>
                                          <input
                                            type="checkbox"
                                            checked={selectedTaskIds.has(task.id)}
                                            onChange={() => toggleTaskSelection(task.id)}
                                            disabled={isInternalTask(task.type)}
                                            className={`w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 ${isInternalTask(task.type) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                          />
                                        </td>
                                        {([1, 9, 11].includes(roleId) || isApprover) && (
                                          <td className="px-2 py-2 text-center" onClick={e => e.stopPropagation()}>
                                            <button
                                              onClick={() => {
                                                const newExpanded = new Set(expandedCustomerRows);
                                                if (newExpanded.has(task.id)) {
                                                  newExpanded.delete(task.id);
                                                } else {
                                                  newExpanded.add(task.id);
                                                }
                                                setExpandedCustomerRows(newExpanded);
                                              }}
                                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                                              title={expandedCustomerRows.has(task.id) ? "Hide Customer Data" : "Show Customer Data"}
                                            >
                                              <FiChevronDown
                                                className={`w-4 h-4 text-gray-600 transition-transform ${expandedCustomerRows.has(task.id) ? 'rotate-180' : ''
                                                  }`}
                                              />
                                            </button>
                                          </td>
                                        )}
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                                          {editingApprovalId === task.id ? (
                                            <input
                                              type="date"
                                              value={editingDate}
                                              onChange={(e) => setEditingDate(e.target.value)}
                                              className="p-1 border rounded text-xs w-32"
                                              max={formatDate(new Date())}
                                            />
                                          ) : (
                                            formatDateDisplay(task.date)
                                          )}
                                          <div className="text-[10px] font-bold text-rose-600 mt-1 uppercase tracking-wider">Employee Data</div>
                                        </td>
                                        <td className="px-4 py-2 text-gray-600">{task.id}</td>
                                        <td className="px-4 py-2 font-medium text-gray-800">{task.employee_name}</td>
                                        <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                                          {editingApprovalId === task.id ? (
                                            <select
                                              value={editingType}
                                              onChange={e => {
                                                const val = e.target.value;
                                                setEditingType(val);
                                                if (['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'].includes(val)) {
                                                  setEditingApprovalRef('INT: ');
                                                  setShowApprovalCustomerDropdown(false);
                                                }
                                              }}
                                              className="p-1 border rounded text-xs w-full"
                                              onClick={e => e.stopPropagation()}
                                            >
                                              {taskTypeOptions.map(opt => (
                                                <option key={opt.value} value={opt.label || opt.value}>{opt.label}</option>
                                              ))}
                                            </select>
                                          ) : (
                                            <span className="px-2 py-1 rounded bg-gray-100 text-xs font-semibold text-gray-700 border border-gray-200">
                                              {task.type}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2 text-gray-700" onClick={e => e.stopPropagation()}>
                                          {editingApprovalId === task.id ? (
                                            showApprovalCustomerDropdown ? (
                                              <div className="w-full">
                                                <Select
                                                  options={customerOptions}
                                                  onChange={(opt) => {
                                                    if (opt) {
                                                      setEditingApprovalRef(`${opt.short_code}: `);
                                                      setEditingApprovalCustomerId(opt.value);
                                                      setShowApprovalCustomerDropdown(false);
                                                    }
                                                  }}
                                                  placeholder="Select customer..."
                                                  className="text-xs"
                                                  menuPortalTarget={document.body}
                                                  styles={{
                                                    control: (base) => ({ ...base, minHeight: '32px', fontSize: '11px', minWidth: '180px' }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                  }}
                                                />
                                              </div>
                                            ) : (

                                              <div className="flex items-center gap-1">
                                                <input
                                                  type="text"
                                                  value={editingApprovalRef}
                                                  onChange={(e) => setEditingApprovalRef(e.target.value)}
                                                  className="p-1 border rounded text-xs w-full"
                                                  autoFocus
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                                {!['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'].includes(editingType) && (
                                                  <button
                                                    onClick={() => {
                                                      setEditingApprovalRef('');
                                                      setShowApprovalCustomerDropdown(true);
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-red-600"
                                                    title="Change Customer"
                                                  >
                                                    <FiTrash2 className="w-3 h-3" />
                                                  </button>
                                                )}
                                              </div>
                                            )
                                          ) : (
                                            <div className="whitespace-pre-wrap break-words min-w-[150px]" title={task.task_ref}>
                                              {task.task_ref || '-'}
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-4 py-2 text-gray-600 max-w-md" onClick={e => e.stopPropagation()}>
                                          {editingApprovalId === task.id ? (
                                            <textarea
                                              value={editingWorkDone}
                                              onChange={e => setEditingWorkDone(e.target.value)}
                                              className="w-full p-1 border rounded text-xs"
                                              rows={2}
                                              onClick={e => e.stopPropagation()}
                                            />
                                          ) : (
                                            <div className="whitespace-pre-wrap break-words" title={task.work_done}>
                                              {task.work_done}
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-4 py-2 text-center" onClick={e => e.stopPropagation()}>
                                          {editingApprovalId === task.id ? (
                                            <input
                                              type="text"
                                              value={editingCompletionPercentage === 0 ? '0' : (editingCompletionPercentage || '')}
                                              onChange={e => {
                                                const val = e.target.value;
                                                if (val === '') {
                                                  setEditingCompletionPercentage('');
                                                  return;
                                                }
                                                if (!/^\d*$/.test(val)) return;
                                                const num = parseInt(val, 10);
                                                if (!isNaN(num) && num >= 0 && num <= 100) {
                                                  setEditingCompletionPercentage(num);
                                                }
                                              }}
                                              className="w-16 p-1 border rounded text-center text-xs"
                                              onClick={e => e.stopPropagation()}
                                              maxLength={3}
                                            />
                                          ) : (
                                            <span>{task.completion_percentage || 0}%</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2 text-center font-bold text-gray-800" onClick={e => e.stopPropagation()}>
                                          {editingApprovalId === task.id ? (
                                            <DurationPicker
                                              className="w-20"
                                              value={editingHours}
                                              onChange={setEditingHours}
                                            />
                                          ) : (
                                            <span>{decimalToHoursMinutes(parseFloat(task.hours || 0))}</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                                          <div className="flex justify-center gap-2 items-center">
                                            {editingApprovalId === task.id ? (
                                              <>
                                                <button
                                                  onClick={() => handleUpdateApprovalTask(task.id)}
                                                  className="p-1 px-2 bg-blue-100 text-blue-700 rounded text-xs whitespace-nowrap"
                                                >
                                                  Save
                                                </button>
                                                <button
                                                  onClick={() => setEditingApprovalId(null)}
                                                  className="p-1 px-2 bg-gray-100 text-gray-700 rounded text-xs whitespace-nowrap"
                                                >
                                                  Cancel
                                                </button>
                                              </>
                                            ) : (
                                              <>
                                                <button
                                                  onClick={() => handleApproveClick(task)}
                                                  className="w-8 h-8 flex items-center justify-center bg-green-100 text-green-700 rounded hover:bg-green-200 border border-green-300 transition-all"
                                                  title="Approve"
                                                >
                                                  <FiCheckCircle className="w-4 h-4" />
                                                </button>
                                                <button
                                                  onClick={() => startEditingApproval(task)}
                                                  className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-700 rounded hover:bg-blue-200 border border-blue-300 transition-all"
                                                  title="Edit Original"
                                                >
                                                  <FiEdit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                  onClick={() => handleReject(task)}
                                                  className="w-8 h-8 flex items-center justify-center bg-red-100 text-red-700 rounded hover:bg-red-200 border border-red-300 transition-all"
                                                  title="Reject"
                                                >
                                                  <FiXCircle className="w-4 h-4" />
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      </tr>

                                      {/* Row 2: Customer/Enhanced Data (Editable) */}
                                      {([1, 9, 11].includes(roleId) || isApprover) && expandedCustomerRows.has(task.id) && (
                                        <tr className="bg-[#EBF5FF] hover:bg-blue-100/40 transition-colors border-l-4 border-l-blue-400">
                                          <td className="px-4 py-2"></td>
                                          <td className="px-2 py-2"></td>
                                          <td className="px-4 py-2 text-xs text-gray-500">
                                            {task.date ? formatDateDisplay(task.date) : ''}
                                            <div className="text-[10px] text-blue-600 font-semibold mt-0.5">CUSTOMER DATA</div>
                                          </td>
                                          <td className="px-4 py-2"></td>
                                          <td className="px-4 py-2"></td>
                                          <td className="px-4 py-2">
                                            {customerEditingTaskId === task.id ? (
                                              <select
                                                value={cvRowData.task_type || task.cv_task_type || task.type}
                                                onChange={e => {
                                                  const val = e.target.value;
                                                  const internalTypes = ['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'];
                                                  // Function to clean ref (remove prefixes)
                                                  const cleanRef = (ref) => {
                                                    if (!ref) return '';
                                                    if (ref.startsWith('INT: ')) return ref.substring(5);
                                                    // Also check for customer prefixes e.g. "JUC: "
                                                    const match = ref.match(/^[A-Z0-9]+:\s/);
                                                    if (match) return ref.substring(match[0].length);
                                                    return ref;
                                                  };

                                                  const currentRef = cvRowData.task_ref || task.cv_task_ref || task.task_ref || '';
                                                  let newRef = currentRef;

                                                  if (internalTypes.includes(val)) {
                                                    // If switching TO internal, ensure INT prefix
                                                    // But only if we don't already have it? 
                                                    // Or simpler: remove any existing prefix and add INT:
                                                    const content = cleanRef(currentRef);
                                                    newRef = 'INT: ' + content;
                                                  } else {
                                                    // If switching TO external, clean INT prefix if present
                                                    if (currentRef.startsWith('INT: ')) {
                                                      newRef = currentRef.substring(5);
                                                    }
                                                  }

                                                  setCvRowData({ ...cvRowData, task_type: val, task_ref: newRef });
                                                }}
                                                className="p-1 border rounded text-xs w-full bg-white"
                                              >
                                                {taskTypeOptions.map(opt => (
                                                  <option key={opt.value} value={opt.label || opt.value}>{opt.label}</option>
                                                ))}
                                              </select>
                                            ) : (
                                              <span className="text-xs text-gray-600 font-medium">
                                                {task.cv_task_type || task.type}
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2">
                                            {customerEditingTaskId === task.id ? (
                                              // Logic: If ref is present or it's an internal type, show INPUT
                                              // If ref is empty AND it's not internal, show DROPDOWN (handled locally by checking empty)
                                              // Actually, we want to mirror the creation logic:
                                              // If (internal OR (ref not empty)), show Input with Clear Button.
                                              // If (external AND ref empty), show Dropdown.

                                              (['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'].includes(cvRowData.task_type || task.cv_task_type || task.type) || (cvRowData.task_ref && cvRowData.task_ref !== '')) ? (
                                                <div className="relative w-full">
                                                  <input
                                                    type="text"
                                                    value={cvRowData.task_ref ?? (task.cv_task_ref || task.task_ref || '')}
                                                    onChange={e => setCvRowData({ ...cvRowData, task_ref: e.target.value })}
                                                    className="p-1 border rounded text-xs w-full bg-white pr-6"
                                                  />
                                                  <button
                                                    onClick={() => setCvRowData({ ...cvRowData, task_ref: '' })}
                                                    className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 p-0.5 rounded-full hover:bg-red-50"
                                                    title="Clear Reference"
                                                  >
                                                    <FiTrash2 className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              ) : (
                                                <div className="w-full">
                                                  <Select
                                                    options={customerOptions}
                                                    onChange={(opt) => {
                                                      if (opt) {
                                                        setCvRowData({ ...cvRowData, customer_id: opt.value, task_ref: `${opt.short_code}: ` });
                                                      }
                                                    }}
                                                    placeholder="Select customer..."
                                                    className="text-xs"
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                      control: (base) => ({ ...base, minHeight: '32px', fontSize: '11px' }),
                                                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                    }}
                                                  />
                                                </div>
                                              )
                                            ) : (
                                              <span className="text-xs text-gray-600">
                                                {task.cv_task_ref || task.task_ref || '-'}
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 max-w-md">
                                            {customerEditingTaskId === task.id ? (
                                              <textarea
                                                value={cvRowData.work_done ?? (task.cv_work_done || task.work_done || '')}
                                                onChange={e => setCvRowData({ ...cvRowData, work_done: e.target.value })}
                                                className="w-full p-1 border rounded text-xs bg-white"
                                                rows={2}
                                                placeholder=""
                                              />
                                            ) : (
                                              <div className="text-xs text-gray-700 whitespace-pre-wrap italic">
                                                {task.cv_work_done || task.work_done}
                                              </div>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-center">
                                            {customerEditingTaskId === task.id ? (
                                              <input
                                                type="number"
                                                value={cvRowData.status_percent ?? task.cv_status_percent ?? task.completion_percentage ?? 0}
                                                onChange={e => setCvRowData({ ...cvRowData, status_percent: e.target.value })}
                                                className="w-16 p-1 border rounded text-center text-xs bg-white"
                                                min="0" max="100"
                                              />
                                            ) : (
                                              <span className="text-xs text-gray-600">{task.cv_status_percent ?? task.completion_percentage ?? 0}%</span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-center">
                                            {customerEditingTaskId === task.id ? (
                                              <DurationPicker
                                                className="w-20"
                                                value={cvRowData.hours ?? task.cv_hours ?? task.hours ?? 0}
                                                onChange={val => setCvRowData({ ...cvRowData, hours: val })}
                                              />
                                            ) : (
                                              <span className="text-xs text-gray-600 font-bold">{decimalToHoursMinutes(parseFloat(task.cv_hours ?? task.hours ?? 0))}</span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2">
                                            <div className="flex justify-center">
                                              {customerEditingTaskId === task.id ? (
                                                <div className="flex gap-1">
                                                  <button
                                                    onClick={() => handleUpdateCustomerView(task, cvRowData)}
                                                    className="px-2 py-1 bg-green-600 text-white rounded text-[10px] font-bold"
                                                  >
                                                    SAVE CV
                                                  </button>
                                                  <button
                                                    onClick={() => setCustomerEditingTaskId(null)}
                                                    className="px-2 py-1 bg-gray-400 text-white rounded text-[10px] font-bold"
                                                  >
                                                    BACK
                                                  </button>
                                                </div>
                                              ) : (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCustomerEditingTaskId(task.id);
                                                    // Convert decimal hours to formatted time for editing
                                                    const hoursDecimal = parseFloat(task.cv_hours ?? task.hours ?? 0);
                                                    setCvRowData({
                                                      task_type: task.cv_task_type || task.type,
                                                      task_ref: task.cv_task_ref || task.task_ref,
                                                      work_done: task.cv_work_done || task.work_done,
                                                      status_percent: task.cv_status_percent ?? task.completion_percentage,
                                                      hours: decimalToHoursMinutes(hoursDecimal)
                                                    });
                                                  }}
                                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                  title="Edit Customer View"
                                                >
                                                  <FiEdit2 className="w-4 h-4" />
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}

                      </div>
                    )}

                    {assignedTasks.length > 0 && (
                      <div className="mt-8 border-t pt-8">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                          <FiFileText className="w-5 h-5 text-blue-600" />
                          Tasks Assigned To Me ({assignedTasks.length})
                        </h2>
                        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-700">
                              <tr>
                                <th className="px-4 py-3 text-left font-bold uppercase tracking-wider text-[10px]">Date</th>
                                <th className="px-4 py-3 text-left font-bold uppercase tracking-wider text-[10px] w-24">By</th>
                                <th className="px-4 py-3 text-left font-bold uppercase tracking-wider text-[10px] w-20">Type</th>
                                <th className="px-4 py-3 text-left font-bold uppercase tracking-wider text-[10px] w-32">Ref</th>
                                <th className="px-4 py-3 text-left font-bold uppercase tracking-wider text-[10px] w-48">Instructions</th>
                                <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px] flex-1">Work Done</th>
                                <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px] w-20">Status %</th>
                                <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px] w-24">Hours</th>
                                <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px] w-20">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {assignedTasks.map(t => (
                                <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-3 text-gray-600 font-bold whitespace-nowrap text-[10px] align-middle">{formatDateDisplay(t.created_at)}</td>
                                  <td className="px-4 py-3 font-medium text-gray-800 text-[11px] truncate align-middle" title={t.assigned_by_name}>{t.assigned_by_name}</td>
                                  <td className="px-4 py-3 align-middle">
                                    <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[9px] font-bold border border-blue-100 uppercase truncate block text-center" title={t.task_type}>
                                      {String(t.task_type).substring(0, 8)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-gray-700 font-medium truncate text-[11px] align-middle" title={t.task_ref}>
                                    {t.task_ref}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 align-middle">
                                    <div className="text-[11px] font-medium text-gray-700 italic truncate" title={t.description}>{t.description}</div>
                                  </td>
                                  <td className="px-4 py-3 align-middle">
                                    <textarea
                                      className="w-full p-1 border border-gray-300 rounded text-[11px] focus:ring-1 focus:ring-blue-500 min-h-[34px] resize-none"
                                      placeholder="Work done..."
                                      value={t.work_done}
                                      onChange={e => {
                                        const updated = assignedTasks.map(x => x.id === t.id ? { ...x, work_done: e.target.value } : x);
                                        setAssignedTasks(updated);
                                      }}
                                      rows={1}
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-center align-middle">
                                    <input
                                      type="number"
                                      className="w-16 p-1 border border-gray-300 rounded text-[11px] text-center"
                                      value={t.completion_percentage}
                                      onChange={e => {
                                        const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                        const updated = assignedTasks.map(x => x.id === t.id ? { ...x, completion_percentage: val } : x);
                                        setAssignedTasks(updated);
                                      }}
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-center align-middle">
                                    <div className="w-20 mx-auto">
                                      <DurationPicker
                                        value={t.hours}
                                        onChange={val => {
                                          const updated = assignedTasks.map(x => x.id === t.id ? { ...x, hours: val } : x);
                                          setAssignedTasks(updated);
                                        }}
                                        className="bg-white text-[11px] p-0.5 w-full"
                                      />
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center align-middle">
                                    <button
                                      onClick={() => onAssignTaskLog(t)}
                                      disabled={isSubmitting}
                                      className="p-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors inline-block"
                                      title="Log & Done"
                                    >
                                      <FiCheckCircle className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )
              }

              {/** V2: My Tasks (Developer View) - Show ONLY when activeTab is 'my_tasks' **/}
              {
                activeTab === 'my_tasks' && (
                  <>
                    {devLoading ? (
                      <div className="text-center p-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading tasks...</p>
                      </div>
                    ) : (
                      <div>
                        <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="w-full sm:w-auto flex items-center gap-4">
                            <h2 className="text-lg font-semibold text-gray-800">My Tasks</h2>
                            <div className="flex items-center gap-2">

                              <div className="flex items-center border border-gray-300 rounded-md bg-white overflow-hidden shadow-sm">
                                <select
                                  value={devItemsPerPage}
                                  onChange={(e) => {
                                    setDevItemsPerPage(Number(e.target.value));
                                    setDevCurrentPage(1);
                                  }}
                                  className="text-sm p-1.5 focus:ring-blue-500 focus:border-blue-500 bg-transparent border-none outline-none font-medium text-gray-700 pl-3 pr-6"
                                >
                                  <option value={5}>5</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                  <option value={100}>100</option>
                                  <option value={filteredTasks.length || 999999}>All</option>
                                </select>
                                <div className="px-2 text-gray-500 bg-gray-50 border-l border-gray-300 text-xs py-2 pointer-events-none">
                                  Records
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="relative w-full sm:w-1/3 md:w-1/4">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              placeholder="Search tasks..."
                              className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              value={searchTerm}
                              onChange={e => setSearchTerm(e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Editable Rows Table */}
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                          <table className="w-full min-w-[600px]">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-12">No</th>

                                <th onClick={() => toggleDevSort('task_type')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer min-w-[120px]">
                                  <div className="flex items-center gap-1">
                                    Task Type
                                    {devSortField === 'task_type' ? (
                                      devSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                    ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                  </div>
                                </th>
                                <th onClick={() => toggleDevSort('task_ref')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer w-64">
                                  <div className="flex items-center gap-1">
                                    Task Ref
                                    {devSortField === 'task_ref' ? (
                                      devSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                    ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                  </div>
                                </th>
                                <th onClick={() => toggleDevSort('work_done')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer min-w-[400px]">
                                  <div className="flex items-center gap-1">
                                    Work Done
                                    {devSortField === 'work_done' ? (
                                      devSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                    ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                  </div>
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-24">
                                  Status %
                                </th>
                                <th onClick={() => toggleDevSort('hours')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer w-24">
                                  <div className="flex items-center gap-1">
                                    Hours
                                    {devSortField === 'hours' ? (
                                      devSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                    ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                  </div>
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-28">
                                  <div className="flex items-center">
                                    Action
                                    {(isEditableDate(myTasksDate) && !isExternal) && (
                                      <button
                                        onClick={addEmptyRow}
                                        title="Add row"
                                        className="ml-2 inline-flex items-center justify-center w-6 h-6 rounded bg-red-600 text-white text-sm hover:bg-red-700"
                                      >
                                        <FiPlus className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {/* Add form row - shown when + button is clicked */}
                              {showForm && (
                                <tr className="bg-blue-50">
                                  <td className="px-4 py-3 text-sm text-gray-600">*</td>

                                  <td className="px-4 py-3">
                                    <select
                                      ref={taskTypeRef}
                                      className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${formErrors.task_type ? 'border-red-500' : 'border-gray-300'}`}
                                      value={newTask.task_type}
                                      onChange={e => onNewTaskChange('task_type', e.target.value)}
                                      autoFocus
                                    >
                                      <option value="">Select</option>
                                      <option value="Ticket">Ticket</option>
                                      <option value="Design">Design</option>
                                      <option value="Development">Development</option>
                                      <option value="Testing">Testing</option>
                                      <option value="Unit Testing">Unit Testing</option>
                                      <option value="Security Scan">Security Scan</option>
                                      <option value="Implementation">Implementation</option>
                                      <option value="Training">Training</option>
                                      <option value="HR">HR</option>
                                      <option value="Others">Others</option>
                                      <option value="Admin">Admin</option>
                                      <option value="System Testing">System Testing</option>
                                      <option value="Bug Fixes in Staging">Bug Fixes in Staging</option>
                                      <option value="Requirement Discussion">Requirement Discussion</option>
                                      <option value="Production Testing">Production Testing</option>
                                      <option value="Customer Testing">Customer Testing</option>
                                      <option value="Post Implementation">Post Implementation</option>
                                      <option value="Infrastructure Administration">Infrastructure Administration</option>
                                      <option value="DEMO PREP">DEMO PREP</option>
                                    </select>
                                    {formErrors.task_type && <p className="text-red-500 text-xs mt-1">{formErrors.task_type}</p>}
                                  </td>
                                  <td className="px-4 py-3">
                                    {newTask.task_type === 'Ticket' ? (
                                      <Select
                                        menuPortalTarget={document.body}
                                        styles={{
                                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                                          control: base => ({ ...base, minHeight: '38px', fontSize: '14px' })
                                        }}
                                        options={availableTicketOptions.map(t => ({
                                          value: t.id,
                                          label: `${t.short_code || 'N/A'}:${t.id} - ${t.title}`,
                                          original: t
                                        }))}
                                        onChange={(opt) => onNewTaskChange('ticket_id', opt ? Number(opt.value) : null)}
                                        placeholder="Search Ticket..."
                                        className="w-full text-sm"
                                        isClearable
                                        filterOption={(candidate, input) => {
                                          if (input) {
                                            const label = candidate.label.toLowerCase();
                                            const val = String(candidate.value).toLowerCase();
                                            const search = input.toLowerCase();
                                            return label.includes(search) || val.includes(search);
                                          }
                                          return true;
                                        }}
                                      />
                                    ) : (
                                      showCustomerDropdown ? (
                                        <div className="w-64">
                                          <Select
                                            options={customerOptions}
                                            value={null}
                                            onChange={handleCustomerSelect}
                                            placeholder="Select customer..."
                                            isClearable
                                            className="text-sm"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                              control: (provided) => ({
                                                ...provided,
                                                borderColor: '#d1d5db',
                                                borderRadius: '0.6rem',
                                                minHeight: '38px',
                                                '&:hover': {
                                                  borderColor: '#d1d5db',
                                                },
                                              }),
                                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                          />
                                        </div>
                                      ) : (
                                        <>
                                          <div className="relative w-64">
                                            <textarea
                                              rows={1}
                                              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none overflow-hidden ${formErrors.task_ref ? 'border-red-500' : 'border-gray-300'} ${!['HR', 'Others', 'Admin'].includes(newTask.task_type) ? 'pr-8' : ''}`}
                                              value={newTask.task_ref}
                                              onChange={e => onTaskRefInputChange(e.target.value)}
                                              placeholder="Enter title"
                                              style={{ minHeight: '38px', whiteSpace: 'pre-wrap' }}
                                            />
                                            <button
                                              onClick={() => {
                                                if (['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'].includes(newTask.task_type)) {
                                                  // For internal tasks, just clear the text but keep the prefix (if any, though usually manual for internal)
                                                  // Actually for internal, we don't usually have a prefix from dropdown.
                                                  // But if we want to clear the 'INT: ' prefix if existing?
                                                  setNewTask(prev => ({ ...prev, task_ref: '' }));
                                                } else {
                                                  // For external, go back to dropdown
                                                  setNewTaskCustomer(null);
                                                  setShowCustomerDropdown(true);
                                                  setTaskRefPrefix('');
                                                  setNewTask(prev => ({ ...prev, task_ref: '' }));
                                                }
                                              }}
                                              className="absolute right-2 top-3 text-gray-400 hover:text-red-600"
                                              title="Clear / Change Customer"
                                            >
                                              <FiTrash2 className="w-4 h-4" />
                                            </button>

                                            {['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'].includes(newTask.task_type) && (
                                              <button
                                                onClick={() => setNewTask(prev => ({ ...prev, task_ref: '' }))}
                                                className="absolute right-2 top-3 text-gray-400 hover:text-red-600"
                                                title="Clear Text"
                                              >
                                                <FiTrash2 className="w-4 h-4" />
                                              </button>
                                            )}
                                          </div>
                                          {formErrors.task_ref && <p className="text-red-500 text-xs mt-1">{formErrors.task_ref}</p>}
                                        </>
                                      )
                                    )
                                    }
                                  </td>
                                  <td className="px-4 py-3 max-w-[400px]">
                                    <textarea
                                      ref={workDoneRef}
                                      className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${formErrors.work_done ? 'border-red-500' : 'border-gray-300'}`}
                                      value={newTask.work_done || ''}
                                      onChange={e => onNewTaskChange('work_done', e.target.value)}
                                      onKeyDown={e => {
                                        // Auto-focus to status % field on Tab key
                                        if (e.key === 'Tab' && !e.shiftKey) {
                                          e.preventDefault();
                                          statusRef.current?.focus();
                                        }
                                      }}
                                      placeholder="Describe work done"
                                      rows={2}
                                    />
                                    {formErrors.work_done && <p className="text-red-500 text-xs mt-1">{formErrors.work_done}</p>}
                                  </td>
                                  <td className="px-4 py-3">
                                    <input
                                      ref={statusRef}
                                      type="text"
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                      value={newTask.completion_percentage === 0 ? '0' : (newTask.completion_percentage || '')}
                                      onChange={e => {
                                        const val = e.target.value;
                                        // Allow empty string for typing
                                        if (val === '') {
                                          onNewTaskChange('completion_percentage', '');
                                          return;
                                        }
                                        // Allow only numbers
                                        if (!/^\d*$/.test(val)) return;

                                        const num = parseInt(val, 10);
                                        if (!isNaN(num) && num >= 0 && num <= 100) {
                                          onNewTaskChange('completion_percentage', num);
                                        }
                                      }}
                                      onKeyDown={e => {
                                        // Auto-focus to hours field on Tab key
                                        if (e.key === 'Tab' && !e.shiftKey) {
                                          e.preventDefault();
                                          hoursRef.current?.focus();
                                        }
                                      }}
                                      placeholder="0-100"
                                      maxLength={3}
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <DurationPicker
                                      ref={hoursRef}
                                      value={newTask.hours}
                                      onChange={val => onNewTaskChange('hours', val)}
                                      className={`${formErrors.hours ? 'border border-red-500 rounded' : ''}`}
                                    />
                                    {formErrors.hours && <p className="text-red-500 text-xs mt-1">{formErrors.hours}</p>}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => {
                                          // Validate and parse hours before calling onAddRow
                                          const hoursDecimal = parseTimeInput(newTask.hours);
                                          if (!hoursDecimal && newTask.hours) {
                                            toast.error('Invalid time format. Use: 2h 30m, 2:30, 150m, or 2.5');
                                            return;
                                          }

                                          const currentTotal = tasks.reduce((sum, t) => {
                                            const tHours = parseTimeInput(String(t.hours)) || parseFloat(t.hours) || 0;
                                            return sum + tHours;
                                          }, 0);
                                          const newHours = hoursDecimal || 0;

                                          if (currentTotal + newHours > 24) {
                                            toast.error(`Total hours cannot exceed 24 per day. Current: ${decimalToHoursMinutes(currentTotal)}, New Total: ${decimalToHoursMinutes(currentTotal + newHours)}`);
                                            return;
                                          }
                                          onAddRow();
                                        }}
                                        disabled={isSubmitting}
                                        className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-1"
                                      >
                                        <FiCheckCircle className="w-4 h-4" />
                                        {isSubmitting ? 'Saving...' : 'Save'}
                                      </button>
                                      <button
                                        onClick={() => {
                                          setShowForm(false);
                                          setFormErrors({ task_type: '', work_done: '', hours: '', task_ref: '' });
                                        }}
                                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}

                              {devPaginatedTasks.map((row, idx) => (
                                <tr key={row._uid || row.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-600">{(devCurrentPage - 1) * devItemsPerPage + idx + 1}</td>

                                  <td className="px-4 py-3">
                                    {editingTaskId === row.id ? (
                                      <select
                                        value={row.task_type === 'Ticket' ? 'Ticket' : (row.task_type_code || row.task_type)}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          const isTicket = val === 'Ticket';
                                          const label = isTicket ? 'Ticket' : (taskTypeOptions.find(o => o.value === val)?.label || val);
                                          // Use Values ('OT', 'HR') instead of Labels ('Others', 'Admin')
                                          const isInternal = ['HR', 'OT', 'AD', 'TR', 'IA'].includes(val);

                                          const updatedTasks = tasks.map(t =>
                                            t.id === row.id ? {
                                              ...t,
                                              task_type: label,
                                              task_type_code: isTicket ? null : val,
                                              task_type_name: label,
                                              task_ref: isInternal ? 'INT: ' : (t.task_ref || ''),
                                              customer_id: isInternal ? null : t.customer_id
                                            } : t
                                          );
                                          setTasks(updatedTasks);
                                        }}
                                        className="w-full p-1 border rounded text-xs"
                                      >
                                        <option value="">Select Type</option>
                                        {taskTypeOptions.map((option, idx) => (
                                          <option key={idx} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap min-w-[110px] ${row.task_type === 'Ticket' ? 'bg-purple-100 text-purple-800' :
                                        row.task_type === 'Design' ? 'bg-blue-100 text-blue-800' :
                                          row.task_type === 'Development' ? 'bg-green-100 text-green-800' :
                                            row.task_type === 'Testing' ? 'bg-yellow-100 text-yellow-800' :
                                              row.task_type === 'Security Scan' ? 'bg-red-100 text-red-800' :
                                                row.task_type === 'Implementation' ? 'bg-indigo-100 text-indigo-800' :
                                                  row.task_type === 'Training' ? 'bg-pink-100 text-pink-800' :
                                                    row.task_type === 'DEMO PREP' ? 'bg-orange-100 text-orange-800' :
                                                      'bg-gray-100 text-gray-800'
                                        }`}>
                                        {row.task_type}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700 max-w-[300px] whitespace-pre-wrap break-words">
                                    {editingTaskId === row.id ? (
                                      row.task_type === 'Ticket' ? (
                                        <Select
                                          menuPortalTarget={document.body}
                                          styles={{
                                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                                            control: base => ({ ...base, minHeight: '30px', fontSize: '12px' })
                                          }}
                                          options={ticketOptions.map(t => ({
                                            value: `${t.short_code}:${t.id}`,
                                            label: `${t.short_code}:${t.id} - ${t.title}`,
                                            original: t
                                          }))}
                                          value={(() => {
                                            if (!row.ticket_id) return null;
                                            const found = ticketOptions.find(t => String(t.id) === String(row.ticket_id));
                                            if (found) return {
                                              value: `${found.short_code}:${found.id}`,
                                              label: `${found.short_code}:${found.id} - ${found.title}`,
                                              original: found
                                            };
                                            // Fallback
                                            return { label: row.task_ref || 'Unknown Ticket', value: 'unknown' };
                                          })()}
                                          onChange={(opt) => {
                                            if (opt && opt.original) {
                                              const matched = opt.original;
                                              const newRef = `${matched.short_code}:${matched.id} - ${matched.title}`;
                                              const updatedTasks = tasks.map(t =>
                                                t.id === row.id ? { ...t, ticket_id: matched.id, task_ref: newRef } : t
                                              );
                                              setTasks(updatedTasks);
                                            }
                                          }}
                                          placeholder="Search Ticket..."
                                          className="text-xs min-w-[200px]"
                                          filterOption={(candidate, input) => {
                                            if (input) {
                                              const label = candidate.label.toLowerCase();
                                              // Extract ID from value string "short_code:id"
                                              const valParts = String(candidate.value).split(':');
                                              const id = valParts[1] || valParts[0]; // fallback
                                              const search = input.toLowerCase();
                                              // Ensure we check against Ticket ID, Task ID (less relevant here, but requested), and Title
                                              return label.includes(search) || id.includes(search);
                                            }
                                            return true;
                                          }}
                                        />
                                      ) : (
                                        // Hybrid: Show Select if empty, Input if has content
                                        (row.task_ref && row.task_ref.trim() !== '') ? (
                                          <input
                                            type="text"
                                            className="w-full p-2 border rounded-lg text-xs"
                                            value={row.task_ref}
                                            onChange={e => {
                                              const val = e.target.value;
                                              setTasks(tasks.map(t => t.id === row.id ? { ...t, task_ref: val } : t));
                                            }}
                                            autoFocus
                                            placeholder="Type task reference (clear to change customer)..."
                                          />
                                        ) : (
                                          <Select
                                            menuPortalTarget={document.body}
                                            styles={{
                                              menuPortal: base => ({ ...base, zIndex: 9999 }),
                                              control: base => ({ ...base, minHeight: '30px', fontSize: '12px' })
                                            }}
                                            options={customerOptions}
                                            value={customerOptions.find(c => String(c.value) === String(row.customer_id))}
                                            onChange={(opt) => {
                                              if (opt) {
                                                const updatedTasks = tasks.map(t =>
                                                  t.id === row.id ? {
                                                    ...t,
                                                    customer_id: opt.value,
                                                    task_ref: `${opt.short_code}: `,
                                                    task_type: t.task_type === 'Ticket' ? 'Task' : t.task_type,
                                                    ticket_id: null
                                                  } : t
                                                );
                                                setTasks(updatedTasks);
                                              }
                                            }}
                                            placeholder="Select Customer..."
                                            className="text-xs min-w-[150px]"
                                          />
                                        )
                                      )
                                    ) : (
                                      row.task_type === 'Ticket' ? (
                                        <div className="flex flex-col">
                                          <div
                                            className="text-blue-600 hover:text-blue-800 cursor-pointer text-sm"
                                            onClick={() => handleTicketRowClick(row.ticket_id)}
                                          >
                                            {(() => {
                                              const taskRef = row.task_ref || '';
                                              const colonIndex = taskRef.indexOf(':');
                                              if (colonIndex > 0) {
                                                const customerPart = taskRef.substring(0, colonIndex + 1);
                                                const remainingPart = taskRef.substring(colonIndex + 1);
                                                return (
                                                  <>
                                                    <span className="font-bold text-gray-900">{customerPart}</span>
                                                    <span className="text-gray-600">{remainingPart}</span>
                                                  </>
                                                );
                                              }
                                              return taskRef;
                                            })()}
                                          </div>
                                          {row.assigned_by_name && (
                                            <span className="text-[10px] text-purple-600 font-bold mt-0.5">
                                              Assigned By: {row.assigned_by_name}
                                            </span>
                                          )}

                                        </div>
                                      ) : (
                                        <div className='flex flex-col'>
                                          <span className="text-gray-700 text-sm">
                                            {(() => {
                                              const taskRef = row.task_ref || '-';
                                              const colonIndex = taskRef.indexOf(':');
                                              if (colonIndex > 0) {
                                                const customerPart = taskRef.substring(0, colonIndex + 1);
                                                const remainingPart = taskRef.substring(colonIndex + 1);
                                                return (
                                                  <>
                                                    <span className="font-bold text-gray-900">{customerPart}</span>
                                                    <span className="text-gray-600">{remainingPart}</span>
                                                  </>
                                                );
                                              }
                                              return taskRef;
                                            })()}
                                          </span>
                                          {row.assigned_by_name && (
                                            <span className="text-[10px] text-purple-600 font-bold mt-0.5">
                                              Assigned By: {row.assigned_by_name}
                                            </span>
                                          )}

                                        </div>
                                      )
                                    )}
                                  </td>
                                  <td className="px-4 py-3 max-w-[400px]">
                                    {editingTaskId === row.id ? (
                                      <textarea
                                        value={row.work_done || ''}
                                        onChange={(e) => {
                                          const updatedTasks = tasks.map(t =>
                                            t.id === row.id ? { ...t, work_done: e.target.value } : t
                                          );
                                          setTasks(updatedTasks);
                                        }}
                                        className="w-full p-1 border rounded text-sm"
                                        rows={2}
                                      />
                                    ) : (
                                      <div className="text-sm text-gray-700 whitespace-pre-wrap break-words overflow-wrap-anywhere">{row.work_done || '-'}</div>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    {editingTaskId === row.id ? (
                                      <input
                                        className="w-20 p-1 border rounded text-sm"
                                        maxLength={3}
                                        value={row.completion_percentage === 0 ? '0' : (row.completion_percentage || '')}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (val === '') {
                                            const updatedTasks = tasks.map(t =>
                                              t.id === row.id ? { ...t, completion_percentage: '' } : t
                                            );
                                            setTasks(updatedTasks);
                                            return;
                                          }
                                          if (!/^\d*$/.test(val)) return;

                                          const num = parseInt(val, 10);
                                          if (!isNaN(num) && num >= 0 && num <= 100) {
                                            const updatedTasks = tasks.map(t =>
                                              t.id === row.id ? { ...t, completion_percentage: num } : t
                                            );
                                            setTasks(updatedTasks);
                                          }
                                        }}
                                        onClick={e => e.stopPropagation()}
                                        onKeyDown={e => e.stopPropagation()}

                                      />
                                    ) : (
                                      <span className="text-sm text-gray-700">{row.completion_percentage || 0}%</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    {editingTaskId === row.id ? (
                                      <DurationPicker
                                        value={row.hours}
                                        onChange={(val) => {
                                          const updatedTasks = tasks.map(t =>
                                            t.id === row.id ? { ...t, hours: val } : t
                                          );
                                          setTasks(updatedTasks);
                                        }}
                                      />
                                    ) : (
                                      <span className="text-sm font-bold text-red-600">{decimalToHoursMinutes(parseFloat(row.hours || 0))}</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex gap-2">
                                      {editingTaskId === row.id ? (
                                        <>
                                          <button
                                            onClick={async () => {
                                              // Validate and parse hours
                                              const hoursDecimal = parseTimeInput(row.hours);
                                              if (!hoursDecimal) {
                                                toast.error('Invalid time format. Use: 2h 30m, 2:30, 150m, or 2.5');
                                                return;
                                              }

                                              const currentTotal = tasks.reduce((sum, t) => {
                                                if (t.id === row.id) return sum;
                                                const tHours = parseTimeInput(String(t.hours)) || parseFloat(t.hours) || 0;
                                                return sum + tHours;
                                              }, 0);

                                              if (currentTotal + hoursDecimal > 24) {
                                                toast.error(`Total hours cannot exceed 24 per day. Current: ${decimalToHoursMinutes(currentTotal)}, New Total: ${decimalToHoursMinutes(currentTotal + hoursDecimal)}`);
                                                return;
                                              }

                                              try {
                                                await api.post('/timesheet/edit/' + row.id, {
                                                  task_ref: row.task_ref,
                                                  work_done: row.work_done,
                                                  hours: row.hours,
                                                  completion_percentage: row.completion_percentage || 0,
                                                  task_type: (row.task_type === 'Ticket' ? 'Ticket' : (row.task_type_code || row.task_type)),
                                                  ticket_id: row.ticket_id,
                                                  customerId: row.customer_id
                                                });
                                                toast.success('Task updated successfully');
                                                setEditingTaskId(null);
                                                loadMyRows(formatDate(myTasksDate));
                                              } catch (e) {
                                                toast.error('Failed to update task');
                                              }
                                            }}
                                            className="p-1.5 text-green-600 hover:text-green-800"
                                            title="Save"
                                          >
                                            <FiCheckCircle className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditingTaskId(null);
                                              loadMyRows(formatDate(myTasksDate));
                                            }}
                                            className="p-1.5 text-gray-600 hover:text-gray-800 text-xl font-bold"
                                            title="Cancel"
                                          >
                                            ×
                                          </button>
                                        </>
                                      ) : (
                                        (() => {
                                          // Status IDs based on backend constants:
                                          // 5: Submitted, 10: Waiting Start, 14: Final Approved, 15: Final Approved Submitted
                                          const statusId = String(row.status_id);
                                          const isReportSent = statusId === '5' || statusId === '15';
                                          const isRejected = statusId === '99';
                                          const isWaitingHigh = ['11', '12', '13'].includes(statusId);
                                          const isFinalApproved = statusId === '14' || statusId === '15';

                                          // Lock Logic:
                                          // Lock Logic:
                                          // - UI Flag: Mail Sent for THIS specific task (sentTaskIds)
                                          // - Submitted (5) or Final Approved Submitted (15) [Legacy/Safety]
                                          // - Rejected (99)
                                          // - Final Approved (14)
                                          // - Workflow (11-13)

                                          const isMailSent = sentTaskIds.has(row.id); // Check UI flag by Task ID
                                          const isLockedByDate = !isEditableDate(myTasksDate);
                                          const isLocked = isMailSent || isReportSent || isRejected || isFinalApproved || isWaitingHigh || isLockedByDate || isExternal;

                                          let lockReason = "Approved";
                                          if (isExternal) lockReason = "Read Only (External Network)";
                                          else if (isLockedByDate) lockReason = "Locked (Past Date)";

                                          if (isLocked) {
                                            return (
                                              <div className="p-1.5" title={lockReason}>
                                                {isExternal || isLockedByDate ? <FiLock className="w-5 h-5 text-gray-400" /> : <FiCheckCircle className="w-5 h-5 text-green-500" />}
                                              </div>
                                            );
                                          }

                                          // Allow if it's the user's task OR if the user is an admin/task admin/approver
                                          // isSuperAdmin = (roleId === 1 || roleId === 11)
                                          // Added isApprover check to fix missing actions for approvers
                                          const canEdit = isSuperAdmin || isApprover || (row.assigned_user === undefined) || (String(row.assigned_user) === String(userId));

                                          // Wait, 'row.assigned_user' might be missing from api response of 'getDailyTasks'.
                                          // Usually 'getDailyTasks' returns tasks for the logged in user, so row.assigned_user is implied.
                                          // But if it's 'All Tasks' viewed by admin, it uses a different table?
                                          // This table is "My Tasks / Developer View".
                                          // If Admin views "My Tasks", I see my own.
                                          // If Admin views "All Tasks", I see the Admin Table (lines 2660+).
                                          // Use simple logic: if not locked, show icons.
                                          // But user says "if i add some colleges... other college cannot show the icons".
                                          // This implies they are seeing OTHER people's tasks here?
                                          // If `getDailyTasks` is 'Timesheet::getDailyTasks', it returns current user's tasks.
                                          // Unless... the user is using "All Tasks" which renders differently?
                                          // Let's look at Step 40 output lines 1311:

                                          // Wait, usually Admins can edit anything.
                                          // Let's just output standard buttons without extra ownership checks here, because `isLocked` handles the state.
                                          // If the user can see it in "My Tasks", they should be able to edit it.
                                          return (
                                            row.id && (
                                              <>
                                                <button
                                                  onClick={() => {
                                                    // Convert hours to formatted time for editing
                                                    const updatedTasks = tasks.map(t => {
                                                      if (t.id === row.id) {
                                                        const hoursDecimal = parseFloat(t.hours) || 0;
                                                        return { ...t, hours: decimalToHoursMinutes(hoursDecimal) };
                                                      }
                                                      return t;
                                                    });
                                                    setTasks(updatedTasks);
                                                    setEditingTaskId(row.id);
                                                  }}
                                                  className="p-1.5 text-blue-600 hover:text-blue-800"
                                                  title="Edit"
                                                >
                                                  <FiEdit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    setTaskToDelete(row);
                                                    setShowDeleteConfirm(true);
                                                  }}
                                                  className="p-1.5 text-red-600 hover:text-red-800"
                                                  title="Delete"
                                                >
                                                  <FiTrash2 className="w-4 h-4" />
                                                </button>
                                              </>
                                            )
                                          );
                                        })()
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            {/* Total Hours Footer for Developer */}
                            <tfoot className="bg-gray-100 font-semibold text-gray-900">
                              <tr>
                                <td colSpan={5} className="px-4 py-3 text-right text-sm">Total Hours:</td>
                                <td className="px-4 py-3 text-left font-bold text-red-600 text-sm">{decimalToHoursMinutes(totalTaskHours)}</td>
                                <td className="px-4 py-3"></td>
                              </tr>
                              <tr>
                                <td colSpan={7} className="px-4 py-3 text-right">
                                  {tasks.some(t => {
                                    // Check DB flag for email sent
                                    const isEmailSent = String(t.is_email_sent) === '1';

                                    // 1=Saved, 4=Completed, 10=Waiting, 11-13=Waiting High, 14=Approved
                                    // Hide if 5 (Submitted), 15 (Approved Submitted), or 99 (Rejected)
                                    const validStatuses = ['1', '4', '10', '11', '12', '13', '14'];
                                    const isValidStatus = validStatuses.includes(String(t.status_id));

                                    // Show button if valid status AND email NOT sent AND date is editable (before 6 AM deadline) AND on allowed IP
                                    return !isEmailSent && isValidStatus && isEditableDate(myTasksDate) && !isExternal;
                                  }) && (
                                      <button
                                        onClick={async () => {
                                          // Fetch email recipients and suggestions
                                          try {
                                            const [recipientsRes, suggestionsRes] = await Promise.all([
                                              api.get('/timesheet/email-recipients'),
                                              api.get('/timesheet/user-emails')
                                            ]);

                                            const reportingEmailString = recipientsRes.data?.reporting_manager_email || '';
                                            const defaultCcString = recipientsRes.data?.default_cc || '';
                                            const suggestions = suggestionsRes.data?.emails || [];

                                            setEmailSuggestions(suggestions);

                                            // Set To field as array for multi-select
                                            const toEmails = reportingEmailString.split(',').map(e => e.trim()).filter(Boolean);
                                            setEmailTo(toEmails.map(email => ({ value: email, label: email })));

                                            // Set CC field as array for multi-select
                                            const ccEmails = defaultCcString.split(',').map(e => e.trim()).filter(Boolean);
                                            setEmailCc(ccEmails.map(email => ({ value: email, label: email })));
                                          } catch (e) {
                                            console.error('Failed to fetch email data', e);
                                          }

                                          // Generate HTML table for email body (without Employee column)
                                          const rows = tasks;
                                          const tableRows = rows.map((row, idx) =>
                                            `<tr>` +
                                            `<td style="border: 1px solid #000; padding: 8px; text-align: center;">${idx + 1}</td>` +
                                            `<td style="border: 1px solid #000; padding: 8px;">${row.task_type || ''}</td>` +
                                            `<td style="border: 1px solid #000; padding: 8px; max-width: 250px; word-wrap: break-word; word-break: break-all;">${row.task_ref || ''}</td>` +
                                            `<td style="border: 1px solid #000; padding: 8px; max-width: 300px; word-wrap: break-word; word-break: break-all;">${row.work_done || ''}</td>` +
                                            `<td style="border: 1px solid #000; padding: 8px; text-align: center;">${decimalToHoursMinutes(parseFloat(row.hours || 0))}</td>` +
                                            `</tr>`
                                          ).join('');

                                          const htmlTable = `
                                    <div style="font-family: Arial, sans-serif;">
                                      <h3>Daily Tasks Status!</h3>
                                      <table style="width: 100%; border-collapse: collapse; border: 1px solid #000;">
                                        <thead>
                                          <tr style="background-color: #f0f0f0;">
                                            <th style="border: 1px solid #000; padding: 8px; text-align: center;">No</th>
                                            <th style="border: 1px solid #000; padding: 8px;">Type</th>
                                            <th style="border: 1px solid #000; padding: 8px;">Reference</th>
                                            <th style="border: 1px solid #000; padding: 8px;">Work Done</th>
                                            <th style="border: 1px solid #000; padding: 8px; text-align: center;">Hours</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          ${tableRows}
                                        </tbody>
                                        <tfoot>
                                          <tr style="background-color: #f0f0f0; font-weight: bold;">
                                            <td colspan="4" style="border: 1px solid #000; padding: 8px; text-align: right;">Total Hours:</td>
                                            <td style="border: 1px solid #000; padding: 8px; text-align: center;">${decimalToHoursMinutes(totalTaskHours)}</td>
                                          </tr>
                                        </tfoot>
                                      </table>
                                    </div>
                                  `;

                                          const employeeName = user ? [user.first_name, user.last_name].filter(Boolean).join(' ') : 'Employee';
                                          const reportDate = formatDate(myTasksDate);
                                          setEmailSubject(`Daily Status Report – ${formatDateDisplay(reportDate)} | ${employeeName}`);
                                          setEmailBody(htmlTable);
                                          setShowEmailModal(true);
                                        }}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                                      >
                                        Submit
                                      </button>
                                    )}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        <div className="mt-4 flex items-center justify-between">
                          <div className="text-sm text-gray-600">
                            {filteredTasks.length > 0 ? (
                              <>Showing {((devCurrentPage - 1) * devItemsPerPage) + 1} to {Math.min(devCurrentPage * devItemsPerPage, filteredTasks.length)} of {filteredTasks.length} entries</>
                            ) : (
                              <>Showing 0 to 0 of 0 entries</>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setDevCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={devCurrentPage === 1}
                              className={`px-3 py-1 border rounded ${devCurrentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-red-600 text-red-600 hover:bg-red-50'}`}
                            >
                              Previous
                            </button>

                            {(() => {
                              const totalPages = Math.max(1, Math.ceil(filteredTasks.length / devItemsPerPage));
                              const maxVisiblePages = 5;
                              let startPage = Math.max(1, devCurrentPage - Math.floor(maxVisiblePages / 2));
                              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                              if (endPage - startPage < maxVisiblePages - 1) {
                                startPage = Math.max(1, endPage - maxVisiblePages + 1);
                              }
                              const pages = [];
                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <button
                                    key={i}
                                    onClick={() => setDevCurrentPage(i)}
                                    className={`px-3 py-1 border rounded ${i === devCurrentPage ? 'bg-red-600 text-white' : 'border-red-600 text-red-600 hover:bg-red-50'}`}
                                  >
                                    {i}
                                  </button>
                                );
                              }
                              if (pages.length === 0) {
                                pages.push(
                                  <button key={1} className="px-3 py-1 border rounded bg-gray-100 text-gray-600" disabled>
                                    1
                                  </button>
                                );
                              }
                              return pages;
                            })()}

                            <button
                              onClick={() => setDevCurrentPage(prev => Math.min(Math.ceil(filteredTasks.length / devItemsPerPage), prev + 1))}
                              disabled={devCurrentPage >= Math.ceil(filteredTasks.length / devItemsPerPage)}
                              className={`px-3 py-1 border rounded ${devCurrentPage >= Math.ceil(filteredTasks.length / devItemsPerPage) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-red-600 text-red-600 hover:bg-red-50'}`}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )
              }

              {/* V3: All Tasks (Admin/Report View) */}
              {
                ((isAdmin && !isApprover) || activeTab === 'all_tasks' || isCustomer) && (
                  (loadingReport ? (
                    <div className="text-center p-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-4 text-gray-600">Loading report...</p>
                    </div>
                  ) : reportData.length > 0 ? (
                    <>
                      <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <h2 className="text-lg font-semibold text-gray-800">{isCustomer ? 'Billable Hours Report' : 'Employee Report'}</h2>
                          {(!isCustomer && ([1, 9, 11].includes(roleId) || isApprover)) && (
                            <div className="flex gap-2">
                              {/* Calculation IIFE */}
                              {(() => {
                                const list = Array.from(selectedReportTaskIds);
                                const approvable = list.filter(id => {
                                  const t = filteredReportData.find(r => String(r.id) === String(id));
                                  // Approvable: Pending states (4 to 13). Not Draft(1) or Approved(14).
                                  return t && Number(t.status_id) >= 4 && Number(t.status_id) < 14;
                                });
                                const revertable = list.filter(id => {
                                  const t = filteredReportData.find(r => String(r.id) === String(id));
                                  // Revertable: Final Approved (14), Submitted (5), or Final Approved Submitted (15)
                                  return t && [5, 14, 15].includes(Number(t.status_id));
                                });

                                return (
                                  <>
                                    {approvable.length > 0 && (
                                      <button
                                        onClick={() => handleBulkApproveClick(approvable)}
                                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded flex items-center gap-1"
                                      >
                                        <FiCheckCircle className="w-3 h-3" /> Approve Selected ({approvable.length})
                                      </button>
                                    )}
                                    {revertable.length > 0 && (
                                      <button
                                        onClick={() => handleBulkRevertClick(revertable)}
                                        className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded flex items-center gap-1"
                                      >
                                        <FiRotateCcw className="w-3 h-3" /> Revert Selected ({revertable.length})
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                          <div className="flex items-center border border-gray-300 rounded-lg bg-white overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 shadow-sm shrink-0">
                            {/* <div className="relative flex items-center h-full"> */}
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                              style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                              className="appearance-none bg-transparent border-none py-2 pl-3 pr-8 text-sm font-medium text-gray-700 focus:ring-0 cursor-pointer outline-none h-full"
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                              <option value={filteredReportData.length || 999999}>All</option>
                            </select>
                            <FiChevronDown className="absolute right-2 text-gray-400 w-4 h-4 pointer-events-none top-1/2 transform -translate-y-1/2" />
                            {/* </div>
                              <div className="hidden sm:block w-px h-8 bg-gray-200"></div> */}
                            <div className="hidden sm:flex bg-gray-50 px-3 py-2 text-xs text-gray-500 font-medium select-none items-center justify-center">
                              Records
                            </div>
                          </div>
                          <div className="relative w-full sm:w-64">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              placeholder="Employee, ticket id, task id..."
                              className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              value={adminSearchTerm}
                              onChange={e => setAdminSearchTerm(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Color Legend/Agenda */}
                      {/* {(!isCustomer && ([1, 9, 11].includes(roleId) || isApprover)) && (
                      <div className="mb-3 flex items-center gap-4 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-[#FFE9E9] border-2 border-rose-400 rounded"></div>
                          <span className="text-xs font-medium text-gray-700">Employee Data</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-[#EBF5FF] border-2 border-blue-400 rounded"></div>
                          <span className="text-xs font-medium text-gray-700">Customer Data</span>
                        </div>
                      </div>
                    )} */}

                      <div ref={tableContainerRef} className="overflow-x-auto border border-gray-200 rounded-lg max-h-[600px]">
                        <table className="w-full min-w-[800px]">
                          <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
                            <tr>
                              {(!isCustomer && ([1, 9, 11].includes(roleId) || isApprover)) && (
                                <th className="px-4 py-2 text-center min-w-[50px]">
                                  <input
                                    type="checkbox"
                                    onChange={toggleAllReportTasks}
                                    checked={selectedReportTaskIds.size > 0 && filteredReportData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).every(r => selectedReportTaskIds.has(r.id))}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                  />
                                </th>
                              )}
                              {(!isCustomer && ([1, 9, 11].includes(roleId) || isApprover)) && (
                                <th className="px-2 py-2 text-center min-w-[40px]">
                                  <span className="text-xs font-medium text-gray-700"></span>
                                </th>
                              )}
                              <th onClick={() => toggleAdminSort('date')} className="px-4 py-3 text-center text-sm font-medium text-gray-700 cursor-pointer min-w-[100px]">
                                <div className="flex items-center gap-1 justify-center">
                                  Date
                                  {adminSortField === 'date' ? (
                                    adminSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAdminSort('id')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer min-w-[120px]">
                                <div className="flex items-center gap-1">
                                  Task ID
                                  {adminSortField === 'id' ? (
                                    adminSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAdminSort('employee_name')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer min-w-[150px]">
                                <div className="flex items-center gap-1">
                                  {isCustomer ? 'Role' : 'Resource'}
                                  {adminSortField === 'employee_name' ? (
                                    adminSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAdminSort('type')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer min-w-[100px]">
                                <div className="flex items-center gap-1">
                                  Task Type
                                  {adminSortField === 'type' ? (
                                    adminSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAdminSort('reference')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer min-w-[250px]">
                                <div className="flex items-center gap-1">
                                  Task Ref
                                  {adminSortField === 'reference' ? (
                                    adminSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAdminSort('work_done')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer min-w-[250px] max-w-[400px]">
                                <div className="flex items-center gap-1">
                                  {!isCustomer && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); scrollTable('left'); }}
                                      className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-700 transition"
                                      title="Scroll Left"
                                    >
                                      <FiChevronsLeft className="w-4 h-4" />
                                    </button>
                                  )}
                                  Work Done
                                  {adminSortField === 'work_done' ? (
                                    adminSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                  {!isCustomer && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); scrollTable('right'); }}
                                      className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-700 transition"
                                      title="Scroll Right"
                                    >
                                      <FiChevronsRight className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 min-w-[80px]">
                                Status %
                              </th>
                              <th onClick={() => toggleAdminSort('hours')} className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer min-w-[80px]">
                                <div className="flex items-center gap-1">
                                  Hours
                                  {adminSortField === 'hours' ? (
                                    adminSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              {!isCustomer && (
                                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 min-w-[140px]">
                                  Approval
                                </th>
                              )}
                              {/* Action Column - Only for Admin Roles */}
                              {(!isCustomer && ([1, 9, 11].includes(roleId))) && (
                                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 min-w-[100px]">
                                  Action
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {(() => {
                              let useGray = true;
                              let lastDate = null;
                              return filteredReportData
                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                .map((record, idx) => {
                                  // Toggle highlight on date change
                                  if (record.date !== lastDate) {
                                    if (idx > 0) useGray = !useGray;
                                    lastDate = record.date;
                                  }
                                  const rowBg = useGray ? 'bg-gray-50' : 'bg-white';

                                  // Check if this specific task is waiting for current user
                                  const isWaitingForMe = pendingApprovals.some(p => p.id === record.id);
                                  // Requirement: Hide customer data from employees. Only SuperAdmins (1, 11) should see dual rows.
                                  const canSeeDualRows = !isCustomer && ([1, 9, 11].includes(roleId) || isApprover);

                                  return (
                                    <React.Fragment key={idx}>
                                      {/* Row 1: Employee Data */}
                                      <tr
                                        className={`hover:bg-gray-100 transition-colors ${record.is_assigned_task ? 'bg-purple-50/50 hover:bg-purple-100/50' : rowBg} ${record.type === 'Ticket' ? 'cursor-pointer' : ''} ${canSeeDualRows ? 'border-l-4 border-l-white' : ''}`}
                                        onClick={() => record.type === 'Ticket' && handleTicketRowClick(record.ticket_id)}
                                      >
                                        {(!isCustomer && ([1, 9, 11].includes(roleId) || isApprover)) && (
                                          <td className="px-4 py-2 text-center" onClick={e => e.stopPropagation()}>
                                            <input
                                              type="checkbox"
                                              checked={selectedReportTaskIds.has(record.id)}
                                              onChange={() => toggleReportTaskSelection(record.id)}
                                              disabled={isInternalTask(record.type || record.task_type_name)}
                                              className={`w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 ${isInternalTask(record.type || record.task_type_name) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                            />
                                          </td>
                                        )}
                                        {(!isCustomer && ([1, 9, 11].includes(roleId) || isApprover)) && (
                                          <td className="px-2 py-2 text-center" onClick={e => e.stopPropagation()}>
                                            <button
                                              onClick={() => {
                                                const newExpanded = new Set(expandedCustomerRows);
                                                if (newExpanded.has(record.id)) {
                                                  newExpanded.delete(record.id);
                                                } else {
                                                  newExpanded.add(record.id);
                                                }
                                                setExpandedCustomerRows(newExpanded);
                                              }}
                                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                                              title={expandedCustomerRows.has(record.id) ? "Hide Customer Data" : "Show Customer Data"}
                                            >
                                              <FiChevronDown
                                                className={`w-4 h-4 text-gray-600 transition-transform ${expandedCustomerRows.has(record.id) ? 'rotate-180' : ''
                                                  }`}
                                              />
                                            </button>
                                          </td>
                                        )}
                                        <td className="px-4 py-3 text-center text-sm text-gray-900">
                                          {adminEditingTaskId === record.id ? (
                                            <input
                                              type="date"
                                              className="w-full p-1 border rounded text-xs"
                                              value={record.date || ''}
                                              onChange={(e) => {
                                                const updatedData = reportData.map(r =>
                                                  r.id === record.id ? { ...r, date: e.target.value } : r
                                                );
                                                setReportData(updatedData);
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                              max={formatDate(new Date())}
                                            />
                                          ) : (
                                            <div>
                                              {record.date ? new Date(record.date).toLocaleDateString('en-GB') : '-'}
                                              {canSeeDualRows && (
                                                <div className="text-[10px] text-rose-600 font-semibold mt-0.5">EMPLOYEE DATA</div>
                                              )}
                                            </div>
                                          )}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">#{record.id || record.ticket_id}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                          {isCustomer ? (
                                            record.designation_name || '-'
                                          ) : (
                                            record.employee_name
                                          )}
                                        </td>
                                        <td className="px-4 py-3">
                                          {adminEditingTaskId === record.id ? (
                                            <select
                                              value={record.type || record.task_type_name}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                const isInternal = ['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'].includes(val);
                                                const updatedData = reportData.map(r =>
                                                  r.id === record.id ? {
                                                    ...r,
                                                    type: val,
                                                    task_type_name: val,
                                                    task_type_code: val,
                                                    reference: isInternal ? 'INT: ' : (r.reference && String(r.reference).startsWith('INT') ? String(r.reference).replace(/^INT:\s*/i, '') : r.reference || ''),
                                                    customer_id: isInternal ? null : r.customer_id,
                                                    ticket_id: (val === 'Ticket') ? r.ticket_id : 0
                                                  } : r
                                                );
                                                setReportData(updatedData);
                                              }}
                                              className="w-full p-2 border rounded-lg text-xs"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {(taskTypeOptions || []).map(opt => (
                                                <option key={opt.value} value={opt.label}>{opt.label}</option>
                                              ))}
                                            </select>
                                          ) : (
                                            (() => {
                                              const raw = (record.type || record.task_type_name || 'Task');
                                              const label = formData.task_types?.[raw] || raw;
                                              const cls = (
                                                label === 'Ticket' ? 'bg-purple-100 text-purple-800' :
                                                  label === 'Design' ? 'bg-blue-100 text-blue-800' :
                                                    label === 'Development' ? 'bg-green-100 text-green-800' :
                                                      label === 'Testing' ? 'bg-yellow-100 text-yellow-800' :
                                                        label === 'Security Scan' ? 'bg-red-100 text-red-800' :
                                                          label === 'Implementation' ? 'bg-indigo-100 text-indigo-800' :
                                                            label === 'Training' ? 'bg-pink-100 text-pink-800' :
                                                              'bg-gray-100 text-gray-800'
                                              );
                                              return (
                                                <span className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap min-w-[110px] ${cls}`}>
                                                  {label}
                                                </span>
                                              );
                                            })()
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700">
                                          {adminEditingTaskId === record.id ? (
                                            (record.type === 'Ticket' || record.task_type_name === 'Ticket') ? (
                                              <Select
                                                menuPortalTarget={document.body}
                                                styles={{
                                                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                  control: base => ({ ...base, minHeight: '30px', fontSize: '12px' })
                                                }}
                                                options={ticketOptions.map(t => ({
                                                  value: `${t.short_code}:${t.id}`,
                                                  label: `${t.short_code}:${t.id} - ${t.title}`,
                                                  original: t
                                                }))}
                                                value={(() => {
                                                  if (!record.ticket_id) return null;
                                                  const found = ticketOptions.find(t => String(t.id) === String(record.ticket_id));
                                                  if (found) return {
                                                    value: `${found.short_code}:${found.id}`,
                                                    label: `${found.short_code}:${found.id} - ${found.title}`,
                                                    original: found
                                                  };
                                                  return { label: record.reference || 'Unknown Ticket', value: 'unknown' };
                                                })()}
                                                onChange={(opt) => {
                                                  if (opt && opt.original) {
                                                    const matched = opt.original;
                                                    const newRef = `${matched.short_code}:${matched.id} - ${matched.title}`;
                                                    const updatedData = reportData.map(r =>
                                                      r.id === record.id ? { ...r, ticket_id: matched.id, reference: newRef } : r
                                                    );
                                                    setReportData(updatedData);
                                                  }
                                                }}
                                                placeholder="Search Ticket..."
                                                className="text-xs min-w-[200px]"
                                                onClick={(e) => e.stopPropagation()}
                                                filterOption={(candidate, input) => {
                                                  if (input) {
                                                    const label = candidate.label.toLowerCase();
                                                    const valParts = String(candidate.value).split(':');
                                                    const id = valParts[1] || valParts[0];
                                                    const search = input.toLowerCase();
                                                    return label.includes(search) || id.includes(search);
                                                  }
                                                  return true;
                                                }}
                                              />
                                            ) : (
                                              ((record.reference && record.reference.trim() !== '' && record.reference !== '-') || isInternalTask(record.type || record.task_type_name)) ? (
                                                <input
                                                  type="text"
                                                  className="w-full p-2 border rounded-lg text-xs"
                                                  value={record.reference || ''}
                                                  onChange={(e) => {
                                                    const updatedData = reportData.map(r =>
                                                      r.id === record.id ? { ...r, reference: e.target.value } : r
                                                    );
                                                    setReportData(updatedData);
                                                  }}
                                                  onClick={(e) => e.stopPropagation()}
                                                  placeholder="Type task reference (clear to change customer)..."
                                                />
                                              ) : (
                                                customerOptions.find(c => String(c.value) === String(record.customer_id)) ? (
                                                  <Select
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                      menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                      control: base => ({ ...base, minHeight: '30px', fontSize: '12px', minWidth: '180px' })
                                                    }}
                                                    options={customerOptions}
                                                    value={customerOptions.find(c => String(c.value) === String(record.customer_id))}
                                                    onChange={(opt) => {
                                                      if (opt) {
                                                        const updatedData = reportData.map(r =>
                                                          r.id === record.id ? { ...r, customer_id: opt.value, reference: `${opt.short_code}: ` } : r
                                                        );
                                                        setReportData(updatedData);
                                                      }
                                                    }}
                                                    placeholder="Select Customer..."
                                                    className="text-xs min-w-[180px]"
                                                    onClick={(e) => e.stopPropagation()}
                                                  />
                                                ) : (
                                                  isInternalTask(record.type || record.task_type_name) ? <span className="text-gray-500">-</span> :
                                                    <Select
                                                      menuPortalTarget={document.body}
                                                      styles={{
                                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                        control: base => ({ ...base, minHeight: '30px', fontSize: '12px', minWidth: '180px' })
                                                      }}
                                                      options={customerOptions}
                                                      value={customerOptions.find(c => String(c.value) === String(record.customer_id))}
                                                      onChange={(opt) => {
                                                        if (opt) {
                                                          const updatedData = reportData.map(r =>
                                                            r.id === record.id ? { ...r, customer_id: opt.value, reference: `${opt.short_code}: ` } : r
                                                          );
                                                          setReportData(updatedData);
                                                        }
                                                      }}
                                                      placeholder="Select Customer..."
                                                      className="text-xs min-w-[180px]"
                                                      onClick={(e) => e.stopPropagation()}
                                                    />
                                                )
                                              )
                                            )
                                          ) : (
                                            <div>
                                              {(() => {
                                                const taskRef = record.reference || '';
                                                const colonIndex = taskRef.indexOf(':');

                                                if (!taskRef || taskRef.trim() === '' || taskRef === '-') {
                                                  return <span className="text-gray-500">-</span>;
                                                }

                                                if (colonIndex > 0) {
                                                  const customerPart = taskRef.substring(0, colonIndex + 1);
                                                  const remainingPart = taskRef.substring(colonIndex + 1);
                                                  return (
                                                    <div className='flex flex-col'>
                                                      <div>
                                                        <span className="font-bold text-gray-900">{customerPart}</span>
                                                        <span className="text-gray-700">{remainingPart}</span>
                                                      </div>
                                                      {record.assigned_by_name && (
                                                        <span className="text-[10px] text-purple-600 font-bold mt-0.5">
                                                          Assigned By: {record.assigned_by_name}
                                                        </span>
                                                      )}
                                                    </div>
                                                  );
                                                }
                                                return <div className='flex flex-col'>
                                                  <span>{taskRef}</span>
                                                  {record.assigned_by_name && (
                                                    <span className="text-[10px] text-purple-600 font-bold mt-0.5">
                                                      Assigned By: {record.assigned_by_name}
                                                    </span>
                                                  )}
                                                </div>;
                                              })()}
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 max-w-[400px]">
                                          {adminEditingTaskId === record.id ? (
                                            <textarea
                                              className="w-full p-1 border rounded text-sm"
                                              rows={2}
                                              value={record.work_done || ''}
                                              onChange={(e) => {
                                                const updatedData = reportData.map(r =>
                                                  r.id === record.id ? { ...r, work_done: e.target.value } : r
                                                );
                                                setReportData(updatedData);
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          ) : (
                                            <div className="text-sm text-gray-600 whitespace-pre-wrap break-words overflow-wrap-anywhere">{record.work_done || '-'}</div>
                                          )}
                                        </td>
                                        <td className="px-4 py-3">
                                          {adminEditingTaskId === record.id ? (
                                            <input
                                              type="text"
                                              className="w-20 p-1 border rounded text-sm"
                                              maxLength={3}
                                              value={record.completion_percentage === 0 ? '0' : (record.completion_percentage || '')}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '') {
                                                  const updatedData = reportData.map(r =>
                                                    r.id === record.id ? { ...r, completion_percentage: '' } : r
                                                  );
                                                  setReportData(updatedData);
                                                  return;
                                                }
                                                if (!/^\d*$/.test(val)) return;
                                                const num = parseInt(val, 10);
                                                if (!isNaN(num) && num >= 0 && num <= 100) {
                                                  const updatedData = reportData.map(r =>
                                                    r.id === record.id ? { ...r, completion_percentage: num } : r
                                                  );
                                                  setReportData(updatedData);
                                                }
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          ) : (
                                            <span className="text-sm text-gray-700">{record.completion_percentage || 0}%</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-center font-bold text-gray-800">
                                          {adminEditingTaskId === record.id ? (
                                            <DurationPicker
                                              value={record.hours}
                                              onChange={(val) => {
                                                const updatedData = reportData.map(r =>
                                                  r.id === record.id ? { ...r, hours: val } : r
                                                );
                                                setReportData(updatedData);
                                              }}
                                            />
                                          ) : (
                                            <span className="text-sm text-gray-700">{decimalToHoursMinutes(parseFloat(record.hours || 0))}</span>
                                          )}
                                        </td>
                                        {!isCustomer && (
                                          <td className="px-4 py-3 text-center text-xs text-gray-500 font-medium">
                                            {record.approval_status_text}
                                          </td>
                                        )}
                                        {/* Hide Action Cell for Employees and Approvers to match Header */}
                                        {(!isCustomer && ([1, 9, 11].includes(roleId))) && (
                                          <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                                            <div className="flex justify-center gap-2">
                                              {adminEditingTaskId === record.id ? (
                                                <>
                                                  <button
                                                    onClick={async () => {
                                                      try {
                                                        const updatedRecord = reportData.find(r => r.id === record.id);
                                                        if (!updatedRecord) {
                                                          toast.error('Task not found');
                                                          return;
                                                        }

                                                        // Parse hours to decimal
                                                        const hoursDecimal = parseTimeInput(updatedRecord.hours);
                                                        if (!hoursDecimal) {
                                                          toast.error('Invalid time format. Use: 2h 30m, 2:30, 150m, or 2.5');
                                                          return;
                                                        }

                                                        await api.post('/timesheet/edit/' + record.id, {
                                                          task_ref: updatedRecord.reference,
                                                          work_done: updatedRecord.work_done,
                                                          hours: String(hoursDecimal),
                                                          completion_percentage: updatedRecord.completion_percentage || 0,
                                                          task_type: updatedRecord.type, // Send the current label directly, backend maps it
                                                          ticket_id: updatedRecord.ticket_id,
                                                          customerId: updatedRecord.customer_id,
                                                          date: updatedRecord.date
                                                        });
                                                        toast.success('Task updated successfully');
                                                        setAdminEditingTaskId(null);
                                                        fetchReport();
                                                      } catch (e) {
                                                        toast.error('Failed to update task');
                                                      }
                                                    }}
                                                    className="p-1 px-3 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                                  >
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={() => {
                                                      setAdminEditingTaskId(null);
                                                      fetchReport(); // Revert local changes
                                                    }}
                                                    className="p-1 px-3 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                                                  >
                                                    Cancel
                                                  </button>
                                                </>
                                              ) : (
                                                <>
                                                  {(isAnyAdmin || isApprover || String(record.assigned_user) === String(user?.id)) && (record.status_id < 14 || (Number(record.status_id) === 14 && isInternalTask(record.type || record.task_type_name))) && (
                                                    <button
                                                      onClick={() => {
                                                        // Convert hours to formatted time for editing
                                                        const updatedData = reportData.map(r => {
                                                          if (r.id === record.id) {
                                                            const hoursDecimal = parseFloat(r.hours) || 0;
                                                            return { ...r, hours: decimalToHoursMinutes(hoursDecimal) };
                                                          }
                                                          return r;
                                                        });
                                                        setReportData(updatedData);
                                                        setAdminEditingTaskId(record.id);
                                                        // Auto-expand customer row when editing (for dual-row display)
                                                        if (isAnyAdmin) {
                                                          setExpandedCustomerRows(prev => {
                                                            const newSet = new Set(prev);
                                                            newSet.add(record.id);
                                                            return newSet;
                                                          });
                                                        }
                                                      }}
                                                      className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                                                      title="Edit Original"
                                                    >
                                                      <FiEdit2 className="w-4 h-4" />
                                                    </button>
                                                  )}
                                                  {isAnyAdmin && (
                                                    <button
                                                      onClick={() => handleRevertTask(record.id)}
                                                      className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                                                      title="Revert Status"
                                                    >
                                                      <FiRotateCcw className="w-4 h-4" />
                                                    </button>
                                                  )}
                                                  {isAnyAdmin && (String(record.status_id) === '14' || String(record.status_id) === '15') && (
                                                    <div className="p-1.5 text-green-600" title="Approved">
                                                      <FiCheckCircle className="w-4 h-4" />
                                                    </div>
                                                  )}
                                                  {isAnyAdmin && isWaitingForMe && record.status_id < 14 && (
                                                    <button
                                                      onClick={() => handleApproveClick(record)}
                                                      className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                                      title="Approve"
                                                    >
                                                      <FiCheckCircle className="w-4 h-4" />
                                                    </button>
                                                  )}
                                                  {(isAnyAdmin || roleId === 9 || isApprover || String(record.assigned_user) === String(user?.id)) && (record.status_id < 14 || (Number(record.status_id) === 14 && isInternalTask(record.type || record.task_type_name))) && (
                                                    <button
                                                      onClick={() => { setAdminTaskToDelete(record); setShowAdminDeleteConfirm(true); }}
                                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                                      title="Delete"
                                                    >
                                                      <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </td>
                                        )}
                                      </tr>

                                      {/* Row 2: Customer View (Show for Admin/Approver, Hide for Customer) */}
                                      {canSeeDualRows && expandedCustomerRows.has(record.id) && (
                                        <tr className="bg-[#EBF5FF] hover:bg-blue-100/40 transition-colors border-l-4 border-l-blue-400">
                                          <td className="px-4 py-2 text-center"></td>
                                          <td className="px-2 py-2 text-center"></td>
                                          <td className="px-4 py-2 text-center text-gray-500">
                                            {formatDateDisplay(record.date)}
                                            <div className="text-[10px] text-blue-600 font-semibold mt-0.5">CUSTOMER DATA</div>
                                          </td>
                                          <td className="px-4 py-2"></td>
                                          <td className="px-4 py-2"></td>
                                          <td className="px-4 py-2">
                                            {customerEditingTaskId === record.id ? (
                                              <select
                                                value={cvRowData.task_type || record.cv_task_type || record.type}
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  const internalTypes = ['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'];
                                                  const isInternal = internalTypes.includes(val);

                                                  let newRef = cvRowData.task_ref !== undefined ? cvRowData.task_ref : (record.cv_task_ref || record.reference || '');

                                                  // Clean existing INT prefix
                                                  if (newRef.startsWith('INT: ')) {
                                                    newRef = newRef.substring(5);
                                                  }

                                                  if (isInternal) {
                                                    newRef = 'INT: ' + newRef;
                                                  }

                                                  setCvRowData({
                                                    ...cvRowData,
                                                    task_type: val,
                                                    task_ref: newRef
                                                  });
                                                }}
                                                className="p-1 border rounded text-xs w-full bg-white"
                                              >
                                                {taskTypeOptions.map(opt => (
                                                  <option key={opt.value} value={opt.label || opt.value}>{opt.label}</option>
                                                ))}
                                              </select>
                                            ) : (
                                              <span className="text-xs text-gray-600 font-medium">
                                                {record.cv_task_type || record.type}
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2">
                                            {customerEditingTaskId === record.id ? (
                                              ((cvRowData.task_ref !== undefined && cvRowData.task_ref !== '') || (cvRowData.task_ref === undefined && (record.cv_task_ref || record.reference))) || (['HR', 'Others', 'Admin', 'Training', 'Infrastructure Administration'].includes(cvRowData.task_type || record.cv_task_type || record.type)) ? (
                                                <div className="relative w-full">
                                                  <input
                                                    type="text"
                                                    value={cvRowData.task_ref ?? (record.cv_task_ref || record.reference || '')}
                                                    onChange={e => setCvRowData({ ...cvRowData, task_ref: e.target.value })}
                                                    className="p-1 border rounded text-xs w-full bg-white pr-6"
                                                  />
                                                  <button
                                                    onClick={() => setCvRowData({ ...cvRowData, task_ref: '' })}
                                                    className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 p-0.5 rounded-full hover:bg-red-50"
                                                    title="Clear Reference"
                                                  >
                                                    <FiXCircle className="w-4 h-4" />
                                                  </button>
                                                </div>
                                              ) : (
                                                <Select
                                                  menuPortalTarget={document.body}
                                                  styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                    control: base => ({ ...base, minHeight: '30px', fontSize: '12px', minWidth: '180px' })
                                                  }}
                                                  options={customerOptions}
                                                  value={customerOptions.find(c => String(c.value) === String(cvRowData.customer_id || record.customer_id))}
                                                  onChange={(opt) => {
                                                    if (opt) {
                                                      setCvRowData({
                                                        ...cvRowData,
                                                        customer_id: opt.value,
                                                        task_ref: `${opt.short_code}: `
                                                      });
                                                    }
                                                  }}
                                                  placeholder="Select Customer..."
                                                  className="text-xs min-w-[180px]"
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                              )
                                            ) : (
                                              <span className="text-xs text-gray-600">
                                                {record.cv_task_ref || record.reference || '-'}
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 max-w-[400px]">
                                            {customerEditingTaskId === record.id ? (
                                              <textarea
                                                value={cvRowData.work_done ?? (record.cv_work_done || record.work_done || '')}
                                                onChange={e => setCvRowData({ ...cvRowData, work_done: e.target.value })}
                                                className="w-full p-1 border rounded text-xs bg-white"
                                                rows={2}
                                                placeholder=""
                                              />
                                            ) : (
                                              <div className="text-xs text-gray-700 whitespace-pre-wrap italic">
                                                {record.cv_work_done || record.work_done}
                                              </div>
                                            )}
                                          </td>
                                          <td className="px-4 py-2">
                                            {customerEditingTaskId === record.id ? (
                                              <input
                                                type="number"
                                                value={cvRowData.status_percent ?? record.cv_status_percent ?? record.completion_percentage ?? 0}
                                                onChange={e => setCvRowData({ ...cvRowData, status_percent: e.target.value })}
                                                className="w-16 p-1 border rounded text-center text-xs bg-white"
                                                min="0" max="100"
                                              />
                                            ) : (
                                              <span className="text-xs text-gray-600">{record.cv_status_percent ?? record.completion_percentage ?? 0}%</span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-center">
                                            {customerEditingTaskId === record.id ? (
                                              <DurationPicker
                                                value={cvRowData.hours ?? record.cv_hours ?? record.hours ?? 0}
                                                onChange={(val) => setCvRowData({ ...cvRowData, hours: val })}
                                              />
                                            ) : (
                                              <span className="text-xs text-gray-600 font-bold">{decimalToHoursMinutes(parseFloat(record.cv_hours ?? record.hours ?? 0))}</span>
                                            )}
                                          </td>
                                          {!isCustomer && <td className="px-4 py-2 text-center text-[10px] text-gray-400 font-medium">CV PERSISTENT</td>}
                                          {(!isCustomer && (isAnyAdmin || String(record.assigned_user) === String(user?.id) || isApprover)) && (
                                            <td className="px-4 py-2">
                                              <div className="flex justify-center">
                                                {customerEditingTaskId === record.id ? (
                                                  <div className="flex gap-1">
                                                    <button
                                                      onClick={() => handleUpdateCustomerView(record, cvRowData)}
                                                      className="px-2 py-1 bg-green-600 text-white rounded text-[10px] font-bold"
                                                    >
                                                      SAVE CV
                                                    </button>
                                                    <button
                                                      onClick={() => setCustomerEditingTaskId(null)}
                                                      className="px-2 py-1 bg-gray-400 text-white rounded text-[10px] font-bold"
                                                    >
                                                      BACK
                                                    </button>
                                                  </div>
                                                ) : (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setCustomerEditingTaskId(record.id);
                                                      // Convert decimal hours to formatted time for editing
                                                      const hoursDecimal = parseFloat(record.cv_hours ?? record.hours ?? 0);
                                                      setCvRowData({
                                                        task_type: record.cv_task_type || record.type,
                                                        task_ref: record.cv_task_ref || record.reference,
                                                        work_done: record.cv_work_done || record.work_done,
                                                        status_percent: record.cv_status_percent ?? record.completion_percentage,
                                                        hours: decimalToHoursMinutes(hoursDecimal)
                                                      });
                                                    }}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                    title="Edit Customer View"
                                                  >
                                                    <FiEdit2 className="w-4 h-4" />
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          )}
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })
                            })()}
                          </tbody>
                          {/* Total Hours Footer */}

                        </table>
                      </div>

                      {/* Pagination Controls */}
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          {filteredReportData.length > 0 ? (
                            <>Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredReportData.length)} of {filteredReportData.length} entries</>
                          ) : (
                            <>Showing 0 to 0 of 0 entries</>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 border rounded ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-red-600 text-red-600 hover:bg-red-50'}`}
                          >
                            Previous
                          </button>

                          {(() => {
                            const totalPages = Math.max(1, Math.ceil(filteredReportData.length / itemsPerPage));
                            const maxVisiblePages = 5;
                            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                            if (endPage - startPage < maxVisiblePages - 1) {
                              startPage = Math.max(1, endPage - maxVisiblePages + 1);
                            }
                            const pages = [];
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <button
                                  key={i}
                                  onClick={() => setCurrentPage(i)}
                                  className={`px-3 py-1 border rounded ${i === currentPage ? 'bg-red-600 text-white' : 'border-red-600 text-red-600 hover:bg-red-50'}`}
                                >
                                  {i}
                                </button>
                              );
                            }
                            if (pages.length === 0) {
                              pages.push(
                                <button key={1} className="px-3 py-1 border rounded bg-gray-100 text-gray-600" disabled>
                                  1
                                </button>
                              );
                            }
                            return pages;
                          })()}

                          <button
                            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredReportData.length / itemsPerPage), prev + 1))}
                            disabled={currentPage >= Math.ceil(filteredReportData.length / itemsPerPage)}
                            className={`px-3 py-1 border rounded ${currentPage >= Math.ceil(filteredReportData.length / itemsPerPage) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-red-600 text-red-600 hover:bg-red-50'}`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center p-12 text-gray-500">
                      {isAdmin ? 'No records found for selected filters' : 'No Records Found on this date'}
                    </div>
                  ))
                )
              }
              {/* V4: Assign Task (Manager View) */}
              {
                activeTab === 'assign' && (roleId === 9) && (
                  <div className="animate-in fade-in duration-500">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <FiPlusCircle className="text-red-600" />
                        Task Assignments
                      </h2>
                    </div>

                    {/* Controls & Toolbar */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3">
                        {/* Records per page */}
                        <div className="flex items-center border border-gray-300 rounded-md bg-white overflow-hidden h-[36px]">
                          <select
                            value={assignItemsPerPage}
                            onChange={(e) => {
                              setAssignItemsPerPage(Number(e.target.value));
                              setAssignCurrentPage(1);
                            }}
                            className="text-sm p-1.5 focus:ring-blue-500 focus:border-blue-500 bg-transparent border-none outline-none font-medium text-gray-700 pl-3 pr-6 h-full"
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={filteredAssignments.length || 999999}>All</option>
                          </select>
                          <div className="px-2 text-gray-500 bg-gray-50 border-l border-gray-300 text-xs flex items-center h-full pointer-events-none">
                            Records
                          </div>
                        </div>

                      </div>

                      {/* Search */}
                      <div className="relative w-full sm:w-1/3 md:w-1/4">
                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          placeholder="Search tasks..."
                          className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                          value={assignSearchTerm}
                          onChange={e => setAssignSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                          <thead className="bg-gray-50">
                            <tr>
                              <th onClick={() => toggleAssignSort('created_at')} className="px-4 py-3 text-left font-bold text-gray-700 uppercase tracking-wider text-[11px] cursor-pointer hover:bg-gray-100 transition-colors select-none">
                                <div className="flex items-center gap-1">
                                  Date
                                  {assignSortField === 'created_at' ? (
                                    assignSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAssignSort('assigned_to_name')} className="px-4 py-3 text-left font-bold text-gray-700 uppercase tracking-wider text-[11px] cursor-pointer hover:bg-gray-100 transition-colors select-none">
                                <div className="flex items-center gap-1">
                                  Assigned To
                                  {assignSortField === 'assigned_to_name' ? (
                                    assignSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAssignSort('task_type')} className="px-4 py-3 text-left font-bold text-gray-700 uppercase tracking-wider text-[11px] cursor-pointer hover:bg-gray-100 transition-colors select-none">
                                <div className="flex items-center gap-1">
                                  Type
                                  {assignSortField === 'task_type' ? (
                                    assignSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAssignSort('task_ref')} className="px-4 py-3 text-left font-bold text-gray-700 uppercase tracking-wider text-[11px] cursor-pointer hover:bg-gray-100 transition-colors select-none">
                                <div className="flex items-center gap-1">
                                  Reference
                                  {assignSortField === 'task_ref' ? (
                                    assignSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAssignSort('description')} className="px-4 py-3 text-left font-bold text-gray-700 uppercase tracking-wider text-[11px] cursor-pointer hover:bg-gray-100 min-w-[200px] transition-colors select-none">
                                <div className="flex items-center gap-1">
                                  Instructions
                                  {assignSortField === 'description' ? (
                                    assignSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th onClick={() => toggleAssignSort('status')} className="px-4 py-3 text-left font-bold text-gray-700 uppercase tracking-wider text-[11px] cursor-pointer hover:bg-gray-100 transition-colors select-none">
                                <div className="flex items-center gap-1">
                                  Status
                                  {assignSortField === 'status' ? (
                                    assignSortDir === 'asc' ? <FiArrowUp className="w-3 h-3" /> : <FiArrowDown className="w-3 h-3" />
                                  ) : <FiMinus className="w-3 h-3 opacity-30" />}
                                </div>
                              </th>
                              <th className="px-4 py-3 text-center font-bold text-gray-700 uppercase tracking-wider text-[11px] w-[120px]">
                                <div className="flex items-center justify-center gap-1">
                                  Action
                                  <button
                                    onClick={() => setShowAssignFormRow(true)}
                                    className="p-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                                    title="Add Assignment"
                                  >
                                    <FiPlus className="w-3 h-3" />
                                  </button>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {showAssignFormRow && (
                              <tr className="bg-red-50 animate-in slide-in-from-left duration-200 border-t-2 border-red-200">
                                <td className="px-2 py-4 text-center text-xs text-gray-400 italic">NEW</td>
                                <td className="px-2 py-4">
                                  <Select
                                    options={employeeOptions}
                                    value={assignForm.employee_id}
                                    onChange={opt => setAssignForm({ ...assignForm, employee_id: opt })}
                                    placeholder="Employee..."
                                    className="text-xs min-w-[150px]"
                                    menuPortalTarget={document.body}
                                  />
                                </td>
                                <td className="px-2 py-4">
                                  <select
                                    className="w-full p-2 border border-gray-300 rounded-lg text-xs bg-white h-[38px] focus:ring-2 focus:ring-red-500"
                                    value={assignForm.task_type}
                                    onChange={e => {
                                      const val = e.target.value;
                                      setAssignForm({ ...assignForm, task_type: val, task_ref: (val === 'HR' || val === 'Admin') ? 'INT: ' : '' });
                                    }}
                                  >
                                    <option value="">Type...</option>
                                    {taskTypeOptions.map(opt => (
                                      <option key={opt.value} value={opt.label || opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-2 py-4">
                                  {assignForm.task_type === 'Ticket' ? (
                                    <Select
                                      options={availableTicketOptions.map(t => ({ value: t.id, label: `${t.short_code}:${t.id}`, original: t }))}
                                      onChange={opt => setAssignForm({ ...assignForm, task_ref: opt ? opt.label : '', ticket_id: opt ? opt.value : null })}
                                      placeholder="Ticket..."
                                      className="text-xs min-w-[150px]"
                                      menuPortalTarget={document.body}
                                    />
                                  ) : (
                                    <div className="flex gap-1 items-center">
                                      {!['HR', 'Admin', 'Others'].includes(assignForm.task_type) && (
                                        <div className="w-24">
                                          <Select
                                            options={customerOptions}
                                            onChange={opt => {
                                              if (opt) setAssignForm({ ...assignForm, task_ref: `${opt.short_code}: ` });
                                            }}
                                            placeholder="Proj"
                                            className="text-[10px]"
                                            menuPortalTarget={document.body}
                                          />
                                        </div>
                                      )}
                                      <input
                                        type="text"
                                        value={assignForm.task_ref}
                                        onChange={e => setAssignForm({ ...assignForm, task_ref: e.target.value })}
                                        className="flex-1 p-2 border border-gray-300 rounded-lg text-xs h-[38px]"
                                        placeholder="Ref..."
                                      />
                                    </div>
                                  )}
                                </td>
                                <td className="px-2 py-4">
                                  <input
                                    className="w-full p-2 border border-gray-300 rounded-lg text-xs h-[38px]"
                                    value={assignForm.description}
                                    onChange={e => setAssignForm({ ...assignForm, description: e.target.value })}
                                    placeholder="Work instructions..."
                                  />
                                </td>
                                <td className="px-2 py-4 text-center">
                                  <span className="text-[10px] font-bold text-red-600 bg-red-100 px-2 py-1 rounded">READY</span>
                                </td>
                                <td className="px-2 py-4">
                                  <div className="flex gap-1">
                                    <button
                                      onClick={handleAssignTaskSubmit}
                                      disabled={isAssigning}
                                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                                      title="Assign Now"
                                    >
                                      {isAssigning ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <FiCheckCircle className="w-4 h-4" />}
                                    </button>
                                    <button
                                      onClick={() => setShowAssignFormRow(false)}
                                      className="p-2 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300"
                                      title="Cancel"
                                    >
                                      <FiXCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                            {/* Paginated Data */}
                            {filteredAssignments.length === 0 && !showAssignFormRow ? (
                              <tr>
                                <td colSpan={7} className="px-4 py-8 text-center text-gray-500 italic bg-white">
                                  No assigned tasks found matching your criteria.
                                </td>
                              </tr>
                            ) : (
                              filteredAssignments
                                .slice((assignCurrentPage - 1) * assignItemsPerPage, assignCurrentPage * assignItemsPerPage)
                                .map(task => (
                                  <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-4 text-gray-500">{formatDateDisplay(task.created_at?.split(' ')[0])}</td>

                                    <td className="px-4 py-4 font-semibold text-gray-800">
                                      {editingAssignmentId === task.id ? (
                                        <Select
                                          options={employeeOptions}
                                          value={employeeOptions.find(opt => opt.value === task.assigned_to)}
                                          onChange={opt => {
                                            const updated = assignmentsSent.map(t => t.id === task.id ? { ...t, assigned_to: opt.value } : t);
                                            setAssignmentsSent(updated);
                                          }}
                                          className="text-xs min-w-[150px]"
                                          menuPortalTarget={document.body}
                                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        />
                                      ) : (
                                        task.assigned_to_name
                                      )}
                                    </td>

                                    <td className="px-4 py-4">
                                      {editingAssignmentId === task.id ? (
                                        <select
                                          className="w-full p-2 border border-gray-300 rounded text-xs"
                                          value={task.task_type}
                                          onChange={e => {
                                            const val = e.target.value;
                                            const updated = assignmentsSent.map(t => t.id === task.id ?
                                              { ...t, task_type: val, task_ref: (val === 'HR' || val === 'Admin') ? 'INT: ' : t.task_ref } : t);
                                            setAssignmentsSent(updated);
                                          }}
                                        >
                                          {taskTypeOptions.map(opt => (
                                            <option key={opt.value} value={opt.label || opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                      ) : (
                                        <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-bold border border-purple-100">
                                          {task.task_type}
                                        </span>
                                      )}
                                    </td>

                                    <td className="px-4 py-4 text-gray-700 max-w-[200px] truncate">
                                      {editingAssignmentId === task.id ? (
                                        <input
                                          className="w-full p-2 border border-gray-300 rounded text-xs"
                                          value={task.task_ref}
                                          onChange={e => {
                                            const updated = assignmentsSent.map(t => t.id === task.id ? { ...t, task_ref: e.target.value } : t);
                                            setAssignmentsSent(updated);
                                          }}
                                        />
                                      ) : (
                                        task.task_ref
                                      )}
                                    </td>

                                    <td className="px-4 py-4 text-gray-600 italic max-w-sm truncate">
                                      {editingAssignmentId === task.id ? (
                                        <input
                                          className="w-full p-2 border border-gray-300 rounded text-xs"
                                          value={task.description}
                                          onChange={e => {
                                            const updated = assignmentsSent.map(t => t.id === task.id ? { ...t, description: e.target.value } : t);
                                            setAssignmentsSent(updated);
                                          }}
                                        />
                                      ) : (
                                        task.description
                                      )}
                                    </td>

                                    <td className="px-4 py-4">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                        {task.status}
                                      </span>
                                    </td>

                                    <td className="px-4 py-4 text-center">
                                      {editingAssignmentId === task.id ? (
                                        <div className="flex justify-center gap-2">
                                          <button
                                            onClick={async () => {
                                              try {
                                                await api.post(`/timesheet/update-assignment/${task.id}`, {
                                                  assigned_to: task.assigned_to,
                                                  task_type: task.task_type,
                                                  task_ref: task.task_ref,
                                                  description: task.description
                                                });
                                                toast.success('Assignment updated');
                                                setEditingAssignmentId(null);
                                                loadAssignmentsSent();
                                              } catch (e) {
                                                toast.error('Failed to update assignment');
                                                console.error(e);
                                              }
                                            }}
                                            className="p-1 px-2 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                            title="Save"
                                          >
                                            <FiCheckCircle className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditingAssignmentId(null);
                                              loadAssignmentsSent(); // Revert changes
                                            }}
                                            className="p-1 px-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                            title="Cancel"
                                          >
                                            <FiXCircle className="w-4 h-4" />
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex justify-center gap-2">
                                          <button
                                            onClick={() => setEditingAssignmentId(task.id)}
                                            className="p-1 px-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                            title="Edit"
                                          >
                                            <FiEdit2 className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => {
                                              if (window.confirm('Are you sure you want to delete this assignment?')) {
                                                api.post(`/timesheet/delete-assignment/${task.id}`).then(() => {
                                                  toast.success('Assignment deleted');
                                                  loadAssignmentsSent();
                                                }).catch(e => toast.error('Failed to delete assignment'));
                                              }
                                            }}
                                            className="p-1 px-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
                                            title="Delete"
                                          >
                                            <FiTrash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination Footer */}
                      {filteredAssignments.length > 0 && (
                        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                          <div className="text-sm text-gray-500">
                            Showing <span className="font-medium">{Math.min(filteredAssignments.length, (assignCurrentPage - 1) * assignItemsPerPage + 1)}</span> to <span className="font-medium">{Math.min(filteredAssignments.length, assignCurrentPage * assignItemsPerPage)}</span> of <span className="font-medium">{filteredAssignments.length}</span> entries
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setAssignCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={assignCurrentPage === 1}
                              className="px-3 py-1 border rounded hover:bg-white disabled:opacity-50 disabled:hover:bg-transparent text-sm bg-white text-gray-700"
                            >
                              Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredAssignments.length / assignItemsPerPage) }, (_, i) => i + 1)
                              .filter(p => p === 1 || p === Math.ceil(filteredAssignments.length / assignItemsPerPage) || (p >= assignCurrentPage - 2 && p <= assignCurrentPage + 2))
                              .map((p, i, arr) => (
                                <React.Fragment key={p}>
                                  {i > 0 && arr[i - 1] !== p - 1 && <span className="px-2">...</span>}
                                  <button
                                    onClick={() => setAssignCurrentPage(p)}
                                    className={`px-3 py-1 border rounded text-sm ${assignCurrentPage === p ? 'bg-blue-600 text-white border-blue-600' : 'bg-white hover:bg-gray-50 text-gray-700'
                                      }`}
                                  >
                                    {p}
                                  </button>
                                </React.Fragment>
                              ))}
                            <button
                              onClick={() => setAssignCurrentPage(prev => Math.min(Math.ceil(filteredAssignments.length / assignItemsPerPage), prev + 1))}
                              disabled={assignCurrentPage === Math.ceil(filteredAssignments.length / assignItemsPerPage)}
                              className="px-3 py-1 border rounded hover:bg-white disabled:opacity-50 disabled:hover:bg-transparent text-sm bg-white text-gray-700"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}


                    </div>
                  </div>
                )
              }
            </div >
          </div >
        </div >
      </div >
      {
        openedTicketId && (
          <TicketDetails
            ticketId={openedTicketId}
            onClose={() => setOpenedTicketId(null)}
            onSuccess={() => {
              if (!isAdmin) {
                loadTickets();
              }
            }}
          />
        )
      }
      {
        showEmailModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl p-4 md:p-6 overflow-y-auto max-h-[90vh]">
              <div className="text-xl font-semibold text-gray-800 mb-4">Send Email Report</div>
              <div className="space-y-4">
                {/* To Field - Multi-select with search and custom entry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <CreatableSelect
                    isMulti
                    options={emailSuggestions.map(s => ({ value: s.email, label: s.label }))}
                    value={emailTo}
                    onChange={(selected) => setEmailTo(selected || [])}
                    placeholder="Select or type email addresses..."
                    isClearable
                    isSearchable
                    className="text-sm"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: '#d1d5db',
                        borderRadius: '0.5rem',
                        minHeight: '42px',
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null
                    }}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                </div>

                {/* CC Field - Multi-select with search and custom entry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CC</label>
                  <CreatableSelect
                    isMulti
                    options={emailSuggestions.map(s => ({ value: s.email, label: s.label }))}
                    value={emailCc}
                    onChange={(selected) => setEmailCc(selected || [])}
                    placeholder="Select or type email addresses..."
                    isClearable
                    isSearchable
                    className="text-sm"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: '#d1d5db',
                        borderRadius: '0.5rem',
                        minHeight: '42px',
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null
                    }}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                </div>

                {/* Subject Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    placeholder="Email subject"
                    value={emailSubject}
                    onChange={e => setEmailSubject(e.target.value)}
                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                {/* Message Field with Toolbar and Editable Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message Preview</label>
                  <div
                    className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-sm overflow-auto"
                    style={{ minHeight: '200px', maxHeight: '300px', fontFamily: 'Arial, sans-serif' }}
                    dangerouslySetInnerHTML={{ __html: emailBody }}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setShowEmailModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (isExternal) {
                        toast.error('Restricted Access: Reports can only be sent from the company network.');
                        return;
                      }
                      if (!emailTo || emailTo.length === 0) {
                        toast.error('Please add at least one recipient');
                        return;
                      }
                      try {
                        const toEmails = emailTo.map(e => e.value).join(', ');
                        const ccEmails = emailCc.map(e => e.value).join(', ');

                        const effDate = (activeTab === 'my_tasks') ? formatDate(myTasksDate) : fromDate;

                        const res = await api.post('/timesheet/send-daily-report', {
                          to: toEmails,
                          cc: ccEmails,
                          subject: emailSubject,
                          body: emailBody,
                          date: effDate, // Pass current date for locking
                          customerId: customerId || null // Pass customer context
                        });
                        toast.success(res.data?.message || 'Report sent successfully');
                        setShowEmailModal(false);

                        // Reload rows to update statuses (status 5) which makes the submit button disappear
                        await loadMyRows(effDate);

                        // Set UI flag for ALL visible tasks
                        // This "locks" them via sentTaskIds
                        const visibleIds = tasks.map(t => t.id);
                        setSentTaskIds(prev => {
                          const next = new Set(prev);
                          visibleIds.forEach(id => next.add(id));
                          return next;
                        });
                      } catch (e) {
                        const errorMsg = e.response?.data?.messages?.error || e.response?.data?.message || 'Failed to send report';
                        toast.error(errorMsg);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Task</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this task? This action cannot be undone.</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setTaskToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await api.post('/timesheet/delete-task', { id: taskToDelete.id });
                      toast.success('Task deleted successfully');
                      setShowDeleteConfirm(false);
                      setTaskToDelete(null);
                      loadMyRows(fromDate);
                    } catch (e) {
                      toast.error('Failed to delete task');
                      setShowDeleteConfirm(false);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )
      }



      {/* Admin Delete Confirmation Modal */}
      {
        showAdminDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Task</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this task? This action cannot be undone.</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAdminDeleteConfirm(false);
                    setAdminTaskToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await api.post('/timesheet/delete-task', { id: adminTaskToDelete.id });
                      toast.success('Task deleted successfully');
                      setShowAdminDeleteConfirm(false);
                      setAdminTaskToDelete(null);
                      fetchReport();
                    } catch (e) {
                      toast.error('Failed to delete task');
                      setShowAdminDeleteConfirm(false);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Revert Confirmation Modal */}
      {
        showRevertConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Revert Task</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to revert this task? This will move the task back to the pending list and unlock it for editing.</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRevertConfirm(false);
                    setTaskToRevert(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRevert}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                >
                  Revert
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Revert Confirmation Modal */}
      {
        showBulkRevertConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Bulk Revert Tasks</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to revert {selectedReportTaskIds.size} selected tasks? This will move them back to the pending list.</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowBulkRevertConfirm(false);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkRevert}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                >
                  Revert Selected
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Approve Confirmation Modal */}
      {
        showApproveConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
              <h3 className="text-lg font-bold mb-4">Confirm Approval</h3>
              <p className="mb-4 text-gray-600">Are you sure you want to approve this task?</p>

              {/* Approval Type Dropdown for Admins (Role 11 Only) */}
              {isSuperAdmin && roleId === 11 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Approval Action</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={approvalType}
                    onChange={(e) => setApprovalType(e.target.value)}
                  >
                    <option value="normal">Normal (Follow Workflow)</option>
                    <option value="override">Override (Approve Instantly)</option>
                  </select>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button onClick={cancelApprove} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
                <button onClick={confirmApprove} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-red-700">Approve</button>
              </div>
            </div>
          </div>
        )
      }
      {/* Bulk Approve Confirmation Modal */}
      {
        showBulkApproveConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
              <h3 className="text-lg font-bold mb-4">Confirm Bulk Approval</h3>
              <p className="mb-4 text-gray-600">Are you sure you want to approve {pendingBulkActionSet ? pendingBulkActionSet.size : 0} tasks?</p>

              {/* Approval Type Dropdown for Admins (Role 11 Only) */}
              {isSuperAdmin && roleId === 11 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Approval Action</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={approvalType}
                    onChange={(e) => setApprovalType(e.target.value)}
                  >
                    <option value="normal">Normal (Follow Workflow)</option>
                    <option value="override">Override (Approve Instantly)</option>
                  </select>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button onClick={() => setShowBulkApproveConfirm(false)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
                <button onClick={confirmBulkApprove} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-red-700">Approve</button>
              </div>
            </div>
          </div>
        )
      }
      {/* Reject Confirmation Modal */}
      {
        showRejectConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all scale-100 border border-gray-100">
              <div className="bg-red-50 px-6 py-4 flex items-center gap-3 border-b border-red-100">
                <div className="bg-red-100 p-2 rounded-full">
                  <FiXCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Reject Task</h3>
              </div>

              <div className="p-6">
                <p className="text-gray-600 text-sm mb-6">
                  Are you sure you want to REJECT this task?<br />
                  <span className="font-semibold text-gray-800">{taskToReject?.reference}</span>
                </p>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowRejectConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmReject}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm transition-colors flex items-center gap-2"
                  >
                    <FiXCircle className="w-4 h-4" />
                    Confirm Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </Layout >
  );
}